// ─── PHYSICAL INTEL — standalone full-screen map tab ─────────────────────────
// Self-contained. Fuses live AIS vessels (/api/altdata/tankers) and NASA FIRMS
// thermal anomalies (/api/altdata/thermal) onto one Mapbox dark map, with
// layer toggles, US port markers, and chokepoint/port labels. Purely additive —
// does not touch AltDataTab. Vessel-layer logic mirrors AltDataTab's VesselMap.

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
// ⚠ Source/Layer are new here (flight arcs). Everything else on this map is a
// <Marker> — a DOM node at a coordinate — but 20 great-circle paths as DOM
// would be absurd, and real Mapbox line layers also render BENEATH markers
// automatically, which is exactly the stacking the arcs need.
import Map, { Marker, Popup, Source, Layer } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { C, font, s, backendFetch } from "./core";
import { liveRows } from "./placeholder";

// Vite only inlines VITE_-prefixed env vars — the token MUST be VITE_MAP_TOKEN.
const MAP_TOKEN = import.meta.env.VITE_MAP_TOKEN;
const REFRESH_MS = 5 * 60 * 1000; // 5 minutes
const LAYERS_KEY = "qs_physicalintel_layers";

// ⚠⚠ THE REGION LIST AND ITS COVERAGE STATE NOW COME FROM THE ENDPOINT.
// /api/altdata/tankers returns a `regions` manifest alongside the vessel list:
//   { name, vessel_count, has_coverage, coverage_note, anchor_lat, anchor_lon }
// The hardcoded CHOKEPOINTS anchors and the NO_COVERAGE set that used to live
// here are gone. They were two of three hand-synced copies; the third,
// _AIS_NO_COVERAGE in app/services/altdata.py, is now the single source.
//
// ⚠ has_coverage is a property of the RECEIVER NETWORK, not of today's count. A
// region can report zero vessels and still be covered; Hormuz and Bab el-Mandeb
// report zero because AISStream's volunteer terrestrial network has nobody there.
// Never re-derive the flag from vessel_count — that relabels a quiet strait as a
// dead one. Read has_coverage; it is authoritative.
//
// ⚠ Region labels now appear only AFTER the first fetch resolves, where the
// static list drew them immediately. That is the correct trade: a label rendered
// before the data can contradict it.

// ⚠ SWEPT src/ ON 2026-08-26 — NO OTHER BUILT-IN SHADOWS FOUND. Every .js/.jsx/
// .mjs/.ts/.tsx under src/ (node_modules excluded) was scanned for default, named
// and namespace imports binding any of: Map, Set, Date, Error, Promise, Array,
// Object, String, Number, WeakMap, WeakSet, RegExp, Symbol, Proxy, JSON, Math,
// Intl, Function, Boolean. Exactly one hit — the react-map-gl `Map` default
// import on the line above — and it is already resolved here.
// ⚠⚠ Resolved via `new globalThis.Map()`, NOT an aliased import. Do not go
// looking for an alias that does not exist. The trap is that the shadowed name
// RESOLVES CLEANLY at build time and only fails at runtime, on the one code path
// that constructs it — `new Map()` built the react-map-gl COMPONENT and threw
// "CO is not a constructor" in the minified bundle while vite reported success.
// Re-run the sweep after adding any dependency whose default export is named
// after a built-in.

// TODO: /api/altdata/tankers should return a REGION MANIFEST alongside the
// vessel list. Without one a client cannot distinguish "monitored but empty"
// from "not monitored", which is why NO_COVERAGE is hand-duplicated in three
// places: app/services/altdata.py (_AIS_NO_COVERAGE), src/AltDataTab.jsx
// (AIS_NO_COVERAGE) and here. A manifest deletes two of the three copies.

// ⚠⚠ NOT C.blue. C.blue is "#3b82f6" — a mid-tone Tailwind blue that sits in the
// same hue and luminance neighbourhood as dark-v11's own blue-grey landmass
// shading, so vessel dots read as muddy smudges against it. #00BFFF is
// cyan-shifted and much higher luminance, which separates it from the basemap
// AND from every other accent in C (amber/teal are both #00FF87, purple #9D4EDD,
// orange #f97316). Defined locally on purpose: C.blue is shared platform-wide
// and core.jsx is out of scope for this change.
const VESSEL_BLUE = "#00BFFF";

// ── Zoom-aware marker sizing ──────────────────────────────────────────────────
// ⚠⚠ react-map-gl's <Marker> positions a DOM node at a coordinate; it does NOT
// scale its children. Every marker in this file was a fixed CSS-pixel box, so a
// dot sized for the zoom-2.5 world view is a speck once you zoom to a port.
//
// ⚠ THE SCALE FACTOR IS CLAMPED, NOT THE PIXEL RESULT. Clamping absolute pixels
// to a single range collapses three working size encodings: the aircraft type
// hierarchy (20/24/28 box), the thermal FRP ladder (6/8/12/16) and the vessel
// dark-vs-normal distinction (12 vs 8). Scaling by a clamped FACTOR keeps every
// relative relationship intact at every zoom.
// ⚠ The reference is the map's OWN initial zoom. Referencing 5 — a level this map
// never opens at — would render every marker at 0.5x on load, which is the
// opposite of the intent.
const MAP_INITIAL_ZOOM = 2.5;
const MARKER_SCALE_MIN = 0.6;
const MARKER_SCALE_MAX = 2.4;
// Below this, per-entity text labels are suppressed. At the default world view
// 649 vessel name plates overlap into an unreadable wall; the dots still carry
// position, and the label is only legible once you are zoomed in anyway.
const LABEL_MIN_ZOOM = 4;

function markerScale(zoom) {
  // ⚠ typeof, not Number(): a NaN or undefined zoom must fall back to the
  // reference rather than propagate into every marker's width as NaN, which CSS
  // drops silently and renders as a zero-size element.
  const z = typeof zoom === "number" && Number.isFinite(zoom) ? zoom : MAP_INITIAL_ZOOM;
  return Math.min(MARKER_SCALE_MAX, Math.max(MARKER_SCALE_MIN, z / MAP_INITIAL_ZOOM));
}

// Scaled pixel size with a hard floor — nothing may become invisible.
const scaledPx = (base, scale) => Math.max(4, Math.round(base * scale));

// US port text labels (rendered with the vessel layer, chokepoint styling).
const PORT_LABELS = [
  { name: "Houston, TX",        lat: 29.75, lon: -95.06 },
  { name: "Corpus Christi, TX", lat: 27.80, lon: -97.40 },
  { name: "Port Arthur, TX",    lat: 29.87, lon: -93.93 },
  { name: "New Orleans, LA",    lat: 28.90, lon: -90.00 },
  { name: "Los Angeles, CA",    lat: 33.74, lon: -118.20 },
  { name: "New York/NJ",        lat: 40.66, lon: -74.05 },
  { name: "Savannah, GA",       lat: 32.08, lon: -81.10 },
  { name: "Norfolk, VA",        lat: 36.92, lon: -76.33 },
  { name: "Seattle, WA",        lat: 47.27, lon: -122.41 },
];

// US port diamond markers (the PORTS layer).
const PORTS = [
  { name: "Houston",        lat: 29.75, lon: -95.06 },
  { name: "Corpus Christi", lat: 27.80, lon: -97.40 },
  { name: "Port Arthur",    lat: 29.87, lon: -93.93 },
  { name: "Lake Charles",   lat: 30.23, lon: -93.22 },
  { name: "New Orleans",    lat: 28.90, lon: -90.00 },
  { name: "Los Angeles",    lat: 33.74, lon: -118.20 },
  { name: "New York/NJ",    lat: 40.66, lon: -74.05 },
  { name: "Savannah",       lat: 32.08, lon: -81.10 },
  { name: "Norfolk",        lat: 36.92, lon: -76.33 },
  { name: "Seattle/Tacoma", lat: 47.27, lon: -122.41 },
];

// A record is placeable only with a real, in-bounds coordinate. Only the exact
// (0,0) pair is rejected ("null island"); a lone zero is legitimate.
function _validCoord(v) {
  const lat = v?.lat, lon = v?.lon;
  return (
    typeof lat === "number" && typeof lon === "number" &&
    Number.isFinite(lat) && Number.isFinite(lon) &&
    !(lat === 0 && lon === 0) &&
    lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180
  );
}

// Thermal marker color + size by location_type / FRP.
function thermalStyle(t) {
  const lt = t?.location_type;
  const frp = typeof t?.frp === "number" ? t.frp : 0;
  if (lt === "REFINERY" || lt === "PORT/INDUSTRIAL") {
    const size = frp < 50 ? 8 : frp < 150 ? 12 : 16;
    return { color: "#FF5252", size };
  }
  if (lt === "AGRICULTURAL") return { color: "#FFC107", size: 8 };
  return { color: "rgba(255,255,255,0.25)", size: 6 };
}

// Coerce a coordinate to a number (the aircraft feed can carry string lat/lon);
// null / non-numeric values pass through unchanged so _validCoord still rejects them.
function _num(x) {
  if (typeof x === "number") return x;
  if (typeof x === "string" && x.trim() !== "") {
    const n = Number(x);
    if (Number.isFinite(n)) return n;
  }
  return x;
}

// Aircraft marker style by classification: accent color, icon fontSize, boxed
// container size, and translucent fill.
function aircraftStyle(type) {
  if (type === "military") return { color: "#FF5252", icon: 20, box: 28, bg: "rgba(255,82,82,0.85)" };
  if (type === "cargo") return { color: "#FFC107", icon: 18, box: 24, bg: "rgba(255,193,7,0.85)" };
  if (type === "bizjet") return { color: "#9D4EDD", icon: 16, box: 20, bg: "rgba(157,78,221,0.85)" };
  return { color: C.textDim, icon: 16, box: 20, bg: "rgba(255,255,255,0.15)" };
}

// US ICAO 24-bit addresses are the A-block (0xA00000–0xAFFFFF), so the hex starts
// with 'a'. Used to keep US-registered bizjets that lack an FAA owner match.
const _isUsHex = (h) => typeof h === "string" && h.trim().toLowerCase().startsWith("a");

// Aircraft list-panel styling (spec: bizjet amber, cargo teal, military red).
const AC_LIST_COLOR = { bizjet: C.amber, cargo: C.teal, military: C.red };
const AC_LIST_BADGE = { bizjet: "BIZJET", cargo: "CARGO", military: "MIL" };
const AC_LIST_ORDER = { bizjet: 0, cargo: 1, military: 2 };

// ── Flight log (LOG MODE) ──────────────────────────────────────────────
const FLIGHT_EVENTS_PATH = "/api/altdata/aircraft/events";

// ── SWIM flight paths ─────────────────────────────────────────────────────────
const SWIM_FLIGHTS_PATH = "/api/altdata/swim/flights";
// ⚠ Its OWN cadence, not REFRESH_MS. The rest of this panel polls every 5
// minutes; SWIM flights move, so both the fetch and the position tick run at
// 60s. They are separate intervals — a position update must never re-fetch.
const SWIM_REFRESH_MS = 60 * 1000;
// ⚠ Bounded for mobile. The arcs are ONE GeoJSON source (3 features each, so
// 150 features at 50 flights — trivial), but each aircraft is a real DOM marker
// re-positioned every tick, and that is the cost that scales. Desktop takes 50;
// narrow viewports keep the old 20.
const MAX_FLIGHTS_DESKTOP = 50;
const MAX_FLIGHTS_MOBILE = 20;
const MOBILE_MAX_WIDTH = 768;
function maxFlights() {
  // ⚠ Read at build time, not stored: a tablet rotating from 1024 to 768 should
  // get the mobile cap on the next fetch without a remount.
  return (typeof window !== "undefined" && window.innerWidth < MOBILE_MAX_WIDTH)
    ? MAX_FLIGHTS_MOBILE : MAX_FLIGHTS_DESKTOP;
}
const ARC_STEPS = 20;   // 20 segments → 21 points along the geodesic

// ⚠⚠ THE GOLD ARC IS THE SIGNAL. A normal leg keeps the takeoff/cruise/landing
// gradient; a flight to a non-commercial field is drawn in one colour so the
// destination reads at a glance without opening anything.
const UNUSUAL_ARC_COLOR = "#FFD700";   // TODO: add to core.jsx palette

// Airframe tier badges in the unified flight list.
// ⚠⚠ T1 IS DELIBERATELY NOT #FFD700. That gold is UNUSUAL_ARC_COLOR, and the
// unusual marker is the signal this list exists to surface — a T1 badge in the
// identical gold would compete with it on every premium row, which is most of
// them. Dark goldenrod still reads as "top tier" without winning the row.
const TIER1_GOLD = "#B8860B";
const TIER2_SILVER = "#C0C0C0";   // TODO: add to core.jsx palette alongside DEST_GOLD/VESSEL_BLUE
const TIER_BADGE_COLOR = { 1: TIER1_GOLD, 2: TIER2_SILVER, 3: C.textDim };

// ⚠ Display limits, not pagination. These are live flights; a page-2 control
// on a list that reorders itself every 60s would move rows out from under the
// reader between clicks.
const SWIM_LIST_CAP = 50;
const ADSB_LIST_CAP = 10;

// ⚠ The reason the BACKEND gave, not one re-derived here. It classified against
// OurAirports type + scheduled_service with the 12 monitored hubs excluded; a
// second opinion computed in the browser would drift from the stored row.
const UNUSUAL_LABEL = {
  small_airport: "SMALL AIRPORT",
  private_strip: "PRIVATE STRIP",
  no_scheduled_service: "NO SCHEDULED SERVICE",
  international: "INTERNATIONAL",
};

/** Badge colour for a 0-10 destination signal score.
 *
 *  ⚠⚠ C.orange FOR THE MIDDLE BAND, NOT C.amber. In this palette `amber` is
 *  #00FF87 — Quantum Emerald, the primary accent — and it is BRIGHTER than
 *  C.green (#22c55e). Using it for 2-4 would paint a score-3 row a more
 *  attention-grabbing green than a score-6 row, inverting the hierarchy the
 *  badge exists to express. #f97316 is the only actual amber in the palette.
 *  ⚠ Measured against live data: nothing has ever scored above 7, score 1 has
 *  never occurred, and 84% of scored rows are 0. Green is deliberately rare.
 */
function scoreColor(n) {
  if (n >= 5) return C.green;
  if (n >= 2) return C.orange;
  return C.textDim;
}

function destAgo(iso) {
  const ms = Date.parse(iso || "");
  if (!Number.isFinite(ms)) return "";
  const secs = Math.max(0, (Date.now() - ms) / 1000);
  if (secs < 3600) return `${Math.max(1, Math.round(secs / 60))}m ago`;
  if (secs < 86400) return `${Math.round(secs / 3600)}h ago`;
  return `${Math.round(secs / 86400)}d ago`;
}

function destWhere(d) {
  const city = d.arrival_municipality || "";
  const reg = d.arrival_region || "";
  if (city && reg) return `${city}, ${reg}`;
  return city || reg || d.arrival_icao || "";
}


// ⚠ The backend classifies authoritatively (OurAirports type +
// scheduled_service, monitored hubs excluded) and that classification is NOT on
// the /swim/flights payload. This is a deliberately WEAKER local proxy for arc
// colour only: an arrival the frontend cannot place at all is somewhere off the
// beaten path. It never contradicts the backend — it just knows less.
// ⚠ A monitored hub is never unusual, matching the backend rule exactly.
function looksUnusual(f) {
  const arr = String(f?.arrival_icao || "").toUpperCase();
  if (!arr || AIRPORT_COORDS[arr]) return false;
  return !AIRPORT_DB.get(arr);
}

// ⚠⚠ HARDCODED, and it has to be: _GEO_AIRPORTS lives in the backend
// (app/services/altdata.py) and no endpoint exposes it, so there is nothing to
// import. If a 13th airport is added to the geo-fence it must be added here too
// or its flights silently lose their arcs.
// ⚠ KADS is 32.9686 — the backend value. A 33.0564 was specified and is ~10km
// north of the actual Addison field, which would have put every ADS arc endpoint
// in the wrong suburb.
// ⚠⚠ GENERATED FROM ourairports.com/data/airports.csv (86,002 rows, which
// redirects to the davidmegginson.github.io mirror). US 4-letter K-prefixed
// large/medium/SMALL airports, minus the 12 above, which keep their own
// hand-checked coordinates. 3,595 entries.
//
// ⚠⚠ SMALL AIRPORTS ARE THE ENTIRE POINT. Measured on 100 live SWIM flights:
// 75 DISTINCT unmonitored destinations, the most frequent appearing 4 times —
// Aspen, Martha's Vineyard, Nantucket, Bozeman, Lebanon NH. A "top 500 by
// traffic" list covers 64% of that tail; including small fields reaches 88%.
// Bizjets fly to the places airlines do not.
//
// ⚠ Pipe-delimited rather than a JSON object literal: same data, ~20% fewer
// bytes, and one line per airport keeps it greppable and diffable. Parsed once
// at module load into a Map, never per render.
// ⚠ The remaining ~12% are international (CYUL, MBPV, TXKF, SKCL…). Those stay
// DOTS. Drawing a line toward a guessed bearing would assert a direction the
// data does not carry — the same reasoning as the original one-endpoint note.
const _AIRPORT_TABLE = `
K00C|37.2032|-107.869|Durango|CO
K00F|45.4705|-105.4571|Broadus|MT
K00M|31.9537|-89.2353|Bay Springs|MS
K00R|30.6859|-95.0179|Livingston|TX
K00V|38.9458|-104.57|Colorado Springs|CO
K01G|42.7415|-78.0526|Perry|NY
K01M|34.4909|-88.201|Belmont|MS
K01T|30.3726|-100.1902|Sonora|TX
K02A|32.8505|-86.6114|Clanton|AL
K02C|43.0902|-88.1782|Brookfield|WI
K02G|40.6733|-80.6414|East Liverpool|OH
K03B|37.1268|-92.6216|Mansfield|MO
K03D|40.4473|-92.227|Memphis|MO
K04A|31.7363|-86.2625|Luverne|AL
K04G|41.1305|-80.6196|Youngstown|OH
K04M|33.93|-89.3433|Pittsboro|MS
K04Y|46.8838|-96.3503|Hawley|MN
K05C|41.5198|-87.3995|Griffith|IN
K05D|47.967|-102.478|New Town|ND
K05U|39.6042|-116.005|Eureka|NV
K06A|32.4605|-85.68|Tuskegee|AL
K06C|41.9893|-88.1012|Chicago/Schaumburg|IL
K06D|48.8843|-99.6209|Rolla|ND
K06M|33.5346|-89.313|Eupora|MS
K06U|41.976|-114.658|Jackpot|NV
K07A|32.1668|-85.8097|Union Springs|AL
K07F|32.5288|-94.9717|Gladewater|TX
K07R|27.6103|-97.7526|Bishop|TX
K07V|37.5238|-105.009|La Veta|CO
K08A|32.5294|-86.3282|Elmore|AL
K08C|42.9359|-85.805|Georgetown Township|MI
K08D|48.3008|-102.406|Stanley|ND
K08K|40.6514|-98.0798|Harvard|NE
K08M|32.7612|-89.5301|Carthage|MS
K09A|32.1193|-88.1275|Butler|AL
K09J|31.0745|-81.4278|Jekyll Island|GA
K09K|41.6349|-99.3422|Sargent|NE
K09M|33.9915|-90.0786|Charleston|MS
K09R|30.7748|-94.4588|Woodville|TX
K0A3|35.9853|-85.8093|Smithville|TN
K0A4|36.3637|-82.309|Johnson City|TN
K0A7|35.3083|-82.4329|Hendersonville|NC
K0A8|32.9369|-87.0905|Centreville|AL
K0A9|36.3712|-82.1733|Elizabethton|TN
K0B1|44.4252|-70.8099|Bethel|ME
K0B4|42.6036|-97.2526|Hartington|NE
K0B5|42.5914|-72.5228|Montague|MA
K0B8|41.2521|-72.0322|Fishers Island|NY
K0C0|42.4025|-88.6324|Harvard|IL
K0C4|42.1139|-96.7281|Pender|NE
K0D8|44.9867|-99.9528|Gettysburg|SD
K0E0|34.9751|-106.0028|Moriarty|NM
K0E8|35.7177|-108.202|Crownpoint|NM
K0F2|33.6017|-97.7756|Bowie|TX
K0F4|41.2864|-98.9897|Loup City|NE
K0F7|35.3887|-95.6002|Checotah|OK
K0F9|34.1985|-96.6745|Tishomingo|OK
K0G3|40.4008|-96.1704|Tecumseh|NE
K0G6|41.4674|-84.5066|Bryan|OH
K0G7|42.8836|-76.7822|Seneca Falls|NY
K0H1|39.0045|-99.8929|WaKeeney|KS
K0I8|38.3662|-84.2833|Cynthiana|KY
K0J4|31.0425|-86.3116|Florala|AL
K0J6|31.3643|-85.3118|Headland|AL
K0J9|40.8678|-97.354|Utica|NE
K0K7|42.7348|-94.2453|Humboldt|IA
K0L7|35.7683|-115.33|Jean|NV
K0L9|36.3111|-114.464|Overton|NV
K0M0|33.8848|-91.5345|Dumas|AR
K0M2|36.4753|-89.3462|Tiptonville|TN
K0M3|35.546|-87.5973|Hohenwald|TN
K0M4|36.0111|-88.1233|Camden|TN
K0M5|36.1166|-87.7382|Waverly|TN
K0M8|32.8259|-91.1877|Lake Providence|LA
K0M9|32.4107|-91.4987|Delhi|LA
K0O2|35.2871|-116.0817|Baker|CA
K0Q5|40.0282|-124.0743|Shelter Cove|CA
K0R0|31.297|-89.8128|Columbia|MS
K0R1|31.0162|-87.4468|Atmore|AL
K0R3|29.9758|-92.0842|Abbeville|LA
K0R4|31.562|-91.5065|Vidalia|LA
K0R5|31.9637|-92.6603|Winnfield|LA
K0R6|33.5226|-92.4603|Hampton|AR
K0S0|46.9768|-118.587|Lind|WA
K0S7|48.959|-119.412|Oroville|WA
K0S9|48.0538|-122.811|Port Townsend|WA
K0V3|40.5155|-98.9462|Minden|NE
K0V4|37.1417|-79.0164|Gladys|VA
K0V7|36.7164|-110.2284|Kayenta|AZ
K0VG|36.6541|-83.2178|Jonesville|VA
K0W3|39.5668|-76.2024|Churchville|MD
K10C|42.4027|-88.3769|Wonder Lake|IL
K10G|40.5372|-81.9544|Millersburg|OH
K10U|41.9536|-116.1822|Owyhee|NV
K11A|31.8833|-85.4849|Clayton|AL
K11R|30.219|-96.3743|Brenham|TX
K11V|40.3288|-104.6146|Greeley|CO
K12D|47.8182|-92.2861|Tower|MN
K12G|40.8729|-82.6974|Shelby|OH
K12J|31.0506|-87.0656|Brewton|AL
K12K|40.0464|-98.0601|Superior|NE
K12N|41.0086|-74.738|Andover|NJ
K12V|38.4409|-82.2008|Milton|WV
K12Y|44.4411|-93.9163|Le Sueur|MN
K13C|43.4521|-85.2648|Lakeview|MI
K13K|37.8516|-96.2917|Eureka|KS
K14A|35.613|-80.8994|Mooresville|NC
K14F|32.8497|-100.1366|Hamlin|TX
K14G|41.3331|-83.1612|Fremont|OH
K14J|31.41|-86.0903|Elba|AL
K14M|33.1826|-90.8307|Hollandale|MS
K14Y|45.8986|-94.8739|Long Prairie|MN
K15F|33.1915|-99.7179|Haskell|TX
K15J|31.1378|-83.4533|Adel|GA
K15M|34.7723|-88.1659|Iuka|MS
K16D|46.6108|-95.6059|Perham|MN
K16G|41.0941|-83.2125|Tiffin|OH
K16J|31.7433|-84.4193|Dawson|GA
K17G|40.7818|-82.9744|Bucyrus|OH
K17J|31.0069|-84.8776|Donalsonville|GA
K17K|36.7743|-102.51|Boise City|OK
K17M|31.8627|-89.8006|Magee|MS
K17N|39.7055|-75.033|Cross Keys|NJ
K17V|40.6275|-102.6009|Haxtun|CO
K18A|34.3401|-83.1335|Canon|GA
K18I|36.6955|-84.3913|Pine Knot|KY
K18V|40.1|-104.701|Hudson|CO
K19A|34.174|-83.5607|Jefferson|GA
K19M|33.1255|-90.0256|Lexington|MS
K19N|39.7784|-74.9478|Berlin|NJ
K19S|37.497|-100.8343|Sublette|KS
K1A3|35.0162|-84.3463|Copperhill|TN
K1A4|31.1038|-86.0625|Samson|AL
K1A5|35.2226|-83.419|Franklin|NC
K1A6|36.6103|-83.7374|Middlesboro|KY
K1A7|36.3959|-85.6413|Gainesboro|TN
K1A9|32.4387|-86.5127|Prattville|AL
K1B0|45.0041|-69.237|Dexter|ME
K1B1|42.2913|-73.7103|Hudson|NY
K1B2|41.3583|-70.5244|Edgartown|MA
K1B6|42.1065|-71.5101|Hopedale|MA
K1B9|42.0001|-71.1968|Mansfield|MA
K1BT|37.9191|-90.5752|Bonne Terre|MO
K1C1|40.449|-88.1277|Paxton|IL
K1C5|41.696|-88.1292|Bolingbrook|IL
K1D1|45.2305|-96.566|Milbank|SD
K1D3|43.4047|-98.8305|Platte|SD
K1D7|45.2926|-97.5114|Webster|SD
K1D8|44.8562|-98.5313|Redfield|SD
K1F0|34.147|-97.1227|Ardmore|OK
K1F4|34.1404|-96.812|Madill|OK
K1F5|39.3672|-100.4413|Hoxie|KS
K1G0|41.391|-83.6301|Bowling Green|OH
K1G1|41.3315|-82.1003|Elyria|OH
K1G3|41.1514|-81.4151|Kent|OH
K1G4|35.9858|-113.8171|Peach Springs|AZ
K1G5|41.1319|-81.7674|Medina|OH
K1H0|38.7268|-90.5083|St Louis|MO
K1H2|39.0704|-88.5335|Effingham|IL
K1H3|38.4716|-91.8173|Linn|MO
K1H5|36.9896|-91.9541|Willow Springs|MO
K1H8|39.3025|-88.0041|Casey|IL
K1I5|42.3643|-74.066|Freehold|NY
K1J0|30.8439|-85.6018|Bonifay|FL
K1K2|34.8493|-97.5853|Lindsay|OK
K1K4|35.1551|-97.4704|Goldsby|OK
K1K7|37.5805|-95.8372|Fredonia|KS
K1K9|37.4567|-100.9847|Satanta|KS
K1L0|30.087|-90.5826|Reserve|LA
K1L1|37.7871|-114.42|Panaca|NV
K1L3|35.4444|-114.909|Searchlight|NV
K1L7|37.7453|-111.57|Escalante|UT
K1L8|37.1389|-113.306|Hurricane|UT
K1L9|37.8597|-112.816|Parowan|UT
K1M2|33.1452|-90.5153|Belzoni|MS
K1M4|34.2803|-87.6004|Haleyville|AL
K1M5|36.5927|-86.4769|Portland|TN
K1M9|36.8178|-87.9075|Cadiz|KY
K1MO|37.1207|-92.3112|Mountain Grove|MO
K1N1|35.0945|-106.167|Edgewood|NM
K1N7|40.9711|-74.9975|Blairstown|NJ
K1O1|34.2376|-98.742|Grandfield|OK
K1O2|38.9906|-122.901|Lakeport|CA
K1O3|38.2024|-121.268|Lodi|CA
K1O4|35.7335|-98.7306|Thomas|OK
K1O5|41.7304|-122.546|Montague|CA
K1O8|34.4586|-99.1713|Tipton|OK
K1Q2|40.6502|-120.7685|Susanville|CA
K1Q4|37.6791|-121.3|Tracy|CA
K1R1|31.6669|-92.1577|Jena|LA
K1R4|31.1264|-92.5014|Woodworth|LA
K1R7|31.6058|-90.4093|Brookhaven|MS
K1R8|30.8703|-87.8193|Bay Minette|AL
K1S3|46.2711|-106.624|Forsyth|MT
K1S5|46.3271|-119.97|Sunnyside|WA
K1S9|48.3141|-117.743|Chewelah|WA
K1T7|29.8115|-98.4258|Spring Branch|TX
K1U2|43.851|-112.5014|Mud Lake|ID
K1U7|42.2472|-111.338|Paris|ID
K1V0|36.8082|-107.6531|Navajo Dam|NM
K1V2|42.01|-101.7701|Hyannis|NE
K1V6|38.4297|-105.1086|Penrose|CO
K1V8|37.7854|-106.0476|Center|CO
K1X1|36.1059|-100.0257|Higgins|TX
K20A|33.9712|-86.3805|Oneonta|AL
K20M|33.1335|-88.5356|Macon|MS
K20N|41.9853|-73.9641|Kingston|NY
K20R|28.6978|-99.8178|Crystal City|TX
K20U|46.9252|-103.982|Beach|ND
K20V|40.0534|-106.3691|Kremmling|CO
K21D|44.9975|-92.8557|St Paul|MN
K21F|33.2287|-98.1467|Jacksboro|TX
K22F|33.2293|-100.569|Jayton|TX
K22I|39.3281|-82.4418|Mc Arthur|OH
K22M|34.2759|-89.0384|Pontotoc|MS
K22N|40.8095|-75.7615|Lehighton|PA
K22S|42.7179|-120.563|Paisley|OR
K23M|32.0849|-88.7389|Quitman|MS
K23R|29.1384|-98.9419|Devine|TX
K24A|35.3174|-83.2099|Sylva|NC
K24F|32.746|-94.3044|Jefferson|TX
K24J|30.3001|-83.0247|Live Oak|FL
K24N|36.8285|-106.884|Dulce|NM
K24R|28.6855|-99.1892|Dilley|TX
K25D|45.2477|-92.9944|Forest Lake|MN
K25J|31.7017|-84.8271|Cuthbert|GA
K25M|34.7223|-89.0151|Ripley|MS
K26A|33.2842|-85.8086|Ashland|AL
K26R|29.001|-96.582|Edna|TX
K26U|42.0095|-117.7273|McDermitt|OR
K27A|34.0954|-82.8176|Elberton|GA
K27K|38.2344|-84.4347|Georgetown|KY
K27R|30.8622|-100.611|Eldorado|TX
K28J|29.6586|-81.6889|Palatka|FL
K29D|41.146|-80.1677|Grove City|PA
K29M|42.8907|-83.9322|Durand|MI
K29S|45.0499|-110.747|Gardiner|MT
K2A0|35.4862|-84.9311|Dayton|TN
K2A1|36.3497|-84.9467|Jamestown|TN
K2A2|35.6507|-92.4038|Clinton|AR
K2A5|35.9118|-79.6176|Liberty|NC
K2A6|34.5824|-91.1652|Holly Grove|AR
K2B3|43.3881|-72.1893|Newport|NH
K2B7|44.7685|-69.3744|Pittsfield|ME
K2C8|48.7838|-97.6319|Cavalier|ND
K2CB|29.9525|-81.9797|Camp Blanding Mil Res(Starke)|FL
K2D5|46.1738|-98.0795|Oakes|ND
K2E3|36.1772|-101.694|Gruver|TX
K2E5|31.9476|-105.192|Dell City|TX
K2E7|35.2462|-100.5469|McLean|TX
K2F0|30.9057|-101.892|Iraan|TX
K2F1|35.2339|-100.1854|Shamrock|TX
K2F4|33.177|-101.82|Tahoka|TX
K2F5|32.7563|-101.92|Lamesa|TX
K2F7|33.2929|-95.8964|Commerce|TX
K2G1|41.667|-81.1972|Painesville|OH
K2G2|40.3594|-80.7001|Steubenville|OH
K2G4|39.5808|-79.3357|Accident|MD
K2G9|40.0391|-79.0146|Somerset|PA
K2GC|34.8937|-78.8435|Fayetteville|NC
K2H0|39.4104|-88.8454|Shelbyville|IL
K2H2|36.9623|-93.6953|Aurora|MO
K2H9|48.6653|-99.8532|Rolette|ND
K2I0|37.3559|-87.3982|Madisonville|KY
K2I3|37.61|-86.5073|Falls of Rough|KY
K2IS|26.7352|-81.0511|Clewiston|FL
K2J2|31.7846|-81.6412|Hinesville|GA
K2J3|32.9865|-82.3857|Louisville|GA
K2J5|32.8936|-81.9654|Millen|GA
K2K3|37.583|-101.733|Johnson City|KS
K2K4|34.8926|-99.5282|Mangum|OK
K2K7|37.4354|-95.6461|Neodesha|KS
K2K9|35.8329|-95.6674|Haskell|OK
K2L0|31.3419|-92.4436|Pineville|LA
K2M0|37.1151|-87.8571|Princeton|KY
K2M2|35.2343|-87.2579|Lawrenceburg|TN
K2M4|32.3549|-89.4882|Forest|MS
K2M8|35.279|-89.9315|Millington|TN
K2MO|37.0684|-93.8849|Mount Vernon|MO
K2N4|42.7365|-76.4861|Moravia|NY
K2O1|39.9439|-120.945|Quincy|CA
K2O3|38.5785|-122.435|Angwin|CA
K2O6|37.1124|-120.247|Chowchilla|CA
K2O7|36.8154|-118.2045|Independence|CA
K2O8|35.5059|-98.3424|Hinton|OK
K2R0|31.646|-88.6348|Waynesboro|MS
K2R1|29.8991|-91.666|Jeanerette|LA
K2R2|39.7481|-86.4738|Indianapolis|IN
K2R4|30.6376|-86.9937|Milton|FL
K2R5|30.5019|-88.2751|St Elmo|AL
K2R6|30.9567|-92.2341|Bunkie|LA
K2R7|30.8194|-90.1125|Franklinton|LA
K2R9|28.825|-97.8656|Kenedy|TX
K2RR|27.7822|-81.2053|River Ranch|FL
K2S4|46.9654|-119.066|Warden|WA
K2S7|42.5794|-121.8791|Chiloquin|OR
K2S8|47.7542|-118.743|Wilbur|WA
K2S9|46.6976|-123.823|South Bend/Raymond/|WA
K2T1|34.1851|-102.641|Muleshoe|TX
K2V5|40.1003|-102.241|Wray|CO
K2V6|40.1042|-102.713|Yuma|CO
K2W5|38.5966|-77.0726|Indian Head|MD
K2W6|38.3154|-76.5501|California|MD
K2Y4|42.3876|-94.6184|Rockwell City|IA
K30K|37.907|-100.531|Ingalls|KS
K31E|39.6654|-74.3079|West Creek|NJ
K32A|35.087|-93.4275|Danville|AR
K32S|46.5251|-114.053|Stevensville|MT
K33J|31.0526|-85.8687|Geneva|AL
K33K|37.909|-99.4031|Kinsley|KS
K33M|34.1668|-89.6862|Water Valley|MS
K33N|39.2187|-75.6004|Dover/Cheswold|DE
K33R|31.0849|-95.1641|Groveton|TX
K33S|47.1232|-118.39|Ritzville|WA
K33U|40.9177|-109.391|Dutch John|UT
K33V|40.7504|-106.271|Walden|CO
K34M|36.4859|-90.0158|Campbell|MO
K34R|29.39|-96.9561|Hallettsville|TX
K35A|34.687|-81.6411|Union|SC
K35D|42.531|-85.8251|Allegan|MI
K35S|45.5894|-120.674|Wasco|OR
K36K|37.9695|-101.255|Lakin|KS
K36S|41.7907|-123.389|Happy Camp|CA
K37F|33.468|-99.5861|Munday|TX
K37K|33.926|-97.2963|Burneyville|OK
K37T|36.1645|-92.1445|Calico Rock|AR
K37V|40.3416|-99.9078|Arapahoe|NE
K38D|40.9481|-80.8621|Salem|OH
K38J|33.7286|-79.516|Hemingway|SC
K38S|46.3882|-112.766|Deer Lodge|MT
K38U|38.3625|-111.596|Loa|UT
K39N|40.3992|-74.6589|Princeton/Rocky Hill|NJ
K3A2|36.4089|-83.5571|Tazewell|TN
K3A4|35.942|-79.6855|Climax|NC
K3A7|32.8203|-87.8651|Eutaw|AL
K3AK|59.1644|-138.4894|Yakutat|AK
K3AU|37.6716|-97.0779|Augusta|KS
K3B0|42.1009|-72.0384|Southbridge|MA
K3B1|45.463|-69.5516|Greenville|ME
K3B4|43.1432|-70.7736|Eliot|ME
K3B5|44.1889|-70.2329|Turner|ME
K3BS|43.6631|-84.2586|Midland|MI
K3C8|40.9151|-72.7919|Calverton|NY
K3CK|42.2068|-88.323|Chicago/Lake in the Hills|IL
K3CM|43.5469|-83.8955|Bay City|MI
K3CU|46.1942|-91.2464|Cable|WI
K3DW|37.2227|-93.2483|Springfield|MO
K3E0|35.7139|-100.603|Miami|TX
K3F2|32.415|-98.9969|Cisco|TX
K3F3|32.0735|-93.7655|Mansfield|LA
K3F4|32.8613|-94.0102|Vivian|LA
K3F6|34.0276|-100.282|Paducah|TX
K3F7|35.8069|-96.4219|Bristow|OK
K3F9|32.6767|-95.5108|Mineola|TX
K3FU|45.0303|-99.1126|Faulkton|SD
K3G1|42.0442|-79.8539|Wattsburg|PA
K3G2|48.2934|-95.6261|Grygla|MN
K3G3|41.0031|-81.7565|Wadsworth|OH
K3G4|40.903|-82.2556|Ashland|OH
K3G5|39.4437|-79.6752|Camp Dawson (Kingwood)|WV
K3G6|40.906|-81.0|Sebring|OH
K3GM|43.034|-86.1982|Grand Haven|MI
K3GV|39.0151|-94.2153|Grain Valley|MO
K3H4|47.3594|-97.0604|Hillsboro|ND
K3HO|41.5542|-87.2625|Hobart|IN
K3I2|38.9146|-82.0986|Point Pleasant|WV
K3I3|39.5472|-87.3786|Terre Haute|IN
K3I4|38.2584|-80.6508|Richwood|WV
K3I7|39.9133|-84.4004|Phillipsburg|OH
K3J0|32.8665|-81.081|Hampton|SC
K3J1|32.4927|-80.9923|Ridgeland|SC
K3J7|33.598|-83.1382|Greensboro|GA
K3JC|39.0445|-96.8446|Junction City|KS
K3K3|37.9971|-101.7482|Syracuse|KS
K3K4|37.3076|-100.4763|Meade|KS
K3K6|38.7329|-89.8066|St Jacob|IL
K3K7|38.457|-101.353|Leoti|KS
K3K8|37.2281|-99.3309|Coldwater|KS
K3L2|35.7953|-115.627|Sandy Valley|NV
K3LF|39.1625|-89.6746|Litchfield|IL
K3M7|36.5184|-86.0583|Lafayette|TN
K3M8|33.3868|-88.0066|Reform|AL
K3M9|33.5604|-92.0854|Warren|AR
K3MY|40.7953|-89.6134|Peoria|IL
K3N6|40.3299|-74.3468|Old Bridge|NJ
K3O1|37.2627|-120.963|Gustine|CA
K3O3|34.9845|-97.3828|Purcell|OK
K3O4|35.1676|-99.6579|Sayre|OK
K3O9|36.5776|-94.8619|Afton|OK
K3P3|46.3597|-102.323|Mott|ND
K3R2|30.1616|-92.4817|Crowley|LA
K3R4|31.5449|-93.4865|Many|LA
K3R7|30.2427|-92.6734|Jennings|LA
K3R9|30.3575|-97.9945|Austin|TX
K3RC|44.475|-84.5667|Roscommon|MI
K3S4|42.1037|-123.682|Cave Junction|OR
K3S8|42.5101|-123.388|Grants Pass|OR
K3S9|45.2466|-120.166|Condon|OR
K3T3|45.044|-92.0204|Boyceville|WI
K3T5|29.908|-96.95|La Grange|TX
K3TR|41.8359|-86.2252|Niles|MI
K3U3|46.1531|-112.868|Anaconda|MT
K3U4|45.6066|-106.2745|Ashland|MT
K3U7|47.481|-112.8709|Augusta|MT
K3U8|48.1632|-110.1157|Big Sandy|MT
K3V0|43.725|-103.35|Crazy Horse|SD
K3W7|47.922|-119.083|Electric City|WA
K3W8|45.8005|-99.644|Eureka|SD
K3Y2|42.9851|-91.7906|West Union|IA
K3Y3|41.3628|-94.0211|Winterset|IA
K40G|35.6506|-112.148|Grand Canyon|AZ
K40J|30.0693|-83.5806|Perry|FL
K40U|40.9861|-109.678|Manila|UT
K41F|34.0023|-101.33|Floydada|TX
K41N|41.212|-80.9702|Newton Falls|OH
K41U|39.3291|-111.615|Manti|UT
K42A|36.071|-91.8301|Melbourne|AR
K42I|40.007|-82.0124|Zanesville|OH
K42J|29.8447|-82.0475|Keystone Heights|FL
K42M|36.5223|-91.572|Thayer|MO
K42U|41.1488|-111.767|Morgan|UT
K42V|40.058|-101.547|Benkelman|NE
K43A|35.3841|-79.7905|Star|NC
K43B|44.7264|-67.9907|Deblois|ME
K43D|47.3476|-118.677|Odessa|WA
K44C|42.4978|-88.9676|Beloit|WI
K44N|41.7074|-73.738|Millbrook|NY
K44U|39.0291|-111.838|Salina|UT
K45G|42.5698|-83.7785|Brighton|MI
K45K|39.0947|-97.7206|Minneapolis|KS
K45R|30.3363|-94.2575|Kountze/Silsbee|TX
K46D|47.4511|-99.1511|Carrington|ND
K46U|43.1819|-111.0387|Alpine|WY
K47A|34.3106|-84.4239|Canton|GA
K47K|38.2091|-97.5027|Moundridge|KS
K47N|40.5244|-74.5984|Manville|NJ
K47V|40.64|-100.4745|Curtis|NE
K48A|32.3994|-83.2759|Cochran|GA
K48D|43.8345|-84.7402|Clare|MI
K48I|38.687|-80.6518|Sutton|WV
K48K|38.4733|-99.9093|Ness City|KS
K48S|48.5661|-108.773|Harlem|MT
K49A|34.6279|-84.5249|Ellijay|GA
K49B|44.418|-103.375|Sturgis|SD
K49R|29.7455|-99.7609|Leakey|TX
K49X|34.5288|-114.432|Havasu Lake|CA
K4A0|33.5356|-84.1791|Stockbridge|GA
K4A4|34.0187|-85.1465|Cedartown|GA
K4A5|35.897|-92.659|Marshall|AR
K4A6|34.6887|-86.0059|Scottsboro|AL
K4A7|33.3899|-84.331|Hampton|GA
K4A9|34.4737|-85.7214|Fort Payne|AL
K4B6|43.8773|-73.4131|Ticonderoga|NY
K4B7|43.8579|-73.7406|Schroon Lake|NY
K4B8|41.6904|-72.8648|Plainville|CT
K4C8|40.9961|-92.7656|Albia|IA
K4D0|42.7752|-84.7364|Grand Ledge|MI
K4E7|46.016|-98.5153|Ellendale|ND
K4F2|32.1761|-94.2988|Carthage|TX
K4F7|33.8039|-93.3622|Prescott|AR
K4F8|33.4434|-93.0542|Stephens|AR
K4F9|46.3473|-98.2815|Lamoure|ND
K4G4|40.9618|-80.6773|Youngstown|OH
K4G5|39.779|-81.1028|Woodsfield|OH
K4G6|42.3821|-77.6821|Hornell|NY
K4G8|41.3188|-81.9602|Columbia Station|OH
K4I3|40.3287|-82.5238|Mount Vernon|OH
K4I7|39.6326|-86.8138|Greencastle|IN
K4I9|40.5245|-82.8501|Mount Gilead|OH
K4J1|31.2074|-81.9058|Nahunta|GA
K4J2|31.2126|-83.2263|Nashville|GA
K4J5|30.8058|-83.5865|Quitman|GA
K4J6|30.753|-81.5588|St Marys|GA
K4J8|32.3877|-82.5637|Soperton|GA
K4K3|39.21|-93.9269|Lexington|MO
K4K6|40.7321|-92.4283|Bloomfield|IA
K4M1|36.3813|-93.6246|Berryville|AR
K4M2|35.1495|-93.8635|Booneville|AR
K4M3|34.8082|-91.7121|Carlisle|AR
K4M7|36.7996|-86.8109|Russellville|KY
K4M9|36.4042|-90.6479|Corning|AR
K4N1|41.1282|-74.3467|West Milford|NJ
K4O4|33.9094|-94.8594|Idabel|OK
K4O5|36.788|-98.3585|Cherokee|OK
K4P3|44.0039|-96.5931|Flandreau|SD
K4R1|31.0155|-89.4826|Lumberton|MS
K4R3|31.4705|-87.8959|Jackson|AL
K4R4|30.4605|-87.877|Fairhope|AL
K4R5|46.7887|-90.7587|La Pointe|WI
K4R7|30.4663|-92.4238|Eunice|LA
K4R9|30.2605|-88.1275|Dauphin Island|AL
K4S1|42.4134|-124.424|Gold Beach|OR
K4S2|45.6726|-121.536|Hood River|OR
K4S3|45.3596|-117.254|Joseph|OR
K4S9|45.2163|-122.59|Portland-Mulino|OR
K4SD|39.6674|-119.876|Reno|NV
K4T2|32.9773|-97.4884|Fort Worth|TX
K4U6|47.4186|-105.562|Circle|MT
K4U9|44.7357|-112.72|Dell|MT
K4V0|40.094|-108.763|Rangely|CO
K4V1|37.6966|-104.784|Walsenburg|CO
K4V4|47.7249|-97.5901|Northwood|ND
K4V9|42.1122|-98.0387|Neligh|NE
K4X4|44.0619|-98.5322|Wessington Springs|SD
K4Y4|44.9125|-84.8764|Gaylord|MI
K50D|46.0091|-88.2739|Crystal Falls|MI
K50F|32.5818|-97.5908|Cresson|TX
K50I|40.7587|-87.4295|Kentland|IN
K50R|29.8503|-97.6724|Lockhart|TX
K51A|32.2837|-83.4382|Hawkinsville|GA
K51D|46.3512|-98.7372|Edgeley|ND
K51J|33.8536|-79.7681|Lake City|SC
K51R|30.9128|-95.952|Madisonville|TX
K52A|33.6121|-83.4604|Madison|GA
K52F|33.0516|-97.2321|Roanoke|TX
K52J|34.2445|-80.236|Bishopville|SC
K53A|32.302|-84.0075|Montezuma|GA
K54F|31.8818|-100.54|Robert Lee|TX
K54J|30.7313|-86.1516|Defuniak Springs|FL
K54T|29.7617|-94.8465|Baytown|TX
K55J|30.6118|-81.4612|Fernandina Beach|FL
K55K|37.2687|-97.0934|Oxford|KS
K55M|33.9255|-91.8611|Star City|AR
K55Y|43.8158|-91.8301|Rushford|MN
K56D|40.8834|-83.3145|Upper Sandusky|OH
K56F|32.8257|-100.415|Rotan|TX
K57C|42.7972|-88.3726|East Troy|WI
K57S|48.4802|-115.904|Troy|MT
K58M|39.5742|-75.8698|Elkton|MD
K5A1|41.2448|-82.5512|Norwalk|OH
K5A4|34.0158|-88.7262|Okolona|MS
K5A6|33.4654|-89.7292|Winona|MS
K5A9|32.9379|-84.6965|Woodbury|GA
K5B2|43.0513|-73.8612|Saratoga Springs|NY
K5B5|46.4944|-99.7601|Napoleon|ND
K5C1|29.7239|-98.6946|Boerne|TX
K5C3|47.3736|-94.7988|Nary|MN
K5C8|47.3512|-101.0259|Washburn|ND
K5F0|32.532|-92.9527|Arcadia|LA
K5F1|33.2019|-101.3371|Post|TX
K5F4|32.7885|-93.0037|Homer|LA
K5G7|40.8854|-83.8686|Bluffton|OH
K5H4|47.7912|-99.9317|Harvey|ND
K5I4|40.1779|-86.2173|Sheridan|IN
K5J9|34.0884|-78.8649|Loris|SC
K5K1|39.42|-89.9912|Palmyra|IL
K5L0|48.0294|-98.3253|Lakota|ND
K5M0|34.4082|-86.933|Hartselle|AL
K5M3|43.7679|-71.3876|Moultonborough|NH
K5M4|33.8458|-92.3655|Fordyce|AR
K5M5|36.3436|-94.4448|Decatur|AR
K5M8|33.9239|-93.1687|Gurdon|AR
K5M9|37.3363|-88.1098|Marion|KY
K5N2|45.5383|-90.276|Prentice|WI
K5N8|46.854|-97.2087|Casselton|ND
K5P2|45.7968|-100.784|Mc Laughlin|SD
K5R1|31.4518|-88.1945|Chatom|AL
K5R2|30.3894|-88.7534|Ocean Springs|MS
K5R4|30.4277|-87.701|Foley|AL
K5R8|30.4412|-93.4735|De Quincy|LA
K5S0|43.7526|-122.503|Oakridge|OR
K5S2|43.5326|-121.95|Crescent Lake|OR
K5S6|42.8579|-124.518|Sixes|OR
K5S9|45.3077|-122.3181|Estacada|OR
K5T6|31.881|-106.705|Santa Teresa|NM
K5T9|28.8598|-100.5167|Eagle Pass|TX
K5U1|47.8472|-111.698|Dutton|MT
K5U5|47.6291|-111.98|Fairfield|MT
K5U7|45.3198|-107.9315|Fort Smith|MT
K5V5|36.6978|-108.701|Shiprock|NM
K5W1|47.4185|-119.1093|Wilson Creek|WA
K5W4|35.0199|-79.191|Raeford|NC
K5W5|35.6203|-78.7003|Raleigh|NC
K5W8|35.7043|-79.5042|Siler City|NC
K5Y1|46.0354|-84.4177|Hessel|MI
K60C|43.7064|-90.2576|Elroy|WI
K60D|43.4385|-83.6354|Reese|MI
K60F|33.6487|-99.2606|Seymour|TX
K60J|33.9085|-78.4367|Ocean Isle Beach|NC
K60R|30.3719|-96.1133|Navasota|TX
K61A|31.9797|-87.3391|Camden|AL
K61B|35.9472|-114.8588|Boulder City|NV
K61C|42.9632|-88.8176|Jefferson|WI
K61R|30.8844|-93.7418|Newton|TX
K61S|43.7998|-123.029|Cottage Grove|OR
K62D|41.267|-80.9274|Warren|OH
K62S|43.2362|-120.6651|Christmas Valley|OR
K63B|43.763|-70.6725|Limington|ME
K63C|43.9601|-89.7892|Adams|WI
K63F|32.1736|-101.822|Stanton|TX
K63M|38.7052|-91.4906|Hermann|MO
K64S|42.7432|-122.488|Prospect|OR
K65J|33.2228|-82.3844|Wrens|GA
K65S|48.7258|-116.295|Bonners Ferry|ID
K66R|29.6411|-96.5158|Columbus|TX
K66V|37.255|-109.633|Bluff|UT
K66Y|30.363|-89.3877|Diamondhead|MS
K67A|31.9724|-86.5922|Fort Deposit|AL
K67L|36.8331|-114.0559|Mesquite|NV
K67R|26.4243|-98.8461|Rio Grande City|TX
K68F|31.6613|-96.31|Teague|TX
K68S|47.6521|-118.1738|Davenport|WA
K69K|39.1972|-96.2589|Wamego|KS
K69V|39.3612|-110.917|Huntington|UT
K6A1|32.5695|-84.2452|Butler|GA
K6A2|33.227|-84.2749|Griffin|GA
K6A4|36.4178|-81.8251|Mountain City|TN
K6B9|42.914|-76.4408|Skaneateles|NY
K6D1|45.5|-95.1128|Brooten|MN
K6D6|43.1423|-85.2538|Greenville|MI
K6D8|46.941|-98.0176|Valley City|ND
K6D9|44.3128|-83.4223|East Tawas|MI
K6E5|44.433|-97.5582|De Smet|SD
K6G1|41.6088|-79.7413|Titusville|PA
K6G4|40.3651|-82.4957|Mount Vernon|OH
K6G5|40.0024|-81.1918|Barnesville|OH
K6I2|37.6335|-85.2418|Springfield|KY
K6I4|40.0073|-86.4406|Lebanon|IN
K6J0|33.7946|-81.2459|Pelion|SC
K6J2|33.1955|-80.5085|Saint George|SC
K6J4|33.9268|-81.7946|Saluda|SC
K6J7|34.2821|-79.9287|Darlington|SC
K6K3|42.4713|-97.8843|Creighton|NE
K6K4|36.2901|-98.4758|Fairview|OK
K6K5|44.3046|-121.539|Sisters|OR
K6L3|46.4466|-97.7273|Lisbon|ND
K6L4|37.8557|-81.9159|Logan|WV
K6L5|46.2464|-99.5379|Wishek|ND
K6M0|34.7594|-91.6381|Hazen|AR
K6M2|36.2214|-91.7555|Horseshoe Bend|AR
K6M6|40.1292|-91.6783|Monticello|MO
K6M7|34.7803|-90.8106|Marianna|AR
K6R3|30.3564|-95.008|Cleveland|TX
K6R6|30.046|-102.213|Dryden|TX
K6S0|45.8067|-109.9792|Big Timber|MT
K6S1|45.2917|-108.9216|Bridger|MT
K6S2|43.9828|-124.111|Florence|OR
K6S3|45.6305|-109.239|Columbus|MT
K6S5|46.2515|-114.1256|Hamilton|MT
K6S8|45.7031|-108.761|Laurel|MT
K6U7|46.2935|-107.1936|Hysham|MT
K6V0|43.2952|-103.844|Edgemont|SD
K6V4|43.9993|-102.2545|Wall|SD
K6V5|45.5183|-102.4679|Bison|SD
K6Y1|45.7663|-84.5038|Bois Blanc Island|MI
K70A|32.2668|-87.7181|Linden|AL
K70J|30.888|-84.1547|Cairo|GA
K71A|31.9668|-87.5833|Pine Hill|AL
K71J|31.4318|-85.6206|Ozark|AL
K71N|40.8918|-76.7789|Sunbury|PA
K72F|33.1801|-99.1498|Throckmorton|TX
K73C|42.7805|-90.681|Lancaster|WI
K74S|48.4985|-122.6625|Anacortes|WA
K74V|40.2783|-110.051|Roosevelt|UT
K75J|31.6871|-83.6328|Ashburn|GA
K76F|32.6815|-95.9841|Wills Point|TX
K76G|42.7212|-82.5964|Marine City|MI
K77F|31.9472|-99.9858|Winters|TX
K77G|43.3118|-83.0909|Marlette|MI
K77S|43.9308|-123.007|Creswell|OR
K78R|31.5395|-94.1701|San Augustine|TX
K78Y|40.3307|-94.8348|Maryville|MO
K79D|39.1662|-80.0626|Philippi|WV
K79J|31.3088|-86.3938|Andalusia/Opp|AL
K79S|47.8451|-110.636|Fort Benton|MT
K7A0|32.6815|-87.6621|Greensboro|AL
K7A3|32.8118|-85.2295|Lanett|AL
K7A4|42.4664|-90.1694|Apple River|IL
K7A5|33.1296|-85.3721|Roanoke|AL
K7A6|34.8863|-85.8033|Stevenson|AL
K7A8|35.9446|-81.9957|Spruce Pine|NC
K7B2|42.3281|-72.6114|Northampton|MA
K7B6|41.9293|-72.5745|Warehouse Point|CT
K7D3|43.8755|-85.8421|Baldwin|MI
K7D9|41.7778|-80.904|Geneva|OH
K7F3|33.0362|-96.2431|Caddo Mills|TX
K7F5|32.5884|-95.8631|Canton|TX
K7F7|31.8173|-97.5697|Clifton|TX
K7G0|43.1811|-77.9158|Brockport|NY
K7G8|41.4496|-81.0629|Middlefield|OH
K7G9|43.3088|-96.5712|Canton|SD
K7I4|38.6584|-86.443|Orleans|IN
K7K4|37.4583|-86.85|Hartford|KY
K7K5|48.6676|-102.048|Kenmare|ND
K7K8|42.456|-96.4729|South Sioux City|NE
K7L2|46.2183|-100.245|Linton|ND
K7L8|39.75|-86.0139|Indianapolis|IN
K7M1|33.6202|-91.3648|McGehee|AR
K7M2|35.8645|-92.0903|Mountain View|AR
K7M3|34.5291|-93.5294|Mount Ida|AR
K7M4|35.6911|-90.0101|Osceola|AR
K7M5|35.5107|-93.8393|Ozark|AR
K7M8|36.2501|-90.3195|Rector|AR
K7M9|36.3569|-91.8304|Salem|AR
K7N1|42.1759|-77.1122|Corning|NY
K7S0|47.5672|-114.101|Ronan|MT
K7S1|45.5337|-112.3003|Twin Bridges|MT
K7S5|44.867|-123.198|Independence|OR
K7S6|46.5041|-110.913|White Sulphur Springs|MT
K7S7|48.2994|-112.2492|Valier|MT
K7S9|45.1804|-122.743|Hubbard|OR
K7T7|31.8585|-102.074|Midland|TX
K7V2|38.8314|-107.6458|Paonia|CO
K7V3|42.5273|-88.6513|Walworth|WI
K7V5|40.2643|-103.577|Brush|CO
K7V6|42.2596|-104.728|Guernsey|WY
K7V7|40.0821|-98.5414|Red Cloud|NE
K7V8|40.9708|-102.315|Julesburg|CO
K7V9|38.0539|-103.2387|Las Animas|CO
K7W5|41.3743|-84.0679|Napoleon|OH
K7W6|35.5624|-75.9552|Engelhard|NC
K7Y4|47.525|-95.3608|Bagley|MN
K80D|44.0528|-84.8125|Harrison|MI
K80F|34.1926|-95.6499|Antlers|OK
K80T|47.2116|-119.84|Quincy|WA
K81B|44.1574|-70.4813|Oxford|ME
K81R|31.2352|-98.717|San Saba|TX
K82A|32.2824|-84.5035|Buena Vista|GA
K82C|43.8387|-90.1377|New Lisbon|WI
K82J|30.3983|-87.3489|Pensacola|FL
K82V|41.1533|-104.13|Pine Bluffs|WY
K83D|45.8917|-84.7381|St Ignace|MI
K84D|44.9844|-101.251|Eagle Butte|SD
K84R|30.0282|-97.167|Smithville|TX
K86F|35.1236|-98.5752|Carnegie|OK
K87I|32.8832|-90.4636|Yazoo City|MS
K87K|37.8567|-93.9991|El Dorado Springs|MO
K87Y|43.1049|-89.1855|Madison|WI
K88M|48.9683|-115.0795|Eureka|MT
K88R|30.4736|-98.1215|Spicewood|TX
K8A0|34.2291|-86.2558|Albertville|AL
K8A1|34.403|-86.2636|Guntersville|AL
K8A3|36.4121|-85.3116|Livingston|TN
K8B0|44.9919|-70.6646|Rangeley|ME
K8B1|43.062|-71.9053|Hillsboro|NH
K8B5|42.3564|-72.1301|Barre/Barre Plains|MA
K8C4|41.7634|-91.1529|Tipton|IA
K8D1|43.9442|-88.1135|New Holstein|WI
K8D3|45.6699|-96.9942|Sisseton|SD
K8D7|44.8955|-97.7123|Clark|SD
K8F3|33.6238|-101.241|Crosbyton|TX
K8F5|33.1481|-94.7002|Daingerfield|TX
K8F6|43.8517|-100.712|Murdo|SD
K8G1|41.0387|-82.7246|Willard|OH
K8G2|41.9076|-79.6411|Corry|PA
K8G6|40.2384|-81.0129|Cadiz|OH
K8J7|47.6964|-99.1312|New Rockford|ND
K8K2|37.2783|-98.0432|Harper|KS
K8M1|34.5908|-88.6476|Baldwyn|MS
K8M8|44.8065|-84.2762|Lewiston|MI
K8M9|37.4248|-87.7361|Providence|KY
K8N2|41.3998|-88.7939|Ottawa|IL
K8N8|40.9483|-76.6441|Danville|PA
K8S0|48.6014|-113.115|Browning|MT
K8S1|47.6954|-114.185|Polson|MT
K8T6|28.3628|-98.1165|George West|TX
K8T8|29.5105|-98.7984|San Antonio|TX
K8U2|48.0796|-113.2433|Hungry Horse|MT
K8U6|46.7771|-105.3131|Terry|MT
K8U8|46.3312|-111.483|Townsend|MT
K8V1|37.7136|-106.354|Del Norte|CO
K8V2|42.5617|-99.0384|Atkinson|NE
K8V3|43.3776|-97.9898|Parkston|SD
K8V7|37.4587|-102.618|Springfield|CO
K8W2|38.6557|-78.709|New Market|VA
K8WC|37.9292|-90.7315|Potosi|MO
K8Y6|45.4437|-93.9703|Clear Lake|MN
K90F|34.014|-94.7586|Broken Bow|OK
K91F|35.1563|-95.6213|Canadian|OK
K92F|34.3687|-98.6821|Chattanooga|OK
K93C|43.2834|-90.2983|Richland Center|WI
K93F|35.6075|-99.7047|Cheyenne|OK
K93M|39.8491|-91.5949|Palmyra|MO
K93Y|41.2309|-97.1229|David City|NE
K94E|32.7594|-108.2096|Silver City|NM
K94K|36.6974|-93.9005|Cassville|MO
K94R|29.2661|-96.0077|Wharton|TX
K95D|47.2508|-101.814|Beulah|ND
K95E|33.8146|-106.6437|Socorro|NM
K95F|36.2838|-96.4633|Cleveland|OK
K96D|48.9406|-97.9028|Walhalla|ND
K97M|45.8778|-104.537|Ekalaka|MT
K98D|44.7008|-100.101|Onida|SD
K99N|33.3045|-81.1084|Bamberg|SC
K99V|38.7037|-107.647|Crawford|CO
K9A0|34.5828|-84.0199|Dahlonega|GA
K9A1|33.6325|-83.8496|Covington|GA
K9A5|34.6886|-85.2904|Lafayette|GA
K9C8|43.8959|-85.2792|Evart|MI
K9D0|44.5406|-99.4508|Highmore|SD
K9D1|43.2217|-99.4033|Gregory|SD
K9D2|45.5806|-103.53|Buffalo|SD
K9D4|40.3518|-76.3296|Myerstown|PA
K9D7|48.48|-99.2359|Cando|ND
K9D9|42.6636|-85.3463|Hastings|MI
K9F0|32.068|-98.3258|Dublin|TX
K9F8|45.2576|-99.7978|Hoven|SD
K9F9|32.6285|-97.3536|Fort Worth|TX
K9G2|42.8076|-83.7699|Linden|MI
K9G3|43.0211|-78.4825|Akron|NY
K9G8|40.4612|-78.7752|Ebensburg|PA
K9K7|38.7523|-98.2288|Ellsworth|KS
K9K8|37.669|-98.1239|Kingman|KS
K9L2|34.9908|-117.863|Edwards|CA
K9M4|33.3017|-89.2274|Ackerman|MS
K9M6|32.8492|-91.4039|Oak Grove|LA
K9M8|34.3274|-92.3513|Sheridan|AR
K9S2|48.8076|-105.4393|Scobey|MT
K9S4|47.1683|-114.854|Superior|MT
K9S5|45.8781|-111.569|Three Forks|MT
K9S9|45.4541|-119.69|Lexington|OR
K9U0|48.8542|-108.409|Turner|MT
K9U3|39.4677|-117.1976|Austin|NV
K9U4|41.0383|-107.497|Dixon|WY
K9U7|38.736|-115.48|Currant|NV
K9V5|42.7365|-102.4443|Rushville|NE
K9V6|43.1656|-101.7127|Martin|SD
K9V7|38.4785|-102.8109|Eads|CO
K9V9|43.7661|-99.3213|Chamberlain|SD
K9W7|42.904|-90.3639|Livingston|WI
K9Y1|47.3928|-102.771|Killdeer|ND
KA08|32.5124|-87.3856|Marion|AL
KA09|34.8875|-114.6166|Bullhead City|AZ
KA20|35.0059|-114.5651|Bullhead City|AZ
KA24|41.4124|-120.684|Alturas|CA
KA30|41.5582|-122.855|Etna|CA
KA32|41.8872|-121.976|Dorris|CA
KA34|39.2386|-119.555|Dayton/Carson City|NV
KA39|32.9908|-111.9185|Maricopa|AZ
KA50|38.8744|-104.41|Ellicott|CO
KAAA|40.1587|-89.335|Lincoln|IL
KAAF|29.7275|-85.0275|Apalachicola|FL
KAAO|37.7476|-97.2211|Wichita|KS
KAAS|37.3583|-85.3094|Campbellsville|KY
KAAT|41.4827|-120.5657|Alturas|CA
KABE|40.6518|-75.4428|Allentown/Bethlehem|PA
KABI|32.4113|-99.6819|Abilene|TX
KABQ|35.04|-106.6089|Albuquerque|NM
KABR|45.4491|-98.4218|Aberdeen|SD
KABY|31.5329|-84.1962|Albany|GA
KACB|44.9886|-85.1984|Bellaire|MI
KACJ|32.1108|-84.1889|Americus|GA
KACK|41.2531|-70.0602|Nantucket|MA
KACP|30.7503|-92.6883|Oakdale|LA
KACQ|44.0735|-93.5529|Waseca|MN
KACT|31.6113|-97.2305|Waco|TX
KACV|40.9781|-124.109|Arcata/Eureka|CA
KACY|39.4562|-74.5775|Atlantic City|NJ
KACZ|34.7179|-78.0036|Wallace|NC
KADC|46.4503|-95.211|Wadena|MN
KADG|41.8662|-84.078|Adrian|MI
KADH|34.8052|-96.672|Ada|OK
KADM|34.303|-97.0196|Ardmore|OK
KADT|39.8412|-101.0432|Atwood|KS
KADU|41.7014|-94.9205|Audubon|IA
KADW|38.8108|-76.867|Camp Springs|MD
KAEG|35.1452|-106.795|Albuquerque|NM
KAEJ|38.8129|-106.1201|Buena Vista|CO
KAEL|43.6815|-93.3672|Albert Lea|MN
KAEX|31.3258|-92.5467|Alexandria|LA
KAFF|38.9725|-104.8211|Colorado Springs|CO
KAFJ|40.1365|-80.2902|Washington|PA
KAFK|40.6069|-95.8657|Nebraska City|NE
KAFN|42.8051|-72.003|Jaffrey|NH
KAFO|42.7112|-110.942|Afton|WY
KAFP|35.0206|-80.0771|Wadesboro|NC
KAFW|32.9904|-97.3195|Fort Worth|TX
KAGC|40.3544|-79.9302|Pittsburgh|PA
KAGO|33.228|-93.217|Magnolia|AR
KAGR|27.6506|-81.3494|Avon Park|FL
KAGS|33.3699|-81.9645|Augusta|GA
KAGZ|43.0649|-98.2967|Wagner|SD
KAHC|40.2662|-120.153|Herlong|CA
KAHH|45.2811|-92.3754|Amery|WI
KAHN|33.9488|-83.3256|Athens|GA
KAHQ|41.2413|-96.594|Wahoo|NE
KAIA|42.0525|-102.804|Alliance|NE
KAIB|38.239|-108.5634|Nucla|CO
KAID|40.1086|-85.613|Anderson|IN
KAIG|45.1542|-89.1107|Antigo|WI
KAIK|33.6494|-81.685|Aiken|SC
KAIO|41.4073|-95.0469|Atlantic|IA
KAIT|46.5484|-93.6768|Aitkin|MN
KAIV|33.1065|-88.1978|Aliceville|AL
KAIZ|38.096|-92.5495|Kaiser Lake Ozark|MO
KAJG|38.6065|-87.7267|Mount Carmel|IL
KAJO|33.8977|-117.602|Corona|CA
KAJR|34.4999|-83.5567|Cornelia|GA
KAJZ|38.7864|-108.064|Delta|CO
KAKH|35.2026|-81.1499|Gastonia|NC
KAKO|40.1756|-103.222|Akron|CO
KAKQ|36.9872|-77.0011|Wakefield|VA
KAKR|41.0374|-81.4678|Akron|OH
KALB|42.7483|-73.8017|Albany|NY
KALI|27.7409|-98.0269|Alice|TX
KALM|32.8378|-105.9931|Alamogordo|NM
KALN|38.8903|-90.046|Alton/St Louis|IL
KALO|42.5571|-92.4003|Waterloo|IA
KALS|37.4349|-105.867|Alamosa|CO
KALW|46.0949|-118.288|Walla Walla|WA
KALX|32.9147|-85.963|Alexander City|AL
KAMA|35.2179|-101.7064|Amarillo|TX
KAMG|31.5361|-82.5066|Alma|GA
KAMN|43.3221|-84.688|Alma|MI
KAMT|38.8515|-83.5663|West Union|OH
KAMW|41.992|-93.6218|Ames|IA
KANB|33.5882|-85.8581|Anniston|AL
KAND|34.4946|-82.7094|Anderson|SC
KANE|45.145|-93.2114|Minneapolis|MN
KANJ|46.4792|-84.3684|Sault Ste Marie|MI
KANK|38.5383|-106.049|Salida|CO
KANP|38.9429|-76.5684|Annapolis|MD
KANQ|41.6397|-85.0835|Angola|IN
KANW|42.5792|-99.993|Ainsworth|NE
KANY|37.1585|-98.0796|Anthony|KS
KAOC|43.6035|-113.334|Arco|ID
KAOH|40.7069|-84.0267|Lima|OH
KAOO|40.2964|-78.32|Altoona|PA
KAOV|36.9719|-92.6819|Ava|MO
KAPA|39.5701|-104.849|Denver|CO
KAPC|38.2132|-122.281|Napa|CA
KAPF|26.1526|-81.7753|Naples|FL
KAPG|39.4662|-76.1688|Aberdeen|MD
KAPH|38.0686|-77.3193|Bowling Green|VA
KAPN|45.0781|-83.5603|Alpena|MI
KAPT|35.0607|-85.5853|Jasper|TN
KAPV|34.5753|-117.186|Apple Valley|CA
KAPY|26.9688|-99.2489|Zapata|TX
KAQO|30.7837|-98.662|Llano|TX
KAQP|45.2282|-96.0059|Appleton|MN
KAQR|34.3983|-96.1481|Atoka|OK
KAQW|42.6959|-73.1704|North Adams|MA
KAQX|32.9951|-81.2702|Allendale|SC
KARA|30.0378|-91.8839|New Iberia|LA
KARB|42.223|-83.7456|Ann Arbor|MI
KARG|36.1247|-90.9251|Walnut Ridge|AR
KARM|29.2543|-96.1544|Wharton|TX
KARR|41.7719|-88.4757|Chicago/Aurora|IL
KART|43.9919|-76.0217|Watertown|NY
KARV|45.9279|-89.7309|Woodruff|WI
KARW|32.4122|-80.6344|Beaufort|SC
KASD|30.3451|-89.8208|Slidell|LA
KASE|39.2232|-106.869|Aspen|CO
KASG|36.1764|-94.1193|Springdale|AR
KASH|42.7817|-71.5148|Nashua|NH
KASJ|36.2975|-77.1709|Aulander|NC
KASL|32.5205|-94.3078|Marshall|TX
KASN|33.5699|-86.0509|Talladega|AL
KAST|46.158|-123.879|Astoria|OR
KASW|41.2747|-85.8401|Warsaw|IN
KASX|46.5485|-90.919|Ashland|WI
KASY|46.0239|-99.3526|Ashley|ND
KATA|33.1018|-94.1953|Atlanta|TX
KATL|33.6367|-84.4281|Atlanta|GA
KATS|32.8525|-104.468|Artesia|NM
KATW|44.2585|-88.519|Appleton|WI
KATY|44.914|-97.1547|Watertown|SD
KAUG|44.3206|-69.7973|Augusta|ME
KAUH|40.8941|-97.9946|Aurora|NE
KAUM|43.665|-92.9334|Austin|MN
KAUN|38.9548|-121.082|Auburn|CA
KAUO|32.6151|-85.434|Auburn|AL
KAUS|30.1975|-97.662|Austin|TX
KAUW|44.9262|-89.6266|Wausau|WI
KAVC|36.6883|-78.0545|Brodnax|VA
KAVK|36.7732|-98.6699|Alva|OK
KAVL|35.4355|-82.5419|Asheville|NC
KAVO|27.5912|-81.5278|Avon Park|FL
KAVP|41.3371|-75.7242|Wilkes-Barre/Scranton|PA
KAVQ|32.4091|-111.2162|Marana|AZ
KAVX|33.4049|-118.416|Avalon|CA
KAWG|41.2761|-91.6734|Washington|IA
KAWM|35.1351|-90.2344|West Memphis|AR
KAWO|48.1614|-122.1584|Arlington|WA
KAXA|43.0781|-94.2718|Algona|IA
KAXH|29.5061|-95.4769|Rosharon|TX
KAXN|45.8663|-95.3947|Alexandria|MN
KAXQ|41.2258|-79.441|Clarion|PA
KAXS|34.698|-99.3385|Altus|OK
KAXV|40.4934|-84.2989|Wapakoneta|OH
KAXX|36.422|-105.29|Angel Fire|NM
KAYS|31.2491|-82.3955|Waycross|GA
KAYX|35.3926|-86.0858|Manchester|TN
KAZC|36.9592|-113.0132|Colorado City|AZ
KAZE|31.8847|-82.6474|Hazlehurst|GA
KAZO|42.2321|-85.5496|Kalamazoo|MI
KB16|43.0803|-76.5384|Weedsport|NY
KB19|43.4641|-70.4724|Biddeford|ME
KB21|45.0853|-70.2164|Carrabassett|ME
KBAB|39.1361|-121.437|Beale Air Force Base|CA
KBAD|32.5018|-93.6627|Bossier City|LA
KBAF|42.1578|-72.7156|Westfield|MA
KBAK|39.2624|-85.8956|Columbus|IN
KBAM|40.599|-116.874|Battle Mountain|NV
KBAX|43.7802|-82.9854|Bad Axe|MI
KBAZ|29.7045|-98.0422|New Braunfels|TX
KBBB|45.3319|-95.6506|Benson|MN
KBBD|31.1789|-99.3247|Brady|TX
KBBG|36.5321|-93.2005|Branson|MO
KBBP|34.6217|-79.7344|Bennettsville|SC
KBBW|41.4365|-99.6422|Broken Bow|NE
KBCB|37.2076|-80.4078|Blacksburg|VA
KBCE|37.7064|-112.145|Bryce Canyon|UT
KBCK|44.2507|-90.8553|Black River Falls|WI
KBCT|26.3785|-80.1077|Boca Raton|FL
KBDE|48.7284|-94.6122|Baudette|MN
KBDG|37.5833|-109.483|Blanding|UT
KBDH|45.1176|-95.1323|Willmar|MN
KBDJ|46.1374|-89.646|Boulder Junction|WI
KBDL|41.9386|-72.688|Hartford|CT
KBDN|44.0948|-121.201|Bend|OR
KBDQ|35.1362|-92.7135|Morrilton|AR
KBDR|41.1635|-73.1262|Bridgeport|CT
KBDU|40.0391|-105.226|Boulder|CO
KBEA|28.3619|-97.791|Beeville|TX
KBEC|37.6945|-97.215|Wichita|KS
KBEH|42.1286|-86.4285|Benton Harbor|MI
KBFA|45.1658|-84.9241|Boyne Falls|MI
KBFD|41.8031|-78.6401|Bradford|PA
KBFE|33.1731|-102.193|Brownfield|TX
KBFF|41.874|-103.596|Scottsbluff|NE
KBFI|47.527|-122.2999|Seattle|WA
KBFK|36.8633|-99.6187|Buffalo|OK
KBFL|35.4336|-119.057|Bakersfield|CA
KBFM|30.6268|-88.0681|Mobile|AL
KBFR|38.84|-86.4454|Bedford|IN
KBGD|35.7009|-101.394|Borger|TX
KBGE|30.9715|-84.6374|Bainbridge|GA
KBGF|35.1775|-86.0662|Winchester|TN
KBGM|42.2087|-75.9798|Binghamton|NY
KBGR|44.8064|-68.8267|Bangor|ME
KBHB|44.45|-68.3615|Bar Harbor|ME
KBHC|31.7138|-82.3938|Baxley|GA
KBHK|46.3466|-104.2581|Baker|MT
KBHM|33.5629|-86.7507|Birmingham|AL
KBID|41.1684|-71.5786|Block Island|RI
KBIE|40.3013|-96.7541|Beatrice|NE
KBIF|31.8495|-106.38|Fort Bliss/El Paso|TX
KBIH|37.3731|-118.364|Bishop|CA
KBIJ|31.3975|-84.8948|Blakely|GA
KBIL|45.8089|-108.5412|Billings|MT
KBIS|46.7727|-100.7469|Bismarck|ND
KBIV|42.7429|-86.1074|Holland|MI
KBIX|30.4104|-88.9244|Biloxi|MS
KBJC|39.9088|-105.117|Denver|CO
KBJI|47.5094|-94.9337|Bemidji|MN
KBJJ|40.8748|-81.8883|Wooster|OH
KBKD|32.719|-98.891|Breckenridge|TX
KBKE|44.8373|-117.809|Baker City|OR
KBKF|39.7017|-104.752|Aurora|CO
KBKL|41.5175|-81.6833|Cleveland|OH
KBKN|36.7452|-97.3496|Blackwell|OK
KBKS|27.2068|-98.1212|Falfurrias|TX
KBKT|37.0742|-77.9575|Blackstone|VA
KBKV|28.4695|-82.4571|Brooksville|FL
KBKW|37.7873|-81.1242|Beaver|WV
KBKX|44.3048|-96.8169|Brookings|SD
KBLF|37.2958|-81.2077|Bluefield|WV
KBLH|33.6192|-114.717|Blythe|CA
KBLI|48.7928|-122.538|Bellingham|WA
KBLM|40.1869|-74.1249|Belmar/Farmingdale|NJ
KBLU|39.275|-120.71|Emigrant Gap|CA
KBLV|38.5452|-89.8352|Belleville|IL
KBMC|41.5524|-112.062|Brigham City|UT
KBMG|39.146|-86.6167|Bloomington|IN
KBMI|40.4771|-88.9159|Bloomington/Normal|IL
KBML|44.5754|-71.1759|Berlin|NH
KBMQ|30.7389|-98.2386|Burnet|TX
KBMT|30.0706|-94.2157|Beaumont|TX
KBNA|36.1245|-86.6782|Nashville|TN
KBNG|33.9225|-116.8507|Banning|CA
KBNL|33.2578|-81.3883|Barnwell|SC
KBNO|43.5905|-118.9552|Burns|OR
KBNW|42.0496|-93.8476|Boone|IA
KBOI|43.5644|-116.223|Boise|ID
KBOK|42.0746|-124.29|Brookings|OR
KBOS|42.362|-71.0079|Boston|MA
KBOW|27.9434|-81.7834|Bartow|FL
KBPG|32.2126|-101.522|Big Spring|TX
KBPI|42.5851|-110.111|Big Piney|WY
KBPK|36.3689|-92.4705|Mountain Home|AR
KBPT|29.9508|-94.0207|Beaumont/Port Arthur|TX
KBQK|31.2588|-81.4665|Brunswick|GA
KBQP|32.756|-91.8806|Bastrop|LA
KBQR|42.9223|-78.6123|Lancaster|NY
KBRD|46.4029|-94.1297|Brainerd|MN
KBRL|40.7832|-91.1255|Burlington|IA
KBRO|25.9072|-97.4252|Brownsville|TX
KBRY|37.8143|-85.4996|Bardstown|KY
KBST|44.4094|-69.0119|Belfast|ME
KBTA|41.4149|-96.1091|Blair|NE
KBTF|40.8694|-111.927|Bountiful|UT
KBTL|42.3073|-85.2515|Battle Creek|MI
KBTM|45.9548|-112.497|Butte|MT
KBTN|45.8152|-97.7431|Britton|SD
KBTP|40.7769|-79.9497|Butler|PA
KBTR|30.5332|-91.1496|Baton Rouge|LA
KBTV|44.4719|-73.1533|Burlington|VT
KBTY|36.8611|-116.787|Beatty|NV
KBUB|41.7767|-99.1497|Burwell|NE
KBUF|42.9405|-78.7322|Buffalo|NY
KBUM|38.2898|-94.3401|Butler|MO
KBUU|42.6907|-88.3046|Burlington|WI
KBUY|36.0485|-79.4749|Burlington|NC
KBVI|40.7725|-80.3914|Beaver Falls|PA
KBVN|41.7286|-98.0558|Albion|NE
KBVO|36.7625|-96.0112|Bartlesville|OK
KBVS|48.4709|-122.421|Burlington|WA
KBVX|35.7262|-91.6473|Batesville|AR
KBVY|42.5842|-70.9165|Beverly / Danvers|MA
KBWC|32.9929|-115.517|Brawley|CA
KBWD|31.7936|-98.9565|Brownwood|TX
KBWG|36.9645|-86.4197|Bowling Green|KY
KBWI|39.1754|-76.6683|Baltimore|MD
KBWP|46.2441|-96.6074|Wahpeton|ND
KBWW|46.1655|-103.3007|Bowman|ND
KBXA|30.8137|-89.865|Bogalusa|LA
KBXG|33.0413|-82.0027|Waynesboro|GA
KBXK|33.4224|-112.6863|Buckeye|AZ
KBXM|43.8922|-69.9386|Brunswick|ME
KBYG|44.3811|-106.722|Buffalo|WY
KBYH|35.9643|-89.944|Blytheville|AR
KBYI|42.5426|-113.772|Burley|ID
KBYS|35.2805|-116.63|Fort Irwin/Barstow|CA
KBYY|28.9733|-95.8635|Bay City|TX
KBZN|45.7789|-111.1537|Bozeman|MT
KC02|42.6149|-88.3896|Lake Geneva|WI
KC03|41.4462|-85.9348|Nappanee|IN
KC04|43.6417|-86.3292|Hart/Shelby|MI
KC08|38.0132|-105.3743|Westcliffe|CO
KC09|41.4254|-88.4187|Morris|IL
KC15|40.4882|-89.6759|Pekin|IL
KC16|40.1464|-88.1985|Urbana|IL
KC17|42.0311|-91.5293|Marion|IA
KC20|41.9517|-86.3677|Berrien Springs|MI
KC24|37.822|-106.9303|Creede|CO
KC27|42.4905|-91.4979|Manchester|IA
KC29|43.1143|-89.5315|Middleton|WI
KC35|43.5259|-89.9832|Reedsburg|WI
KC47|43.5603|-89.4829|Portage|WI
KC57|43.0957|-84.3496|Elsie|MI
KC62|41.4727|-85.2608|Kendallville|IN
KC65|41.3651|-86.3005|Plymouth|IN
KC71|31.296|-91.0529|Crosby|MS
KC73|41.8339|-89.444|Dixon|IL
KC74|42.7042|-90.9646|Cassville|WI
KC75|41.0184|-89.3859|Lacon|IL
KC77|42.3229|-88.8363|Poplar Grove|IL
KC80|36.1631|-120.294|Coalinga|CA
KC81|42.3246|-88.0741|Grayslake|IL
KC83|37.8265|-121.6217|Byron|CA
KC88|43.1391|-84.2225|Oakley|MI
KC91|41.9929|-86.128|Dowagiac|MI
KCA2|52.2188|-123.4495|The Hill|BC
KCAD|44.2757|-85.4215|Cadillac|MI
KCAE|33.9382|-81.123|Columbia|SC
KCAG|40.4952|-107.522|Craig|CO
KCAK|40.9161|-81.4422|Akron|OH
KCAO|36.4462|-103.167|Clayton|NM
KCAR|46.8715|-68.0179|Caribou|ME
KCAV|42.7419|-93.7589|Clarion|IA
KCBE|39.6154|-78.7609|Wiley Ford|WV
KCBF|41.2592|-95.7606|Council Bluffs|IA
KCBG|45.5575|-93.2642|Cambridge|MN
KCBK|39.4275|-101.047|Colby|KS
KCBM|33.6438|-88.4438|Columbus|MS
KCCA|35.5978|-92.4516|Clinton|AR
KCCB|34.1116|-117.688|Upland|CA
KCCO|33.3116|-84.7698|Newnan|GA
KCCR|37.9897|-122.057|Concord|CA
KCCY|43.0726|-92.6108|Charles City|IA
KCDA|44.5691|-72.018|Lyndonville|VT
KCDC|37.701|-113.099|Cedar City|UT
KCDH|33.6228|-92.7634|Camden|AR
KCDI|39.975|-81.5776|Cambridge|OH
KCDK|29.1341|-83.0508|Cedar Key|FL
KCDN|34.2836|-80.5649|Camden|SC
KCDR|42.8376|-103.095|Chadron|NE
KCDS|34.4338|-100.288|Childress|TX
KCDW|40.8752|-74.2814|Caldwell|NJ
KCEA|37.6486|-97.2506|Wichita|KS
KCEC|41.7789|-124.2364|Crescent City|CA
KCEF|42.194|-72.5348|Chicopee|MA
KCEK|40.6179|-96.9249|Crete|NE
KCEU|34.6719|-82.8865|Clemson|SC
KCEV|39.6985|-85.1297|Connersville|IN
KCEW|30.7788|-86.5221|Crestview|FL
KCEY|36.6646|-88.3728|Murray|KY
KCEZ|37.303|-108.628|Cortez|CO
KCFD|30.7157|-96.3314|Bryan|TX
KCFE|45.159|-93.8433|Buffalo|MN
KCFJ|39.9756|-86.9199|Crawfordsville|IN
KCFS|43.4588|-83.4455|Caro|MI
KCFT|32.957|-109.2114|Clifton|AZ
KCFV|37.094|-95.5719|Coffeyville|KS
KCGC|28.8672|-82.571|Crystal River|FL
KCGE|38.5393|-76.0304|Cambridge|MD
KCGF|41.5651|-81.4864|Cleveland|OH
KCGI|37.2253|-89.5708|Cape Girardeau|MO
KCGS|38.9806|-76.9223|College Park|MD
KCGZ|32.9549|-111.767|Casa Grande|AZ
KCHA|35.0353|-85.2038|Chattanooga|TN
KCHD|33.2691|-111.811|Chandler|AZ
KCHK|35.0974|-97.9677|Chickasha|OK
KCHN|27.5149|-81.8805|Wauchula|FL
KCHO|38.1386|-78.4529|Charlottesville|VA
KCHQ|36.8421|-89.3597|Charleston|MO
KCHS|32.8962|-80.0382|Charleston|SC
KCHT|39.7822|-93.4957|Chillicothe|MO
KCHU|43.5964|-91.5039|Caledonia|MN
KCIC|39.7956|-121.8577|Chico|CA
KCID|41.8847|-91.7108|Cedar Rapids|IA
KCII|47.8283|-112.168|Choteau|MT
KCIN|42.0462|-94.789|Carroll|IA
KCIR|37.0645|-89.2196|Cairo|IL
KCIU|46.242|-84.4621|Kincheloe|MI
KCJR|38.5267|-77.8589|Culpeper|VA
KCKA|36.7343|-98.1235|Cherokee|OK
KCKB|39.2966|-80.2281|Bridgeport|WV
KCKC|47.8383|-90.3829|Grand Marais|MN
KCKF|31.9888|-83.7739|Cordele|GA
KCKI|33.7172|-79.857|Kingstree|SC
KCKM|34.2997|-90.5123|Clarksdale|MS
KCKN|47.8417|-96.6216|Crookston|MN
KCKP|42.7317|-95.5559|Cherokee|IA
KCKV|36.6219|-87.415|Clarksville|TN
KCKZ|40.3892|-75.2905|Perkasie|PA
KCLE|41.4117|-81.8498|Cleveland|OH
KCLI|44.6138|-88.7313|Clintonville|WI
KCLK|35.5383|-98.9327|Clinton|OK
KCLL|30.5886|-96.3638|College Station|TX
KCLM|48.1202|-123.5|Port Angeles|WA
KCLR|33.1315|-115.521|Calipatria|CA
KCLS|46.677|-122.983|Chehalis|WA
KCLT|35.214|-80.9431|Charlotte|NC
KCLW|27.9767|-82.7587|Clearwater|FL
KCMA|34.2137|-119.094|Camarillo|CA
KCMD|34.2687|-86.858|Cullman|AL
KCMH|39.998|-82.8919|Columbus|OH
KCMI|40.0398|-88.2762|Savoy|IL
KCMR|35.3056|-112.1944|Williams|AZ
KCMX|47.1684|-88.4891|Hancock|MI
KCMY|43.958|-90.7379|Sparta|WI
KCNB|44.7295|-96.266|Canby|MN
KCNC|41.0196|-93.3597|Chariton|IA
KCNH|43.3704|-72.3687|Claremont|NH
KCNK|39.5493|-97.6523|Concordia|KS
KCNM|32.3375|-104.263|Carlsbad|NM
KCNO|33.9747|-117.637|Chino|CA
KCNP|41.0775|-102.464|Chappell|NE
KCNU|37.6682|-95.4867|Chanute|KS
KCNW|31.6411|-97.0733|Waco|TX
KCNY|38.755|-109.755|Moab|UT
KCOD|44.5202|-109.024|Cody|WY
KCOE|47.7743|-116.82|Coeur d'Alene|ID
KCOF|28.2349|-80.6101|Cocoa Beach|FL
KCOI|28.3416|-80.6855|Merritt Island|FL
KCOM|31.8411|-99.4036|Coleman|TX
KCON|43.2027|-71.5023|Concord|NH
KCOQ|46.7011|-92.5036|Cloquet|MN
KCOS|38.8058|-104.701|Colorado Springs|CO
KCOT|28.4567|-99.2203|Cotulla|TX
KCOU|38.8181|-92.2196|Columbia|MO
KCPC|34.2729|-78.715|Whiteville|NC
KCPK|36.6656|-76.3207|Chesapeake|VA
KCPM|33.89|-118.244|Compton|CA
KCPR|42.9074|-106.4616|Casper|WY
KCPS|38.5707|-90.1562|Cahokia/St Louis|IL
KCPT|32.3538|-97.4337|Cleburne|TX
KCPU|38.1461|-120.648|San Andreas|CA
KCQA|40.4841|-84.5601|Celina|OH
KCQB|35.7238|-96.8203|Chandler|OK
KCQM|47.8219|-92.6894|Cook|MN
KCQW|34.7129|-79.957|Cheraw|SC
KCQX|41.6884|-69.9895|Chatham|MA
KCRE|33.8117|-78.7239|North Myrtle Beach|SC
KCRG|30.3363|-81.5144|Jacksonville|FL
KCRP|27.7704|-97.5012|Corpus Christi|TX
KCRQ|33.1283|-117.28|Carlsbad|CA
KCRS|32.0281|-96.4006|Corsicana|TX
KCRT|33.1783|-91.8802|Crossett|AR
KCRW|38.3731|-81.5932|Charleston|WV
KCRX|34.915|-88.6035|Corinth|MS
KCRZ|40.9941|-94.755|Corning|IA
KCSB|40.3066|-100.162|Cambridge|NE
KCSG|32.5164|-84.9396|Columbus|GA
KCSM|35.3398|-99.2005|Clinton|OK
KCSQ|41.0214|-94.3633|Creston|IA
KCSV|35.9513|-85.085|Crossville|TN
KCTB|48.6087|-112.3782|Cut Bank|MT
KCTJ|33.631|-85.152|Carrollton|GA
KCTK|40.5691|-90.0748|Canton|IL
KCTY|29.6355|-83.1048|Cross City|FL
KCTZ|34.9756|-78.3646|Clinton|NC
KCUB|33.9705|-80.9952|Columbia|SC
KCUH|35.9505|-96.7724|Cushing|OK
KCUL|38.0895|-88.1231|Carmi|IL
KCUT|43.7333|-103.618|Custer|SD
KCVB|29.3419|-98.8509|Castroville|TX
KCVG|39.0488|-84.6678|Cincinnati / Covington|KY
KCVH|36.8933|-121.41|Hollister|CA
KCVK|36.2649|-91.5626|Ash Flat|AR
KCVN|34.4266|-103.0788|Clovis|NM
KCVO|44.4972|-123.29|Corvallis|OR
KCVS|34.3828|-103.322|Clovis|NM
KCVX|45.3048|-85.2748|Charlevoix|MI
KCWA|44.7774|-89.6702|Mosinee|WI
KCWC|33.8578|-98.4904|Wichita Falls|TX
KCWF|30.2106|-93.1432|Lake Charles|LA
KCWI|41.8311|-90.3291|Clinton|IA
KCWV|32.1951|-81.8696|Claxton|GA
KCXE|36.7883|-78.5016|Chase City|VA
KCXL|32.6695|-115.513|Calexico|CA
KCXO|30.3518|-95.4145|Houston|TX
KCXP|39.1943|-119.7343|Carson City|NV
KCXU|31.2131|-84.2354|Camilla|GA
KCXW|35.0199|-92.5551|Conway|AR
KCXY|40.2171|-76.8515|Harrisburg|PA
KCYO|39.516|-82.9821|Circleville|OH
KCYS|41.1557|-104.812|Cheyenne|WY
KCYW|39.3871|-97.1572|Clay Center|KS
KCZD|40.8691|-100.004|Cozad|NE
KCZG|42.0785|-76.0963|Endicott|NY
KCZK|45.6766|-121.8789|Cascade Locks|OR
KCZL|34.4554|-84.9392|Calhoun|GA
KCZT|28.5222|-99.8236|Carrizo Springs|TX
KD00|47.2605|-96.4003|Twin Valley|MN
KD02|43.2924|-92.7956|Osage|IA
KD05|47.6559|-101.437|Garrison|ND
KD07|45.0361|-102.02|Faith|SD
KD09|48.8304|-100.417|Bottineau|ND
KD11|43.72|-85.0028|Weidman|MI
KD14|47.5519|-96.2919|Fertile|MN
KD17|38.7575|-108.148|Delta|CO
KD23|42.5654|-78.4248|Arcade|NY
KD25|46.122|-89.8823|Manitowish Waters|WI
KD31|48.285|-99.4035|Leeds|ND
KD37|48.1915|-96.7119|Warren|MN
KD39|45.7066|-94.9334|Sauk Centre|MN
KD42|44.2311|-94.9989|Springfield|MN
KD50|48.9285|-103.297|Crosby|ND
KD54|46.9008|-96.9187|Fargo|ND
KD55|48.753|-98.3933|Langdon|ND
KD56|47.4763|-97.3281|Mayville|ND
KD57|46.8128|-101.86|Glen Ullin|ND
KD60|48.3805|-102.898|Tioga|ND
KD64|48.9134|-101.033|Westhope|ND
KD68|34.1311|-109.3103|Springerville|AZ
KD73|33.7824|-83.6929|Monroe|GA
KD74|38.8497|-75.6128|Farmington|DE
KD83|39.0127|-123.383|Boonville|CA
KD86|36.4486|-119.319|Visalia|CA
KD95|43.0666|-83.2723|Lapeer|MI
KD98|42.7961|-82.975|Romeo|MI
KDAA|38.715|-77.181|Fort Belvoir|VA
KDAB|29.1825|-81.0595|Daytona Beach|FL
KDAF|44.0334|-90.0851|Necedah|WI
KDAG|34.8537|-116.787|Daggett|CA
KDAN|36.5729|-79.3361|Danville|VA
KDAW|43.2841|-70.9293|Rochester|NH
KDAY|39.9024|-84.2194|Dayton|OH
KDBN|32.5644|-82.9853|Dublin|GA
KDBQ|42.402|-90.7095|Dubuque|IA
KDCA|38.8521|-77.0377|Washington|DC
KDCM|34.7893|-81.1958|Chester|SC
KDCU|34.6527|-86.9454|Decatur|AL
KDCY|38.7004|-87.1297|Washington|IN
KDDC|37.7634|-99.9656|Dodge City|KS
KDDH|42.8913|-73.2464|Bennington|VT
KDEC|39.8346|-88.8657|Decatur|IL
KDED|29.067|-81.2838|Deland|FL
KDEH|43.2755|-91.7394|Decorah|IA
KDEN|39.86|-104.6738|Denver|CO
KDEQ|34.047|-94.3994|DeQueen|AR
KDET|42.4092|-83.0099|Detroit|MI
KDEW|47.9666|-117.427|Deer Park|WA
KDFI|41.3375|-84.4288|Defiance|OH
KDFW|32.8968|-97.038|Dallas-Fort Worth|TX
KDGL|31.3426|-109.506|Douglas|AZ
KDGW|42.7972|-105.386|Douglas|WY
KDHN|31.3213|-85.4496|Dothan|AL
KDHT|36.0226|-102.547|Dalhart|TX
KDIJ|43.7427|-111.0968|Driggs|ID
KDIK|46.7975|-102.8019|Dickinson|ND
KDKB|41.9363|-88.7041|DeKalb|IL
KDKK|42.4925|-79.2731|Dunkirk|NY
KDKR|31.307|-95.4038|Crockett|TX
KDKX|35.9639|-83.8739|Knoxville|TN
KDLC|34.4491|-79.3686|Dillon|SC
KDLF|29.3595|-100.778|Del Rio|TX
KDLH|46.8419|-92.1987|Duluth|MN
KDLL|43.5227|-89.7702|Baraboo|WI
KDLN|45.2554|-112.553|Dillon|MT
KDLO|35.7456|-119.237|Delano|CA
KDLS|45.621|-121.1708|Dallesport / The Dalles|OR
KDLZ|40.2797|-83.1148|Delaware|OH
KDMA|32.1665|-110.883|Tucson|AZ
KDMN|32.2623|-107.721|Deming|NM
KDMO|38.7074|-93.1759|Sedalia|MO
KDMW|39.6083|-77.0077|Westminster|MD
KDNL|33.4665|-82.0394|Augusta|GA
KDNN|34.7229|-84.8702|Dalton|GA
KDNS|41.9864|-95.3807|Denison|IA
KDNV|40.1991|-87.5966|Danville|IL
KDOV|39.1295|-75.466|Dover|DE
KDPA|41.9078|-88.2486|Chicago/West Chicago|IL
KDPG|40.1974|-112.9351|Dugway Proving Ground|UT
KDPL|35.0001|-77.9817|Kenansville|NC
KDQH|31.4767|-82.8605|Douglas|GA
KDRA|36.6194|-116.033|Mercury|NV
KDRI|30.8317|-93.3399|DeRidder|LA
KDRM|46.0093|-83.7439|Drummond Island|MI
KDRO|37.1515|-107.754|Durango|CO
KDRP|35.1201|-90.8265|Colt|AR
KDRT|29.3742|-100.927|Del Rio|TX
KDSM|41.534|-93.6567|Des Moines|IA
KDSV|42.5705|-77.7133|Dansville|NY
KDTA|39.3806|-112.508|Delta|UT
KDTL|46.8243|-95.8833|Detroit Lakes|MN
KDTN|32.5402|-93.745|Shreveport|LA
KDTO|33.2018|-97.198|Denton|TX
KDTS|30.4001|-86.4715|Destin|FL
KDTW|42.2138|-83.3538|Detroit|MI
KDUA|33.9397|-96.3951|Durant|OK
KDUC|34.4709|-97.9599|Duncan|OK
KDUG|31.4641|-109.6046|Douglas Bisbee|AZ
KDUH|41.7359|-83.6554|Lambertville|MI
KDUJ|41.1783|-78.8987|Dubois|PA
KDUX|35.8579|-102.013|Dumas|TX
KDVK|37.5794|-84.7743|Danville|KY
KDVL|48.1155|-98.9088|Devils Lake|ND
KDVN|41.6104|-90.5897|Davenport|IA
KDVO|38.1436|-122.556|Novato|CA
KDVP|43.9868|-95.7826|Slayton|MN
KDVT|33.6883|-112.083|Phoenix|AZ
KDWA|38.5791|-121.857|Davis/Woodland/Winters|CA
KDWH|30.0618|-95.5528|Houston|TX
KDWU|38.5545|-82.738|Worthington|KY
KDXE|36.7775|-89.9412|Dexter|MO
KDXR|41.3717|-73.4822|Danbury|CT
KDXX|44.9862|-96.1777|Louisburg|MN
KDYA|32.4638|-87.9541|Demopolis|AL
KDYB|33.0634|-80.2793|Summerville|SC
KDYL|40.333|-75.1223|Doylestown|PA
KDYR|35.9992|-89.4049|Dyersburg|TN
KDYS|32.4208|-99.8546|Abilene|TX
KDYT|46.7219|-92.0434|Duluth|MN
KDZB|30.5271|-98.3587|Horseshoe Bay|TX
KDZJ|34.8551|-83.9969|Blairsville|GA
KE01|31.5825|-102.909|Monahans|TX
KE05|32.6611|-107.198|Hatch|NM
KE06|32.9525|-103.4099|Lovington|NM
KE11|32.3311|-102.53|Andrews|TX
KE13|31.4151|-102.363|Crane|TX
KE14|36.0261|-106.0454|Espanola|NM
KE16|37.0816|-121.597|San Martin|CA
KE19|36.2337|-101.432|Gruver|TX
KE24|33.8102|-109.9866|Whiteriver|AZ
KE25|33.9702|-112.796|Wickenburg|AZ
KE26|32.1311|-103.155|Jal|NM
KE30|31.6745|-99.977|Ballinger|TX
KE34|34.9115|-100.869|Clarendon|TX
KE35|31.5172|-106.147|Fabens|TX
KE38|30.3842|-103.684|Alpine|TX
KE41|31.1989|-101.473|Big Lake|TX
KE42|36.221|-101.195|Spearman|TX
KE45|37.8615|-120.1782|Groveland|CA
KE48|31.1251|-102.225|McCamey|TX
KE51|34.5928|-113.172|Bagdad|AZ
KE52|35.2307|-102.3987|Vega|TX
KE57|32.9765|-102.8423|Denver City|TX
KE60|32.807|-111.587|Eloy|AZ
KE63|32.9581|-112.678|Gila Bend|AZ
KE67|33.0476|-110.909|Kearny|AZ
KE77|32.6365|-110.647|San Manuel|AZ
KE78|31.9329|-111.894|Sells|AZ
KE80|34.6452|-106.834|Belen|NM
KE89|35.3679|-104.1809|Conchas Dam|NM
KE91|36.1109|-109.575|Chinle|AZ
KE95|31.9994|-110.3582|Benson|AZ
KE98|34.7598|-106.745|Los Lunas|NM
KEAG|42.7098|-93.9161|Eagle Grove|IA
KEAN|42.0555|-104.929|Wheatland|WY
KEAR|40.727|-99.0068|Kearney|NE
KEAT|47.3989|-120.207|Wenatchee|WA
KEAU|44.8658|-91.4843|Eau Claire|WI
KEBD|37.6818|-82.1221|Varney|WV
KEBG|26.4417|-98.1222|Edinburg|TX
KEBS|42.4368|-93.8699|Webster City|IA
KECG|36.2606|-76.1746|Elizabeth City|NC
KECP|30.3571|-85.7954|Panama City Beach|FL
KECS|43.8838|-104.3142|Newcastle|WY
KECU|29.9469|-100.174|Rocksprings|TX
KEDC|30.398|-97.5669|Pflugerville|TX
KEDE|36.0277|-76.5671|Edenton|NC
KEDG|39.3916|-76.2911|Edgewood Arsenal|MD
KEDJ|40.3723|-83.819|Bellefontaine|OH
KEDN|31.2997|-85.8999|Enterprise|AL
KEDU|38.5315|-121.7865|Davis|CA
KEDW|34.9108|-117.8864|Edwards|CA
KEE0|32.6862|-111.5471|Eloy|AZ
KEED|34.7663|-114.623|Needles|CA
KEEN|42.8984|-72.2708|Keene|NH
KEEO|40.0488|-107.886|Meeker|CO
KEET|33.177|-86.7828|Alabaster|AL
KEFC|44.7342|-103.862|Belle Fourche|SD
KEFD|29.6073|-95.1588|Houston|TX
KEFK|44.8888|-72.2292|Newport|VT
KEFT|42.6149|-89.5904|Monroe|WI
KEFW|42.0099|-94.3417|Jefferson|IA
KEGE|39.6426|-106.918|Eagle|CO
KEGI|30.6504|-86.5229|Crestview|FL
KEGQ|43.102|-94.7047|Emmetsburg|IA
KEGT|37.3236|-97.3883|Wellington|KS
KEGV|45.9323|-89.2683|Eagle River|WI
KEHA|37.0007|-101.88|Elkhart|KS
KEHO|35.2556|-81.601|Shelby|NC
KEHR|37.8078|-87.6857|Henderson|KY
KEIK|40.0102|-105.048|Erie|CO
KEIW|36.5353|-89.5997|New Madrid|MO
KEKA|40.8034|-124.113|Eureka|CA
KEKM|41.7194|-86.0032|Elkhart|IN
KEKN|38.8898|-79.8577|Elkins|WV
KEKO|40.8249|-115.792|Elko|NV
KEKQ|36.8553|-84.8561|Monticello|KY
KEKS|45.2718|-111.649|Ennis|MT
KEKX|37.686|-85.925|Elizabethtown|KY
KEKY|33.3129|-86.9259|Bessemer|AL
KELA|29.6006|-96.3219|Eagle Lake|TX
KELD|33.221|-92.8133|El Dorado|AR
KELK|35.4308|-99.3943|Elk City|OK
KELM|42.1599|-76.8916|Elmira/Corning|NY
KELN|47.033|-120.531|Ellensburg|WA
KELO|47.8245|-91.8307|Ely|MN
KELP|31.8099|-106.3756|El Paso|TX
KELY|39.2997|-114.842|Ely|NV
KELZ|42.1095|-77.99|Wellsville|NY
KEMM|41.8241|-110.557|Kemmerer|WY
KEMP|38.3321|-96.1912|Emporia|KS
KEMT|34.0861|-118.035|El Monte|CA
KEMV|36.6869|-77.4828|Emporia|VA
KEND|36.3392|-97.9165|Enid|OK
KENL|38.5151|-89.0911|Centralia|IL
KENV|40.7187|-114.031|Wendover|UT
KENW|42.5957|-87.9278|Kenosha|WI
KEOE|34.3093|-81.6397|Newberry|SC
KEOK|40.4599|-91.4285|Keokuk|IA
KEOP|39.1669|-82.9282|Waverly|OH
KEOS|36.8108|-94.3917|Neosho|MO
KEPH|47.3076|-119.516|Ephrata|WA
KEPM|44.9101|-67.0127|Eastport|ME
KEQA|37.7741|-96.8176|El Dorado|KS
KEQY|35.0188|-80.6202|Monroe|NC
KERI|42.0831|-80.1739|Erie|PA
KERR|44.7925|-71.1642|Errol|NH
KERV|29.9767|-99.0857|Kerrville|TX
KERY|46.3112|-85.4573|Newberry|MI
KESC|45.7232|-87.0886|Escanaba|MI
KESF|31.3943|-92.2941|Alexandria|LA
KESN|38.8042|-76.069|Easton|MD
KEST|43.4074|-94.7464|Estherville|IA
KESW|47.2542|-121.186|Easton|WA
KETB|43.4223|-88.129|West Bend|WI
KETC|35.9371|-77.5466|Tarboro|NC
KETH|45.7821|-96.5436|Lake Valley Township|MN
KETN|32.4135|-98.8098|Eastland|TX
KEUF|31.9513|-85.1289|Eufaula|AL
KEUG|44.1246|-123.212|Eugene|OR
KEUL|43.6419|-116.636|Caldwell|ID
KEVB|29.0557|-80.9489|New Smyrna Beach|FL
KEVM|47.4246|-92.4992|Eveleth|MN
KEVU|40.3525|-94.915|Maryville|MO
KEVV|38.037|-87.5324|Evansville|IN
KEVW|41.2748|-111.035|Evanston|WY
KEVY|39.5204|-75.7204|Middletown|DE
KEWB|41.6761|-70.9569|New Bedford|MA
KEWK|38.0582|-97.2745|Newton|KS
KEWN|35.073|-77.0429|New Bern|NC
KEWR|40.6894|-74.1705|Newark|NJ
KEXX|35.7811|-80.3038|Lexington|NC
KEYE|39.8307|-86.2944|Indianapolis|IN
KEYF|34.6018|-78.5793|Elizabethtown|NC
KEYW|24.5561|-81.7596|Key West|FL
KEZF|38.2668|-77.4492|Fredericksburg|VA
KEZI|41.2052|-89.9639|Kewanee|IL
KEZM|32.2142|-83.128|Eastman|GA
KEZS|44.7875|-88.5605|Shawano|WI
KEZZ|39.7276|-94.2764|Shoal Township|MO
KF00|33.6128|-96.1793|Bonham|TX
KF01|34.2771|-99.7593|Quanah|TX
KF05|34.2257|-99.2838|Vernon|TX
KF06|34.844|-100.1928|Wellington|TX
KF08|35.298|-95.6274|Eufaula|OK
KF10|35.4069|-96.0158|Henryetta|OK
KF14|33.9477|-98.6167|Wichita Falls|TX
KF17|31.8316|-94.1564|Center|TX
KF21|34.7396|-100.53|Memphis|TX
KF22|36.3856|-97.2772|Perry|OK
KF24|32.646|-93.2981|Minden|LA
KF29|35.4881|-97.8236|Oklahoma City|OK
KF30|34.5245|-96.9897|Sulphur|OK
KF31|33.991|-96.6428|Kingston|OK
KF32|34.2493|-97.4739|Healdton|OK
KF34|36.86|-120.464|Firebaugh|CA
KF35|32.9232|-98.4364|Graford|TX
KF36|35.2976|-98.9674|Cordell|OK
KF37|33.6489|-105.896|Carrizozo|NM
KF41|32.3297|-96.6639|Ennis|TX
KF43|33.1912|-92.6632|El Dorado|AR
KF44|32.1638|-95.8284|Athens|TX
KF45|26.8444|-80.2213|West Palm Beach|FL
KF46|32.9306|-96.4355|Rockwall|TX
KF47|29.646|-84.9166|Apalachicola|FL
KF49|33.4848|-101.661|Slaton|TX
KF51|32.9388|-95.2789|Winnsboro|TX
KF53|33.2154|-95.2374|Mount Vernon|TX
KF56|32.9091|-99.736|Stamford|TX
KF62|40.5471|-123.182|Hayfork|CA
KF68|35.0522|-98.2643|Anadarko|OK
KF69|33.0235|-96.8369|Dallas|TX
KF70|33.5742|-117.128|Murrieta|CA
KF72|38.3049|-121.43|Franklin|CA
KF75|33.4369|-99.8158|Knox City|TX
KF82|33.4856|-101.813|Lubbock|TX
KF84|35.2891|-95.0939|Stigler|OK
KF85|33.7293|-102.734|Morton|TX
KF86|32.1222|-92.0545|Columbia|LA
KF87|32.725|-92.3372|Farmerville|LA
KF88|32.202|-92.7329|Jonesboro|LA
KF89|32.153|-91.6986|Winnsboro|LA
KF97|32.9546|-102.541|Seagraves|TX
KF98|33.2173|-102.83|Plains|TX
KF99|35.0859|-96.4167|Holdenville|OK
KFAF|37.1325|-76.6088|Newport News (Fort Eustis)|VA
KFAM|37.7611|-90.4286|Farmington|MO
KFAR|46.9207|-96.8158|Fargo|ND
KFAT|36.7758|-119.718|Fresno|CA
KFAY|34.9912|-78.8803|Fayetteville|NC
KFBG|35.1318|-78.9367|Fort Bragg|NC
KFBL|44.3284|-93.3125|Faribault|MN
KFBR|41.3919|-110.407|Fort Bridger|WY
KFBY|40.183|-97.1693|Fairbury|NE
KFCH|36.7324|-119.82|Fresno|CA
KFCI|37.4065|-77.525|North Chesterfield|VA
KFCM|44.8272|-93.4571|Minneapolis|MN
KFCS|38.6784|-104.757|Fort Carson|CO
KFCY|34.942|-90.775|Forrest City|AR
KFDK|39.4176|-77.3743|Frederick|MD
KFDR|34.352|-98.9839|Frederick|OK
KFDW|34.3155|-81.1088|Winnsboro|SC
KFDY|41.0135|-83.6687|Findlay|OH
KFEP|42.2462|-89.582|Freeport|IL
KFET|41.4502|-96.5229|Fremont|NE
KFFA|36.0182|-75.6713|Kill Devil Hills|NC
KFFC|33.3573|-84.5718|Peachtree City|GA
KFFL|41.053|-91.9801|Fairfield|IA
KFFM|46.2844|-96.1567|Fergus Falls|MN
KFFO|39.8261|-84.0483|Dayton|OH
KFFT|38.1817|-84.9068|Frankfort|KY
KFFX|43.4369|-85.9966|Fremont|MI
KFFZ|33.4608|-111.728|Mesa|AZ
KFGU|35.0443|-85.0201|Apison|TN
KFGX|38.5418|-83.7434|Flemingsburg|KY
KFHR|48.5237|-123.0246|Friday Harbor|WA
KFHU|31.5874|-110.3482|Fort Huachuca / Sierra Vista|AZ
KFIG|41.049|-78.415|Clearfield|PA
KFIT|42.5541|-71.759|Fitchburg|MA
KFKA|43.6768|-92.1797|Preston|MN
KFKL|41.3779|-79.8604|Franklin|PA
KFKN|36.6981|-76.9038|Franklin|VA
KFKR|40.2734|-86.5622|Frankfort|IN
KFKS|44.6252|-86.2008|Frankfort|MI
KFLD|43.7712|-88.4884|Fond du Lac|WI
KFLG|35.1398|-111.6698|Flagstaff|AZ
KFLL|26.0726|-80.1527|Fort Lauderdale|FL
KFLO|34.1854|-79.7239|Florence|SC
KFLP|36.2909|-92.5903|Flippin|AR
KFLR|25.5562|-80.356|Coral Gable|FL
KFLV|39.3683|-94.9147|Fort Leavenworth|KS
KFLX|39.4991|-118.749|Fallon|NV
KFME|39.0854|-76.7594|Fort Meade(Odenton)|MD
KFMH|41.6579|-70.5216|Falmouth|MA
KFMM|40.3358|-103.8044|Fort Morgan|CO
KFMN|36.7412|-108.23|Farmington|NM
KFMY|26.5866|-81.8633|Fort Myers|FL
KFMZ|40.5861|-97.5731|Fairmont|NE
KFNB|40.0788|-95.592|Falls City|NE
KFNL|40.4497|-105.0113|Loveland|CO
KFNT|42.9693|-83.7434|Flint|MI
KFOA|38.6649|-88.453|Flora|IL
KFOD|42.5526|-94.1912|Fort Dodge|IA
KFOE|38.9509|-95.6636|Topeka|KS
KFOK|40.8437|-72.6318|Westhampton Beach|NY
KFOM|38.9583|-112.363|Fillmore|UT
KFOT|40.5539|-124.133|Fortuna|CA
KFOZ|47.7839|-93.651|Bigfork|MN
KFPK|42.5745|-84.8114|Charlotte|MI
KFPR|27.4951|-80.3683|Fort Pierce|FL
KFQD|35.4282|-81.9351|Rutherfordton|NC
KFRG|40.7286|-73.4143|East Farmingdale|NY
KFRH|38.5062|-86.6369|French Lick|IN
KFRI|39.053|-96.7642|Fort Riley (Junction City)|KS
KFRM|43.6439|-94.4156|Fairmont|MN
KFRR|38.9175|-78.2535|Front Royal|VA
KFSD|43.5855|-96.7412|Sioux Falls|SD
KFSE|47.5928|-95.7735|Fosston|MN
KFSI|34.6498|-98.4022|Fort Sill|OK
KFSK|37.7984|-94.7694|Fort Scott|KS
KFSM|35.3366|-94.3674|Fort Smith|AR
KFSO|44.9403|-73.0975|Swanton|VT
KFST|30.9157|-102.916|Fort Stockton|TX
KFSU|34.4834|-104.217|Fort Sumner|NM
KFSW|40.6591|-91.3278|Fort Madison|IA
KFTG|39.7842|-104.5376|Denver|CO
KFTK|37.9071|-85.9721|Fort Knox|KY
KFTT|38.8381|-92.0026|Fulton|MO
KFTW|32.8199|-97.3608|Fort Worth|TX
KFTY|33.7791|-84.5214|Atlanta|GA
KFUL|33.872|-117.98|Fullerton|CA
KFVE|47.2855|-68.3128|Frenchville|ME
KFVX|37.3575|-78.4378|Farmville|VA
KFWA|40.9789|-85.1945|Fort Wayne|IN
KFWC|38.3786|-88.4127|Fairfield|IL
KFWN|41.2002|-74.623|Sussex|NJ
KFWQ|40.2097|-79.8314|Monongahela|PA
KFWS|32.5652|-97.3081|Fort Worth|TX
KFXE|26.1973|-80.1707|Fort Lauderdale|FL
KFXY|43.2347|-93.6241|Forest City|IA
KFYE|35.2077|-89.3945|Somerville|TN
KFYJ|37.5213|-76.7628|Shacklefords|VA
KFYM|35.0597|-86.564|Fayetteville|TN
KFYV|36.0051|-94.1701|Fayetteville|AR
KFZG|31.6837|-83.2705|Fitzgerald|GA
KFZI|41.1908|-83.3945|Fostoria|OH
KFZY|43.3508|-76.3881|Fulton|NY
KGAB|38.9232|-117.9572|Gabbs|NV
KGAD|33.9726|-86.089|Gadsden|AL
KGAF|48.4047|-97.3709|Grafton|ND
KGAG|36.2955|-99.7764|Gage|OK
KGAI|39.1683|-77.166|Gaithersburg|MD
KGAO|29.4411|-90.2611|Galliano|LA
KGAS|38.8341|-82.1634|Gallipolis|OH
KGAX|32.8847|-112.8165|Gila Bend|AZ
KGBD|38.3443|-98.8592|Great Bend|KS
KGBG|40.938|-90.4311|Galesburg|IL
KGBN|32.8875|-112.72|Gila Bend|AZ
KGBR|42.1842|-73.4032|Great Barrington|MA
KGCC|44.3489|-105.539|Gillette|WY
KGCD|44.4042|-118.963|John Day|OR
KGCK|37.9275|-100.724|Garden City|KS
KGCM|36.2927|-95.4796|Claremore|OK
KGCN|35.9524|-112.147|Grand Canyon - Tusayan|AZ
KGCT|41.6878|-94.4352|Guthrie Center|IA
KGCY|36.1956|-82.8115|Greeneville|TN
KGDB|44.7533|-95.556|Granite Falls|MN
KGDJ|32.4422|-97.8244|Granbury|TX
KGDM|42.55|-72.0161|Gardner|MA
KGDV|47.1377|-104.8069|Glendive|MT
KGDW|43.9706|-84.475|Gladwin|MI
KGED|38.6892|-75.3589|Georgetown|DE
KGEG|47.6199|-117.534|Spokane|WA
KGEO|38.8819|-83.8827|Georgetown|OH
KGEU|33.5269|-112.295|Glendale|AZ
KGEV|36.4324|-81.4185|Jefferson|NC
KGEY|44.5168|-108.083|Greybull|WY
KGEZ|39.586|-85.804|Shelbyville|IN
KGFD|39.7907|-85.735|Greenfield|IN
KGFK|47.9493|-97.1761|Grand Forks|ND
KGFL|43.3412|-73.6103|Glens Falls|NY
KGFZ|41.327|-94.4457|Greenfield|IA
KGGE|33.3117|-79.3196|Georgetown|SC
KGGF|40.8695|-101.733|Grant|NE
KGGG|32.384|-94.7115|Longview|TX
KGGI|41.7099|-92.7361|Grinnell|IA
KGGP|40.7112|-86.3749|Logansport|IN
KGGW|48.2125|-106.615|Glasgow|MT
KGHG|42.0977|-70.6728|Marshfield|MA
KGHM|35.8374|-87.4454|Centerville|TN
KGHW|45.6439|-95.3204|Glenwood|MN
KGIC|45.9426|-116.123|Grangeville|ID
KGIF|28.0629|-81.7533|Winter Haven|FL
KGJT|39.1267|-108.5294|Grand Junction|CO
KGKJ|41.6265|-80.2147|Meadville|PA
KGKT|35.8578|-83.5287|Sevierville|TN
KGKY|32.6639|-97.0943|Arlington|TX
KGLD|39.3707|-101.6998|Goodland|KS
KGLE|33.6514|-97.197|Gainesville|TX
KGLH|33.4829|-90.9856|Greenville|MS
KGLR|45.0135|-84.7036|Gaylord|MI
KGLS|29.2653|-94.8604|Galveston|TX
KGLW|37.0318|-85.9537|Glasgow|KY
KGLY|38.3566|-93.6842|Clinton|MO
KGMJ|36.6068|-94.7386|Grove|OK
KGMU|34.8479|-82.3502|Greenville|SC
KGNB|40.0897|-105.917|Granby|CO
KGNC|32.6768|-102.6499|Seminole|TX
KGNF|33.8325|-89.7982|Grenada|MS
KGNG|42.9172|-114.765|Gooding|ID
KGNT|35.1673|-107.902|Grants|NM
KGNV|29.6901|-82.2718|Gainesville|FL
KGOK|35.8498|-97.4156|Guthrie|OK
KGON|41.3301|-72.0451|Groton|CT
KGOO|39.224|-121.003|Grass Valley|CA
KGOP|31.4213|-97.797|Gatesville|TX
KGOV|44.6803|-84.7289|Grayling|MI
KGPH|39.3325|-94.3096|Excelsior Springs|MO
KGPI|48.3105|-114.256|Kalispell|MT
KGPM|32.6988|-97.0469|Grand Prairie|TX
KGPT|30.4056|-89.0698|Gulfport|MS
KGPZ|47.2111|-93.5098|Grand Rapids|MN
KGQQ|40.7534|-82.7238|Galion|OH
KGRB|44.4835|-88.1308|Green Bay|WI
KGRD|34.2487|-82.1591|Greenwood|SC
KGRE|38.8368|-89.3765|Greenville|IL
KGRF|47.0792|-122.581|Joint Base Lewis McChord|WA
KGRI|40.9675|-98.3096|Grand Island|NE
KGRK|31.0672|-97.8289|Fort Cavazos|TX
KGRN|42.806|-102.175|Gordon|NE
KGRR|42.8808|-85.5228|Grand Rapids|MI
KGSB|35.3394|-77.9606|Goldsboro|NC
KGSH|41.5264|-85.7929|Goshen|IN
KGSO|36.0994|-79.9373|Greensboro|NC
KGSP|34.8957|-82.2189|Greenville/Greer/Spartanburg|SC
KGTB|44.0556|-75.7195|Fort Drum|NY
KGTE|40.9263|-100.148|Gothenburg|NE
KGTF|47.482|-111.371|Great Falls|MT
KGTG|45.7981|-92.6644|Grantsburg|WI
KGTR|33.4503|-88.5914|Columbus/W Point/Starkville|MS
KGTU|30.6775|-97.6785|Georgetown|TX
KGUC|38.5347|-106.9346|Gunnison|CO
KGUP|35.5117|-108.7882|Gallup|NM
KGUS|40.6481|-86.1521|Peru|IN
KGUY|36.6851|-101.508|Guymon|OK
KGVE|38.156|-78.1658|Gordonsville|VA
KGVL|34.2726|-83.8302|Gainesville|GA
KGVQ|43.0317|-78.1676|Batavia|NY
KGVT|33.0678|-96.0653|Greenville|TX
KGWB|41.3072|-85.0644|Auburn|IN
KGWO|33.495|-90.0882|Greenwood|MS
KGWR|46.2187|-97.6432|Gwinner|ND
KGWS|39.5083|-107.311|Glenwood Springs|CO
KGWW|35.4606|-77.9649|Pikeville|NC
KGXY|40.4374|-104.633|Greeley|CO
KGYB|30.1693|-96.98|Giddings|TX
KGYH|34.7583|-82.3764|Greenville|SC
KGYI|33.7141|-96.6737|Denison|TX
KGYL|44.756|-94.0812|Glencoe|MN
KGYR|33.4225|-112.376|Goodyear|AZ
KGYY|41.6171|-87.4132|Gary|IN
KGZH|31.4158|-87.044|Evergreen|AL
KGZN|32.3673|-99.0236|Cisco|TX
KGZS|35.1537|-87.0568|Pulaski|TN
KH04|36.633|-95.1514|Vinita|OK
KH05|34.92|-95.394|Wilburton|OK
KH17|37.6542|-93.0877|Buffalo|MO
KH19|39.3699|-91.2193|Bowling Green|MO
KH21|37.974|-92.6913|Camdenton|MO
KH34|36.0782|-93.7548|Huntsville|AR
KH35|35.4707|-93.4272|Clarksville|AR
KH37|40.1388|-120.179|Herlong|CA
KH58|37.1847|-92.7376|Seymour|MO
KH68|35.9626|-95.3419|Wagoner|OK
KH70|36.3456|-102.049|Stratford|TX
KH71|36.2254|-95.3301|Pryor|OK
KH76|36.6717|-96.4056|Pawhuska|OK
KH79|38.3606|-92.5716|Eldon|MO
KH88|37.6058|-90.2873|Fredericktown|MO
KH92|36.4427|-96.3872|Hominy|OK
KH96|38.0067|-88.9344|Benton|IL
KHAB|34.1176|-87.9982|Hamilton|AL
KHAE|39.7245|-91.4437|Hannibal|MO
KHAF|37.5134|-122.501|Half Moon Bay|CA
KHAI|41.9598|-85.5934|Three Rivers|MI
KHAO|39.3638|-84.522|Hamilton|OH
KHBC|48.7684|-101.537|Mohall|ND
KHBG|31.2648|-89.2528|Hattiesburg|MS
KHBI|35.6545|-79.8947|Asheboro|NC
KHBR|34.9913|-99.0513|Hobart|OK
KHBV|27.3496|-98.737|Hebbronville|TX
KHBZ|35.5117|-92.013|Heber Springs|AR
KHCD|44.8599|-94.3825|Hutchinson|MN
KHCO|48.7527|-96.943|Hallock|MN
KHCR|40.4818|-111.429|Heber|UT
KHDC|30.5216|-90.4184|Hammond|LA
KHDE|40.4521|-99.3365|Holdrege|NE
KHDN|40.4812|-107.218|Hayden|CO
KHDO|29.3595|-99.1767|Hondo|TX
KHEE|34.5766|-90.6762|West Helena|AR
KHEF|38.723|-77.5154|Manassas|VA
KHEG|30.2778|-81.8059|Jacksonville|FL
KHEI|46.0149|-102.656|Hettinger|ND
KHEQ|40.5694|-102.273|Holyoke|CO
KHEZ|31.6148|-91.2973|Natchez|MS
KHFD|41.7367|-72.6494|Hartford|CT
KHFF|35.0361|-79.4986|Hoffman|NC
KHFJ|36.9062|-94.0128|Pierce City|MO
KHFY|39.6284|-86.0879|Indianapolis|IN
KHGR|39.7088|-77.728|Hagerstown|MD
KHHF|35.8953|-100.404|Canadian|TX
KHHG|40.8529|-85.4571|Huntington|IN
KHHR|33.9228|-118.335|Hawthorne|CA
KHHW|34.0348|-95.5419|Hugo|OK
KHIB|47.3848|-92.8369|Hibbing|MN
KHIE|44.3675|-71.5458|Whitefield|NH
KHIF|41.124|-111.9731|Ogden|UT
KHIG|39.0729|-93.6775|Higginsville|MO
KHII|34.5705|-114.3577|Lake Havasu City|AZ
KHIO|45.5404|-122.95|Portland|OR
KHJH|40.1519|-97.584|Hebron|NE
KHJO|36.3167|-119.628|Hanford|CA
KHKA|35.9404|-89.8308|Blytheville|AR
KHKS|32.3345|-90.2222|Jackson|MS
KHKY|35.7411|-81.3895|Hickory|NC
KHLB|39.3429|-85.2584|Batesville|IN
KHLC|39.3788|-99.8315|Hill City|KS
KHLG|40.175|-80.6463|Wheeling|WV
KHLN|46.6068|-111.983|Helena|MT
KHLX|36.7661|-80.8236|Hillsville|VA
KHMN|32.8525|-106.107|Alamogordo|NM
KHMT|33.734|-117.023|Hemet|CA
KHMZ|40.086|-78.5134|Bedford|PA
KHNB|38.249|-86.9537|Huntingburg|IN
KHND|35.9728|-115.134|Las Vegas|NV
KHNR|41.5844|-95.3396|Harlan|IA
KHNZ|36.3616|-78.5292|Oxford|NC
KHOB|32.6875|-103.217|Hobbs|NM
KHOC|39.1888|-83.5388|Hillsboro|OH
KHOE|31.0559|-82.7741|Homerville|GA
KHON|44.3852|-98.2285|Huron|SD
KHOP|36.6742|-87.4897|Fort Campbell|KY
KHOT|34.4788|-93.0963|Hot Springs|AR
KHPT|42.7237|-93.2263|Hampton|IA
KHPY|29.7861|-94.9527|Baytown|TX
KHQG|37.1631|-101.371|Hugoton|KS
KHQM|46.9712|-123.937|Hoquiam|WA
KHQU|33.5297|-82.5165|Thomson|GA
KHQZ|32.747|-96.5304|Mesquite|TX
KHRI|45.8282|-119.259|Hermiston|OR
KHRJ|35.3794|-78.733|Erwin|NC
KHRL|26.2285|-97.6544|Harlingen|TX
KHRO|36.2615|-93.1547|Harrison|AR
KHRT|30.4278|-86.6893|Mary Esther|FL
KHRU|38.6947|-96.808|Herington|KS
KHRX|34.8578|-102.326|Hereford|TX
KHSA|30.3678|-89.4546|Bay St Louis|MS
KHSB|37.8113|-88.5503|Harrisburg|IL
KHSD|35.6018|-97.7062|Oklahoma City|OK
KHSE|35.2328|-75.6178|Hatteras|NC
KHSG|43.7136|-108.3897|Thermopolis|WY
KHSI|40.6053|-98.4279|Hastings|NE
KHSP|37.9514|-79.8339|Hot Springs|VA
KHSR|43.3666|-103.3905|Hot Springs|SD
KHST|25.4886|-80.3836|Homestead|FL
KHSV|34.6362|-86.7744|Huntsville|AL
KHTH|38.5444|-118.634|Hawthorne|NV
KHTL|44.3598|-84.6711|Houghton Lake|MI
KHTO|40.96|-72.2511|East Hampton|NY
KHTS|38.3667|-82.558|Huntington|WV
KHTW|38.4191|-82.4949|South Point|OH
KHUA|34.6787|-86.6848|Redstone Arsnl Huntsville|AL
KHUF|39.4515|-87.3076|Terre Haute|IN
KHUL|46.1231|-67.7921|Houlton|ME
KHUM|29.5665|-90.6604|Houma|LA
KHUT|38.0655|-97.8606|Hutchinson|KS
KHVC|36.857|-87.4551|Hopkinsville|KY
KHVE|38.418|-110.704|Hanksville|UT
KHVN|41.2629|-72.8877|New Haven|CT
KHVR|48.5414|-109.7629|Havre|MT
KHVS|34.4031|-80.1192|Hartsville|SC
KHWD|37.6592|-122.122|Hayward|CA
KHWO|26.0012|-80.2407|Hollywood|FL
KHWQ|46.4486|-109.853|Harlowton|MT
KHWV|40.8219|-72.8694|Shirley|NY
KHWY|38.5863|-77.7106|Midland|VA
KHXD|32.2244|-80.6975|Hilton Head Island|SC
KHXF|43.3493|-88.3911|Hartford|WI
KHYA|41.6693|-70.2804|Hyannis|MA
KHYI|29.8927|-97.863|San Marcos|TX
KHYR|46.0252|-91.4443|Hayward|WI
KHYS|38.8445|-99.2731|Hays|KS
KHYW|33.8285|-79.1222|Conway|SC
KHYX|43.4334|-83.8623|Saginaw|MI
KHZD|36.0893|-88.4633|Huntingdon|TN
KHZE|47.2899|-101.581|Hazen|ND
KHZL|40.9868|-75.9949|Hazleton|PA
KHZR|30.7183|-91.4787|New Roads|LA
KHZX|46.6188|-93.3098|Mc Gregor|MN
KHZY|41.778|-80.6955|Ashtabula|OH
KI06|34.5668|-101.781|Tulia|TX
KI10|39.801|-81.5363|Caldwell|OH
KI12|40.2414|-84.1509|Sidney|OH
KI16|37.6004|-81.5593|Pineville|WV
KI17|40.1647|-84.3084|Piqua|OH
KI18|38.9298|-81.8195|Ravenswood|WV
KI22|40.1689|-84.9257|Winchester|IN
KI23|39.5704|-83.4205|Washington Court House|OH
KI34|39.3269|-85.5225|Greensburg|IN
KI35|36.8594|-83.3585|Baxter|KY
KI39|37.6308|-84.3323|Richmond|KY
KI40|40.3092|-81.8534|Coshocton|OH
KI43|38.9814|-82.5779|Jackson|OH
KI50|37.8499|-83.8457|Stanton|KY
KI53|37.3087|-85.0593|Liberty|KY
KI54|40.0201|-83.8285|Tremont City|OH
KI58|34.9344|-104.643|Santa Rosa|NM
KI63|39.9875|-90.8042|Mount Sterling|IL
KI64|41.2934|-82.3725|Wakeman|OH
KI66|39.5033|-83.8628|Wilmington|OH
KI67|39.2589|-84.7743|Harrison|OH
KI68|39.4622|-84.2518|Lebanon|OH
KI69|39.0784|-84.2102|Batavia|OH
KI71|39.6542|-81.8032|McConnelsville|OH
KI73|39.6822|-84.239|Dayton|OH
KI74|40.1326|-83.7534|Urbana|OH
KI75|41.0522|-93.6897|Osceola|IA
KI76|40.7863|-86.1464|Peru|IN
KI86|39.6916|-82.1978|New Lexington|OH
KI93|37.7848|-86.4413|Hardinsburg|KY
KI95|40.6107|-83.6436|Kenton|OH
KIAB|37.6219|-97.2682|Wichita|KS
KIAD|38.9445|-77.4558|Dulles|VA
KIAG|43.1073|-78.9462|Niagara Falls|NY
KIAH|29.9844|-95.3414|Houston|TX
KIBM|41.1881|-103.677|Kimball|NE
KICL|40.7218|-95.0264|Clarinda|IA
KICR|43.3902|-99.8421|Winner|SD
KICT|37.6503|-97.4286|Wichita|KS
KIDA|43.5146|-112.071|Idaho Falls|ID
KIDG|42.3326|-95.4449|Ida Grove|IA
KIDI|40.6311|-79.1019|Indiana|PA
KIDL|33.4857|-90.6789|Indianola|MS
KIDP|37.1584|-95.7784|Independence|KS
KIEN|43.0209|-102.5061|Pine Ridge|SD
KIER|31.7357|-93.0991|Natchitoches|LA
KIFA|42.4708|-93.27|Iowa Falls|IA
KIFP|35.1547|-114.5593|Bullhead City|AZ
KIGM|35.2595|-113.938|Kingman|AZ
KIGQ|41.5349|-87.5295|Chicago|IL
KIIB|42.4568|-91.9477|Independence|IA
KIIY|33.7794|-82.8158|Washington|GA
KIJD|41.744|-72.1803|North Windham|CT
KIJX|39.7746|-90.2383|Jacksonville|IL
KIKG|27.5509|-98.0309|Kingsville|TX
KIKK|41.0714|-87.8463|Kankakee|IL
KIKV|41.6914|-93.5664|Ankeny|IA
KILE|31.0858|-97.6865|Killeen|TX
KILG|39.6787|-75.6065|Wilmington|DE
KILM|34.2723|-77.9051|Wilmington|NC
KILN|39.4279|-83.7921|Wilmington|OH
KIML|40.5093|-101.621|Imperial|NE
KIMM|26.4279|-81.4035|Immokalee|FL
KIMS|38.7589|-85.4655|Madison|IN
KIMT|45.8191|-88.1146|Kingsford|MI
KIND|39.7173|-86.2944|Indianapolis|IN
KINF|28.8036|-82.3183|Inverness|FL
KINJ|32.0835|-97.0972|Hillsboro|TX
KINK|31.7796|-103.201|Wink|TX
KINL|48.5662|-93.4031|International Falls|MN
KINS|36.5872|-115.673|Indian Springs|NV
KINT|36.1337|-80.222|Winston Salem|NC
KINW|35.0219|-110.723|Winslow|AZ
KIOB|38.0581|-83.9796|Mount Sterling|KY
KIOW|41.6392|-91.5465|Iowa City|IA
KIPJ|35.4833|-81.1613|Lincolnton|NC
KIPL|32.8354|-115.574|Imperial|CA
KIPT|41.2421|-76.9224|Williamsport|PA
KIRK|40.0935|-92.5449|Kirksville|MO
KIRS|41.8133|-85.439|Sturgis|MI
KISB|43.3695|-95.7598|Sibley|IA
KISM|28.2898|-81.4371|Orlando|FL
KISO|35.3314|-77.6088|Kinston|NC
KISP|40.7963|-73.1017|Islip|NY
KISQ|45.9746|-86.1718|Manistique|MI
KISW|44.3603|-89.839|Wisconsin Rapids|WI
KITH|42.491|-76.4584|Ithaca|NY
KITR|39.2425|-102.285|Burlington|CO
KIWA|33.3078|-111.655|Mesa|AZ
KIWD|46.5253|-90.1316|Ironwood|MI
KIWH|40.763|-85.7997|Wabash|IN
KIWI|43.9614|-69.7126|Wiscasset|ME
KIWS|29.8182|-95.6726|Houston|TX
KIXA|36.3298|-77.6352|Halifax|NC
KIXD|38.8309|-94.8903|New Century|KS
KIYK|35.6588|-117.83|Inyokern|CA
KIZA|34.6068|-120.076|Santa Ynez|CA
KIZG|43.9911|-70.9479|Fryeburg|ME
KJAC|43.6073|-110.738|Jackson|WY
KJAN|32.3112|-90.0759|Jackson|MS
KJAS|30.8857|-94.0349|Jasper|TX
KJAU|36.3341|-84.163|Jacksboro|TN
KJAX|30.4925|-81.6878|Jacksonville|FL
KJBR|35.8317|-90.6464|Jonesboro|AR
KJCT|30.5113|-99.7635|Junction|TX
KJDD|32.7422|-95.4965|Mineola|TX
KJDN|47.3288|-106.953|Jordan|MT
KJEF|38.5912|-92.1561|Jefferson City|MO
KJER|42.7267|-114.457|Jerome|ID
KJES|31.554|-81.8825|Jesup|GA
KJFK|40.6394|-73.7793|New York|NY
KJFX|33.902|-87.3142|Jasper|AL
KJFZ|37.0637|-81.7983|Cedar Bluff|VA
KJGG|37.2392|-76.7161|Williamsburg|VA
KJHW|42.1542|-79.254|Jamestown|NY
KJKA|30.2905|-87.6718|Gulf Shores|AL
KJKJ|46.8392|-96.6637|Moorhead|MN
KJKL|37.5939|-83.3173|Jackson|KY
KJLN|37.1518|-94.4983|Joplin|MO
KJMR|45.8861|-93.2718|Mora|MN
KJMS|46.9297|-98.6782|Jamestown|ND
KJNX|35.5409|-78.3903|Smithfield|NC
KJOT|41.5178|-88.1755|Joliet|IL
KJQF|35.3878|-80.7091|Concord|NC
KJSO|31.8693|-95.2174|Jacksonville|TX
KJST|40.3161|-78.8339|Johnstown|PA
KJSV|35.4382|-94.8028|Sallisaw|OK
KJVL|42.6203|-89.0416|Janesville|WI
KJVY|38.3654|-85.7382|Jeffersonville|IN
KJWG|35.8647|-98.4208|Watonga|OK
KJWN|36.1824|-86.8867|Nashville|TN
KJWY|32.4587|-96.9124|Midlothian/Waxahachie|TX
KJXI|32.699|-94.9489|Gilmer|TX
KJXN|42.2605|-84.463|Jackson|MI
KJYG|43.9863|-94.5579|St James|MN
KJYL|32.6453|-81.5971|Sylvania|GA
KJYM|41.9213|-84.5858|Hillsdale|MI
KJYO|39.078|-77.5575|Leesburg|VA
KJYR|40.8968|-97.6228|York|NE
KJZI|32.6982|-80.0057|Johns Island|SC
KJZP|34.4534|-84.4574|Jasper|GA
KK02|37.8687|-89.8621|Perryville|MO
KK06|39.9734|-90.4037|Beardstown|IL
KK09|43.4534|-74.5176|Piseco|NY
KK15|38.1105|-92.6805|Osage Beach|MO
KK16|43.2503|-75.6062|Rome|NY
KK19|40.2622|-94.339|Albany|MO
KK20|37.3848|-83.2597|Chavies|KY
KK22|37.751|-82.6367|Debord|KY
KK24|37.0097|-85.1027|Jamestown|KY
KK32|37.7481|-97.4067|Wichita|KS
KK33|37.6152|-91.6044|Salem|MO
KK38|39.7353|-97.0476|Washington|KS
KK44|36.7989|-100.53|Beaver|OK
KK49|36.5056|-101.814|Texhoma|OK
KK51|37.266|-98.5481|Medicine Lodge|KS
KK52|39.6344|-91.727|Monroe City|MO
KK57|40.4458|-95.3628|Tarkio|MO
KK58|37.1671|-99.7725|Ashland|KS
KK59|39.5705|-95.1803|Atchison|KS
KK61|39.4712|-98.1288|Beloit|KS
KK62|38.7042|-84.3916|Falmouth|KY
KK74|46.6486|-96.9987|Kindred|ND
KK75|39.4336|-98.6795|Osborne|KS
KK77|36.7584|-99.1024|Freedom|OK
KK79|37.9845|-99.8943|Jetmore|KS
KK81|38.5397|-94.9205|Paola|KS
KK82|39.7624|-98.7945|Smith Center|KS
KK83|39.906|-95.7763|Sabetha|KS
KK88|37.8701|-95.3864|Iola|KS
KK89|39.7287|-92.4645|Macon|MO
KK94|38.5468|-99.2898|La Crosse|KS
KKIC|36.228|-121.122|King City|CA
KKLS|46.118|-122.898|Kelso|WA
KKNB|37.0111|-112.531|Kanab|UT
KL00|34.8708|-118.209|Rosamond|CA
KL05|35.7282|-118.4197|Kernville|CA
KL06|36.4638|-116.881|Death Valley|CA
KL08|33.259|-116.321|Borrego Springs|CA
KL09|36.6041|-117.159|Death Valley|CA
KL17|35.1415|-119.4386|Taft|CA
KL19|35.6197|-119.354|Wasco|CA
KL22|34.1297|-116.4074|Yucca Valley|CA
KL23|37.1021|-116.3128|Mercury|NV
KL26|34.3772|-117.316|Hesperia|CA
KL31|30.4451|-89.9889|Covington|LA
KL32|31.6202|-91.8344|Jonesville|LA
KL33|31.9774|-91.2405|St Joseph|LA
KL35|34.2638|-116.856|Big Bear|CA
KL38|30.1727|-90.9406|Gonzales|LA
KL39|31.1686|-93.3425|Leesville|LA
KL41|36.8125|-111.647|Marble Canyon|AZ
KL45|35.3248|-118.996|Bakersfield|CA
KL62|35.3527|-119.479|Buttonwillow|CA
KL64|33.7476|-115.3251|Desert Center|CA
KL65|33.7609|-117.218|Perris|CA
KL66|31.4775|-92.4611|Pollock|LA
KL70|34.5036|-118.3129|Agua Dulce|CA
KL71|35.1514|-118.0161|California City|CA
KL72|35.8125|-117.327|Trona|CA
KL73|35.5966|-119.128|Famoso|CA
KL77|33.6647|-115.7108|Chiriaco Summit|CA
KL83|29.7478|-90.8329|Schriever|LA
KL88|34.9375|-119.688|New Cuyama|CA
KL94|35.1011|-118.423|Tehachapi|CA
KLAA|38.0664|-102.6914|Lamar|CO
KLAF|40.4129|-86.9394|West Lafayette|IN
KLAL|27.9893|-82.0207|Lakeland|FL
KLAM|35.8798|-106.269|Los Alamos|NM
KLAN|42.7776|-84.5857|Lansing|MI
KLAR|41.3121|-105.675|Laramie|WY
KLAS|36.0834|-115.1518|Las Vegas|NV
KLAW|34.5677|-98.4166|Lawton|OK
KLAX|33.9425|-118.408|Los Angeles|CA
KLBB|33.6636|-101.823|Lubbock|TX
KLBE|40.2759|-79.4048|Latrobe|PA
KLBF|41.1262|-100.684|North Platte|NE
KLBL|37.0442|-100.96|Liberal|KS
KLBO|37.6483|-92.6524|Lebanon|MO
KLBR|33.5932|-95.0636|Clarksville|TX
KLBT|34.6108|-79.0594|Lumberton|NC
KLBX|29.1086|-95.4621|Angleton|TX
KLCG|42.2419|-96.9814|Wayne|NE
KLCH|30.1261|-93.2233|Lake Charles|LA
KLCI|43.5727|-71.4189|Laconia / Gilford|NH
KLCK|39.8138|-82.9278|Columbus|OH
KLCQ|30.182|-82.5769|Lake City|FL
KLDJ|40.6174|-74.2446|Linden|NJ
KLDM|43.9625|-86.4079|Ludington|MI
KLEB|43.6261|-72.3042|Lebanon|NH
KLEE|28.8231|-81.8087|Leesburg|FL
KLEM|45.9187|-102.106|Lemmon|SD
KLEW|44.0485|-70.2835|Auburn/Lewiston|ME
KLEX|38.0351|-84.6067|Lexington|KY
KLFI|37.0829|-76.3605|Hampton|VA
KLFK|31.234|-94.75|Lufkin|TX
KLFT|30.2053|-91.9876|Lafayette|LA
KLGA|40.7772|-73.8726|New York|NY
KLGB|33.8165|-118.1499|Long Beach|CA
KLGC|33.0089|-85.0743|LaGrange|GA
KLGD|45.2902|-118.007|La Grande|OR
KLGF|32.8608|-114.3962|Yuma Proving Ground(Yuma)|AZ
KLGU|41.7912|-111.852|Logan|UT
KLHB|30.8718|-96.6222|Hearne|TX
KLHM|38.9092|-121.351|Lincoln|CA
KLHQ|39.7556|-82.6571|Lancaster|OH
KLHV|41.1356|-77.4223|Lock Haven|PA
KLHW|31.8912|-81.561|Hinesville|GA
KLHX|38.0497|-103.509|La Junta|CO
KLHZ|36.0233|-78.3303|Louisburg|NC
KLIC|39.2748|-103.666|Limon|CO
KLIT|34.7292|-92.2236|Little Rock|AR
KLIU|33.9239|-102.387|Littlefield|TX
KLJF|45.0971|-94.5073|Litchfield|MN
KLKP|44.2645|-73.9619|Lake Placid|NY
KLKR|34.7229|-80.8546|Lancaster|SC
KLKU|38.0098|-77.9701|Louisa|VA
KLKV|42.1611|-120.399|Lakeview|OR
KLLJ|44.523|-114.218|Challis|ID
KLLN|33.5525|-102.372|Levelland|TX
KLLQ|33.6386|-91.751|Monticello|AR
KLLR|39.262|-123.754|Little River|CA
KLLU|37.4872|-94.3133|Lamar|MO
KLMO|40.1637|-105.163|Longmont|CO
KLMS|33.1462|-89.0625|Louisville|MS
KLMT|42.1561|-121.733|Klamath Falls|OR
KLNA|26.593|-80.0851|West Palm Beach|FL
KLNC|32.5776|-96.7175|Lancaster|TX
KLND|42.8152|-108.73|Lander|WY
KLNK|40.8449|-96.7618|Lincoln|NE
KLNL|46.154|-89.2121|Land O' Lakes|WI
KLNN|41.684|-81.3897|Willoughby|OH
KLNP|36.9875|-82.53|Wise|VA
KLNR|43.2099|-90.1813|Spring Green|WI
KLNS|40.1217|-76.2961|Lancaster|PA
KLOL|40.0664|-118.565|Lovelock|NV
KLOM|40.1375|-75.2651|Philadelphia|PA
KLOT|41.6073|-88.0962|Chicago/Romeoville|IL
KLOU|38.228|-85.6637|Louisville|KY
KLOZ|37.0822|-84.0849|London|KY
KLPC|34.6656|-120.468|Lompoc|CA
KLPR|41.3443|-82.1776|Lorain/Elyria|OH
KLQK|34.81|-82.7029|Pickens|SC
KLQR|38.2086|-99.086|Larned|KS
KLRD|27.5438|-99.4616|Laredo|TX
KLRF|34.9169|-92.1497|Jacksonville|AR
KLRG|45.3622|-68.5347|Lincoln|ME
KLRJ|42.778|-96.1937|Le Mars|IA
KLRO|32.8978|-79.7829|Mount Pleasant|SC
KLRU|32.2894|-106.922|Las Cruces|NM
KLRY|38.6099|-94.3435|Harrisonville|MO
KLSB|32.3335|-108.692|Lordsburg|NM
KLSE|43.879|-91.2567|La Crosse|WI
KLSF|32.3325|-84.988|Fort Benning|GA
KLSK|42.7538|-104.405|Lusk|WY
KLSN|37.0629|-120.869|Los Banos|CA
KLSV|36.2362|-115.034|Las Vegas|NV
KLTS|34.6671|-99.2667|Altus|OK
KLTY|48.5107|-110.991|Chester|MT
KLUD|33.2546|-97.5806|Decatur|TX
KLUF|33.535|-112.383|Glendale|AZ
KLUG|35.507|-86.8039|Lewisburg|TN
KLUK|39.1024|-84.4189|Cincinnati|OH
KLUL|31.6726|-89.1722|Laurel|MS
KLUM|44.8923|-91.8679|Menomonie|WI
KLUX|34.5071|-81.9472|Laurens|SC
KLVJ|29.5213|-95.2421|Houston|TX
KLVK|37.6934|-121.82|Livermore|CA
KLVL|36.7742|-77.7938|Lawrenceville|VA
KLVM|45.6994|-110.448|Livingston|MT
KLVN|44.6279|-93.2281|Minneapolis|MN
KLVS|35.6542|-105.142|Las Vegas|NM
KLWA|42.3512|-86.2557|South Haven|MI
KLWB|37.8579|-80.4004|Lewisburg|WV
KLWC|39.0112|-95.2166|Lawrence|KS
KLWL|41.1171|-114.922|Wells|NV
KLWM|42.7172|-71.1234|Lawrence|MA
KLWS|46.3745|-117.015|Lewiston|ID
KLWT|47.0484|-109.4661|Lewistown|MT
KLWV|38.7643|-87.6055|Lawrenceville|IL
KLXL|45.9497|-94.3474|Little Falls|MN
KLXN|40.791|-99.7773|Lexington|NE
KLXT|38.9597|-94.3714|Lee's Summit|MO
KLXV|39.2203|-106.317|Leadville|CO
KLXY|31.6398|-96.5147|Mexia|TX
KLYH|37.3267|-79.2004|Lynchburg|VA
KLYO|38.3395|-98.2287|Lyons|KS
KLYV|43.6162|-96.2179|Luverne|MN
KLZD|41.8197|-71.901|Danielson|CT
KLZU|33.9781|-83.9624|Lawrenceville|GA
KLZZ|31.1062|-98.1959|Lampasas|TX
KM01|35.2007|-90.054|Memphis|TN
KM02|36.128|-87.4298|Dickson|TN
KM04|35.5834|-89.5872|Covington|TN
KM05|36.1691|-89.6765|Caruthersville|MO
KM08|35.2145|-89.0434|Bolivar|TN
KM11|31.9019|-90.3667|Hazlehurst|MS
KM12|36.0959|-89.8634|Steele|MO
KM13|30.786|-89.5045|Poplarville|MS
KM15|35.5945|-87.8767|Linden|TN
KM16|32.3033|-90.4085|Raymond|MS
KM17|37.5961|-93.3477|Bolivar|MO
KM18|33.7201|-93.6588|Hope|AR
KM19|35.6377|-91.1764|Newport|AR
KM20|37.3995|-86.2602|Leitchfield|KY
KM21|37.226|-87.157|Greenville|KY
KM22|34.4444|-87.7119|Russellville|AL
KM23|32.3119|-89.1356|Newton|MS
KM24|30.8432|-89.1598|Wiggins|MS
KM25|36.7691|-88.5847|Mayfield|KY
KM27|34.876|-94.1093|Waldron|AR
KM29|35.3836|-87.9681|Clifton|TN
KM30|37.1824|-88.7507|Metropolis|IL
KM31|35.9034|-89.3973|Halls|TN
KM32|33.346|-91.3157|Lake Village|AR
KM33|36.3751|-86.4084|Gallatin|TN
KM34|37.0098|-88.2994|Calvert City|KY
KM36|34.8803|-91.1764|Brinkley|AR
KM37|33.7764|-90.525|Drew|MS
KM40|33.8737|-88.4897|Aberdeen|MS
KM41|34.8043|-89.5211|Holly Springs|MS
KM43|31.5954|-89.9065|Prentiss|MS
KM44|33.8862|-89.0234|Houston|MS
KM45|38.7347|-119.767|Markleeville|CA
KM46|45.8529|-106.709|Colstrip|MT
KM48|37.3301|-91.9732|Houston|MO
KM50|45.8148|-119.821|Boardman|OR
KM51|33.4975|-88.6813|Starkville|MS
KM53|35.8022|-88.8749|Humboldt|TN
KM54|36.1904|-86.3157|Lebanon|TN
KM55|33.8467|-88.1155|Vernon|AL
KM59|31.3174|-88.935|Richton|MS
KM60|35.2719|-91.2697|Augusta|AR
KM66|38.3409|-97.2139|Hillsboro|KS
KM70|36.2455|-90.9552|Pocahontas|AR
KM71|38.9018|-90.9603|Moscow Mills|MO
KM72|34.549|-89.0245|New Albany|MS
KM73|34.4123|-91.4663|Almyra|AR
KM75|48.3669|-107.919|Malta|MT
KM77|33.9967|-93.8381|Nashville|AR
KM78|34.3333|-92.7615|Malvern|AR
KM79|32.4856|-91.7724|Rayville|LA
KM80|32.4163|-91.1489|Richmond|LA
KM83|33.584|-88.6667|West Point|MS
KM85|36.4438|-89.9038|Gideon|MO
KM89|34.0998|-93.0661|Arkadelphia|AR
KM90|36.7586|-120.371|Mendota|CA
KM91|36.5368|-86.9212|Springfield|TN
KM93|36.3132|-87.914|Mc Kinnon|TN
KM94|46.6874|-119.921|Mattawa|WA
KM95|33.7092|-87.8153|Fayette|AL
KMAC|32.8221|-83.562|Macon|GA
KMAE|36.9886|-120.112|Madera|CA
KMAF|31.9425|-102.202|Midland|TX
KMAI|30.8378|-85.1819|Marianna|FL
KMAL|44.8537|-74.3289|Malone|NY
KMAO|34.1812|-79.3347|Mullins|SC
KMAW|36.6006|-89.9922|Malden|MO
KMBG|45.5465|-100.408|Mobridge|SD
KMBL|44.2727|-86.2465|Manistee|MI
KMBO|32.4387|-90.1031|Madison|MS
KMBS|43.5332|-84.0831|Freeland|MI
KMBT|35.8775|-86.3775|Murfreesboro|TN
KMBY|39.4644|-92.4284|Moberly|MO
KMCB|31.1785|-90.4719|McComb|MS
KMCC|38.6676|-121.401|Sacramento|CA
KMCD|45.8649|-84.6373|Mackinac Island|MI
KMCE|37.2847|-120.514|Merced|CA
KMCF|27.8493|-82.5212|Tampa|FL
KMCI|39.3017|-94.7139|Kansas City|MO
KMCK|40.2078|-100.5928|McCook|NE
KMCN|32.6928|-83.6492|Macon|GA
KMCO|28.4294|-81.309|Orlando|FL
KMCW|43.1598|-93.3297|Mason City|IA
KMCX|40.7088|-86.7668|Monticello|IN
KMCZ|35.8622|-77.1782|Williamston|NC
KMDD|32.0365|-102.101|Midland|TX
KMDF|36.4848|-99.1942|Mooreland|OK
KMDH|37.7781|-89.252|Murphysboro|IL
KMDQ|34.8614|-86.5575|Huntsville|AL
KMDS|44.016|-97.0859|Madison|SD
KMDT|40.1928|-76.7623|Harrisburg|PA
KMDW|41.786|-87.7524|Chicago|IL
KMDZ|45.101|-90.3033|Medford|WI
KMEB|34.7919|-79.3658|Maxton|NC
KMEI|32.3326|-88.7519|Meridian|MS
KMEJ|37.2769|-100.356|Meade|KS
KMEM|35.0438|-89.9763|Memphis|TN
KMER|37.3805|-120.568|Merced|CA
KMEV|39.0003|-119.751|Minden|NV
KMEY|42.1783|-95.7936|Mapleton|IA
KMEZ|34.5454|-94.2027|Mena|AR
KMFD|40.8214|-82.5166|Mansfield|OH
KMFE|26.1761|-98.238|McAllen|TX
KMFI|44.6369|-90.1893|Marshfield|WI
KMFR|42.3742|-122.873|Medford|OR
KMFV|37.6469|-75.7611|Melfa|VA
KMGC|41.7033|-86.8212|Michigan City|IN
KMGE|33.9154|-84.5163|Marietta|GA
KMGG|45.236|-93.9856|Maple Lake|MN
KMGJ|41.51|-74.2646|Montgomery|NY
KMGM|32.3006|-86.394|Montgomery|AL
KMGN|45.4256|-84.9134|Harbor Springs|MI
KMGR|31.0849|-83.8033|Moultrie|GA
KMGW|39.6433|-79.9176|Morgantown|WV
KMGY|39.589|-84.2249|Dayton|OH
KMHE|43.7748|-98.0386|Mitchell|SD
KMHK|39.141|-96.6708|Manhattan|KS
KMHL|39.0958|-93.2029|Marshall|MO
KMHP|32.3739|-82.0818|Metter|GA
KMHR|38.5547|-121.298|Sacramento|CA
KMHT|42.9326|-71.4357|Manchester|NH
KMHV|35.0564|-118.1453|Mojave|CA
KMIA|25.796|-80.2898|Miami|FL
KMIB|48.4156|-101.358|Minot|ND
KMIC|45.062|-93.3539|Minneapolis|MN
KMIE|40.2423|-85.3959|Muncie|IN
KMIO|36.9092|-94.8875|Miami|OK
KMIT|35.5074|-119.192|Shafter|CA
KMIV|39.3678|-75.0722|Millville|NJ
KMIW|42.1127|-92.9178|Marshalltown|IA
KMJD|30.4875|-89.6512|Picayune|MS
KMJQ|43.65|-94.9865|Jackson|MN
KMJX|39.9275|-74.2924|Toms River|NJ
KMKA|44.5252|-98.9581|Miller|SD
KMKC|39.1232|-94.5928|Kansas City|MO
KMKE|42.9472|-87.8966|Milwaukee|WI
KMKG|43.1695|-86.2382|Muskegon|MI
KMKJ|36.8949|-81.3499|Rural Retreat|VA
KMKL|35.5999|-88.9156|Jackson|TN
KMKN|31.9168|-98.6003|Comanche|TX
KMKO|35.6565|-95.3667|Muskogee|OK
KMKS|33.1855|-80.0363|Moncks Corner|SC
KMKT|44.2248|-93.9191|Mankato|MN
KMKV|31.0947|-92.0691|Marksville|LA
KMKY|25.995|-81.6725|Marco Island|FL
KMLB|28.102|-80.6411|Melbourne|FL
KMLC|34.8824|-95.7835|Mc Alester|OK
KMLD|42.1666|-112.297|Malad City|ID
KMLE|41.1959|-96.1121|Omaha|NE
KMLF|38.4266|-113.012|Milford|UT
KMLI|41.4485|-90.5075|Moline|IL
KMLJ|33.1542|-83.2407|Milledgeville|GA
KMLS|46.4273|-105.8854|Miles City|MT
KMLT|45.6478|-68.6856|Millinocket|ME
KMLU|32.5109|-92.0377|Monroe|LA
KMMH|37.6254|-118.8431|Mammoth Lakes|CA
KMMI|35.3973|-84.5626|Athens|TN
KMMK|41.5087|-72.8295|Meriden|CT
KMML|44.4505|-95.8219|Marshall|MN
KMMS|34.2315|-90.2896|Marks|MS
KMMT|33.9208|-80.8013|Eastover|SC
KMMU|40.7991|-74.4149|Morristown|NJ
KMMV|45.1944|-123.136|McMinnville|OR
KMNF|36.9928|-91.7145|Mountain View|MO
KMNI|33.5871|-80.2087|Manning|SC
KMNM|45.1267|-87.6384|Menominee|MI
KMNN|40.6162|-83.0635|Marion|OH
KMNV|35.5454|-84.3802|Madisonville|TN
KMNZ|31.6659|-98.1486|Hamilton|TX
KMO1|37.875|-92.4085|Richland|MO
KMO3|37.6603|-93.8168|Stockton|MO
KMO6|38.5876|-90.9938|Washington|MO
KMO8|39.77|-93.0127|Brookfield|MO
KMOB|30.6912|-88.2428|Mobile|AL
KMOD|37.6258|-120.954|Modesto|CA
KMOP|43.6217|-84.7375|Mount Pleasant|MI
KMOR|36.1794|-83.3755|Morristown|TN
KMOT|48.258|-101.2791|Minot|ND
KMOX|45.566|-95.9672|Morris|MN
KMPE|32.7994|-89.126|Philadelphia|MS
KMPG|39.8808|-80.7358|Moundsville|WV
KMPI|37.5109|-120.04|Mariposa|CA
KMPJ|35.1389|-92.9092|Morrilton|AR
KMPO|41.136|-75.3796|Mount Pocono|PA
KMPR|38.3524|-97.6913|Mc Pherson|KS
KMPV|44.2035|-72.5623|Barre/Montpelier|VT
KMPZ|40.9466|-91.5111|Mount Pleasant|IA
KMQB|40.5201|-90.6524|Macomb|IL
KMQI|35.9188|-75.6957|Manteo|NC
KMQJ|39.8435|-85.8971|Indianapolis|IN
KMQS|39.979|-75.8655|Coatesville|PA
KMQW|32.0958|-82.88|Mc Rae|GA
KMQY|36.009|-86.5201|Smyrna|TN
KMRB|39.4019|-77.9846|Martinsburg|WV
KMRC|35.5541|-87.1789|Columbia/Mount Pleasant|TN
KMRF|30.3696|-104.0159|Marfa|TX
KMRH|34.7336|-76.6606|Beaufort|NC
KMRJ|42.8868|-90.2362|Mineral Point|WI
KMRN|35.8202|-81.6114|Morganton|NC
KMRT|40.2247|-83.3516|Marysville|OH
KMRY|36.5868|-121.8442|Monterey|CA
KMSL|34.7451|-87.613|Muscle Shoals|AL
KMSN|43.1399|-89.3375|Madison|WI
KMSO|46.9158|-114.0911|Missoula|MT
KMSP|44.8801|-93.2217|Minneapolis|MN
KMSS|44.9362|-74.8443|Massena|NY
KMSV|41.7016|-74.795|Monticello|NY
KMSY|29.9934|-90.2647|New Orleans|LA
KMTC|42.6135|-82.8369|Mount Clemens|MI
KMTH|24.726|-81.0514|Marathon|FL
KMTJ|38.5098|-107.894|Montrose|CO
KMTN|39.3257|-76.4138|Baltimore|MD
KMTO|39.4779|-88.2792|Mattoon|IL
KMTP|41.0765|-71.9208|Montauk|NY
KMTV|36.6307|-80.0183|Martinsville|VA
KMTW|44.1271|-87.682|Manitowoc|WI
KMUI|40.4352|-76.5687|Fort Indiantown Gap(Annville)|PA
KMUL|31.1416|-83.7037|Moultrie|GA
KMUO|43.0436|-115.872|Mountain Home|ID
KMUT|41.3678|-91.1482|Muscatine|IA
KMVC|31.458|-87.351|Monroeville|AL
KMVE|44.9691|-95.7103|Montevideo|MN
KMVI|37.5273|-106.048|Monte Vista|CO
KMVL|44.5346|-72.614|Morrisville|VT
KMVM|44.7031|-67.4786|Machias|ME
KMVN|38.3228|-88.8593|Mount Vernon|IL
KMVY|41.3931|-70.6143|Martha's Vineyard|MA
KMWA|37.7512|-89.0166|Marion|IL
KMWC|43.1104|-88.0344|Milwaukee|WI
KMWH|47.2077|-119.32|Moses Lake|WA
KMWK|36.4597|-80.553|Mount Airy|NC
KMWL|32.7816|-98.0602|Mineral Wells|TX
KMWM|43.9134|-95.1094|Windom|MN
KMWO|39.531|-84.3953|Middletown|OH
KMXA|35.8944|-90.1546|Manila|AR
KMXF|32.3829|-86.3658|Montgomery|AL
KMXO|42.2204|-91.1633|Monticello|IA
KMYF|32.8157|-117.14|San Diego|CA
KMYJ|39.1575|-91.8183|Mexico|MO
KMYL|44.8888|-116.1011|McCall|ID
KMYR|33.6797|-78.9283|Myrtle Beach|SC
KMYV|39.0978|-121.57|Marysville|CA
KMYZ|39.8564|-96.6307|Marysville|KS
KMZH|46.4188|-92.8047|Moose Lake|MN
KMZJ|32.5106|-111.328|Marana|AZ
KMZZ|40.4899|-85.6797|Marion|IN
KN03|42.5926|-76.2149|Cortland|NY
KN06|38.5422|-75.5944|Laurel|DE
KN12|40.0668|-74.1777|Lakewood|NJ
KN13|40.9977|-76.4353|Bloomsburg|PA
KN14|39.9343|-74.8073|Lumberton|NJ
KN19|36.837|-108.0289|Aztec|NM
KN23|42.3026|-75.416|Sidney|NY
KN24|36.8003|-105.598|Questa|NM
KN27|41.74|-76.4471|Towanda|PA
KN35|40.9667|-78.93|Punxsutawney|PA
KN37|41.6225|-74.7013|Monticello|NY
KN38|41.7279|-77.3965|Wellsboro|PA
KN40|40.5663|-74.9786|Pittstown|NJ
KN47|40.2603|-75.6708|Pottstown|PA
KN49|39.3498|-119.474|Stagecoach|NV
KN51|40.5837|-74.7362|Whitehouse Station|NJ
KN52|34.8638|-80.748|Waxhaw|NC
KN57|39.8305|-75.7697|Toughkenamon|PA
KN58|39.5596|-119.241|Fernley|NV
KN59|38.8394|-119.338|Smith|NV
KN66|42.5247|-75.0644|Oneonta|NY
KN68|39.9731|-77.6433|Chambersburg|PA
KN71|40.0922|-76.5744|Mount Joy/Marietta|PA
KN73|39.9042|-74.7495|Vincentown|NJ
KN79|40.8369|-76.5525|Shamokin|PA
KN81|39.6675|-74.7577|Hammonton|NJ
KN82|41.598|-74.4595|Wurtsboro|NY
KN87|40.2139|-74.6018|Robbinsville|NJ
KN89|41.7288|-74.3769|Ellenville|NY
KN94|40.1879|-77.1743|Carlisle|PA
KN96|40.8855|-77.8163|Bellefonte|PA
KN98|45.2083|-84.9903|Boyne City|MI
KNBC|32.4774|-80.7232|Beaufort|SC
KNBG|29.8253|-90.035|New Orleans|LA
KNBJ|30.3891|-87.6353|Foley|AL
KNCA|34.7084|-77.4397|Jacksonville|NC
KNDY|38.3325|-77.0372|Dahlgren|VA
KNDZ|30.7044|-87.023|Milton|FL
KNEL|40.0333|-74.3533|Lakehurst|NJ
KNEN|30.3539|-81.8719|Jacksonville|FL
KNEW|30.0424|-90.0283|New Orleans|LA
KNFD|30.5077|-87.6455|Summerdale|AL
KNFE|36.6916|-76.135|Fentress|VA
KNFG|33.3013|-117.355|Oceanside|CA
KNFJ|30.5069|-86.9597|Milton|FL
KNFL|39.4166|-118.701|Fallon|NV
KNFW|32.7692|-97.4415|Fort Worth|TX
KNGP|27.6926|-97.2911|Corpus Christi|TX
KNGS|30.6108|-86.94|Milton|FL
KNGU|36.9376|-76.2893|Norfolk|VA
KNGW|27.7044|-97.4406|Corpus Christi|TX
KNHK|38.286|-76.4118|Patuxent River|MD
KNID|35.6854|-117.692|China Lake|CA
KNIP|30.2358|-81.6806|Jacksonville|FL
KNJK|32.8292|-115.672|El Centro|CA
KNJM|34.6904|-77.0297|Bogue|NC
KNJW|32.7985|-88.8343|Preston|MS
KNKT|34.9009|-76.8807|Cherry Point|NC
KNKX|32.8684|-117.143|San Diego|CA
KNLC|36.333|-119.952|Lemoore|CA
KNMM|32.5531|-88.5541|Lauderdale|MS
KNOG|27.9011|-98.0517|Orange Grove|TX
KNOW|48.1415|-123.414|Port Angeles|WA
KNPA|30.3527|-87.3186|Pensacola|FL
KNQA|35.3567|-89.8703|Millington|TN
KNQB|30.5636|-87.8097|Daphne|AL
KNQI|27.5072|-97.8097|Kingsville|TX
KNQX|24.5758|-81.6889|Key West|FL
KNRA|48.1883|-122.632|Coupeville|WA
KNRB|30.3911|-81.4247|Jacksonville|FL
KNRN|39.8504|-99.8947|Norton|KS
KNRQ|30.6253|-87.14|Pace|FL
KNRS|32.5667|-117.117|Imperial Beach|CA
KNSE|30.7242|-87.0219|Milton|FL
KNSI|33.2398|-119.458|San Nicolas Island|CA
KNTD|34.1203|-119.121|Point Mugu|CA
KNTU|36.8207|-76.0335|Virginia Beach|VA
KNUC|33.0227|-118.588|San Clemente Island|CA
KNUI|38.1442|-76.4269|St Inigoes|MD
KNUQ|37.4161|-122.049|Mountain View|CA
KNUW|48.3518|-122.656|Oak Harbor|WA
KNVD|37.8521|-94.3049|Nevada|MO
KNWL|27.635|-97.3122|Corpus Christi|TX
KNXP|34.2962|-116.162|Twentynine Palms|CA
KNY0|42.9982|-74.3296|Johnstown|NY
KNY2|43.0854|-76.2952|Warners|NY
KNYG|38.5017|-77.3053|Quantico|VA
KNYL|32.6509|-114.6094|Yuma|AZ
KNZY|32.6992|-117.215|San Diego|CA
KO02|39.8185|-120.353|Beckwourth|CA
KO05|40.2824|-121.241|Chester|CA
KO08|39.179|-121.993|Colusa|CA
KO09|39.7902|-123.266|Covelo|CA
KO11|35.757|-94.6499|Stilwell|OK
KO15|37.4874|-120.697|Turlock|CA
KO16|40.086|-123.814|Garberville|CA
KO20|38.0916|-121.359|Lodi|CA
KO22|38.0304|-120.415|Columbia|CA
KO24|37.9583|-119.107|Lee Vining|CA
KO26|36.5883|-118.052|Lone Pine|CA
KO27|37.7563|-120.8|Oakdale|CA
KO28|39.4513|-123.3724|Willits|CA
KO32|36.6663|-119.45|Reedley|CA
KO35|34.7053|-99.9086|Hollis|OK
KO37|39.7212|-122.147|Orland|CA
KO41|38.6739|-121.872|Woodland|CA
KO42|36.3988|-119.107|Woodlake|CA
KO43|39.0041|-119.158|Yerington|NV
KO46|41.4808|-122.4548|Weed|CA
KO47|35.4823|-96.7187|Prague|OK
KO52|39.1237|-121.605|Yuba City|CA
KO53|36.7906|-97.749|Medford|OK
KO54|40.7457|-122.922|Weaverville|CA
KO57|38.2639|-119.2228|Bridgeport|CA
KO59|41.5527|-120.166|Cedarville|CA
KO60|38.776|-122.993|Cloverdale|CA
KO61|38.684|-120.988|Cameron Park|CA
KO65|36.1125|-98.3087|Okeene|OK
KO69|38.2578|-122.606|Petaluma|CA
KO70|38.3768|-120.794|Jackson|CA
KO79|39.581|-120.354|Sierraville|CA
KO81|41.8874|-121.359|Tulelake|CA
KO86|40.9832|-122.694|Trinity Center|CA
KO88|38.1934|-121.704|Rio Vista|CA
KO89|41.0188|-121.433|Fall River Mills|CA
KOAJ|34.8292|-77.6121|Richlands|NC
KOAK|37.7201|-122.2212|Oakland|CA
KOAR|36.6819|-121.762|Marina|CA
KOBE|27.2628|-80.8498|Okeechobee|FL
KOBI|39.2192|-74.7948|Woodbine|NJ
KOCF|29.1726|-82.2242|Ocala|FL
KOCH|31.578|-94.7095|Nacogdoches|TX
KOCQ|44.8743|-87.9117|Oconto|WI
KOCW|35.5705|-77.0498|Washington|NC
KODO|31.9206|-102.387|Odessa|TX
KODX|41.6242|-98.9524|Ord|NE
KOEA|38.6852|-87.5431|Lawrenceville|IL
KOEB|41.9334|-85.0526|Coldwater|MI
KOEL|39.1099|-100.816|Oakley|KS
KOEO|45.31|-92.6919|Osceola|WI
KOFF|41.1193|-95.9085|Omaha|NE
KOFK|41.9855|-97.4351|Norfolk|NE
KOFP|37.709|-77.4367|Hanover|VA
KOGA|41.1195|-101.77|Ogallala|NE
KOGB|33.4568|-80.8595|Orangeburg|SC
KOGD|41.1959|-112.012|Ogden|UT
KOGM|46.8455|-89.3671|Ontonagon|MI
KOGS|44.6819|-75.4655|Ogdensburg|NY
KOIC|42.5666|-75.5241|Norwich|NY
KOIN|39.8339|-100.539|Oberlin|KS
KOJA|35.5448|-98.6685|Weatherford|OK
KOJC|38.8476|-94.7376|Olathe|KS
KOKB|33.2179|-117.3517|Oceanside|CA
KOKC|35.3934|-97.5982|Oklahoma City|OK
KOKH|48.2515|-122.674|Oak Harbor|WA
KOKK|40.5282|-86.059|Kokomo|IN
KOKM|35.6681|-95.9487|Okmulgee|OK
KOKS|41.401|-102.355|Oshkosh|NE
KOKV|39.1435|-78.1444|Winchester|VA
KOKZ|32.9667|-82.8382|Sandersville|GA
KOLD|44.9528|-68.6743|Old Town|ME
KOLE|42.2412|-78.3714|Olean|NY
KOLF|48.0945|-105.575|Wolf Point|MT
KOLG|46.3148|-91.8164|Solon Springs|WI
KOLM|46.9694|-122.903|Olympia|WA
KOLS|31.4177|-110.848|Nogales|AZ
KOLU|41.4481|-97.3402|Columbus|NE
KOLV|34.9787|-89.7869|Olive Branch|MS
KOLY|38.7218|-88.1764|Olney-Noble|IL
KOLZ|42.6809|-91.9737|Oelwein|IA
KOMA|41.3032|-95.8941|Omaha|NE
KOMH|38.2472|-78.0456|Orange|VA
KOMK|48.4644|-119.518|Omak|WA
KOMN|29.3006|-81.1136|Ormond Beach|FL
KONA|44.0772|-91.7083|Winona|MN
KONL|42.4689|-98.687|O'Neill|NE
KONM|34.0225|-106.903|Socorro|NM
KONO|44.0198|-117.0133|Oregon|OR
KONP|44.5804|-124.058|Newport|OR
KONT|34.056|-117.601|Ontario|CA
KONX|36.3989|-76.0163|Maple|NC
KONY|33.3509|-98.8192|Olney|TX
KONZ|42.0991|-83.1615|Detroit/Grosse Ile|MI
KOOA|41.2262|-92.4939|Cedar|IA
KOPL|30.5584|-92.0994|Opelousas|LA
KOPN|32.9546|-84.2632|Thomaston|GA
KOQN|39.9901|-75.5819|West Chester|PA
KOQU|41.5971|-71.4121|North Kingstown|RI
KOQW|42.0507|-90.7392|Maquoketa|IA
KORB|48.0159|-92.8561|Orr|MN
KORD|41.9786|-87.9048|Chicago|IL
KORE|42.5701|-72.2886|Orange|MA
KORF|36.8953|-76.201|Norfolk|VA
KORG|30.0692|-93.8009|Orange|TX
KORH|42.2673|-71.8757|Worcester|MA
KORK|34.8331|-92.2541|North Little Rock|AR
KORL|28.5455|-81.3329|Orlando|FL
KORS|48.7082|-122.91|Eastsound|WA
KOSA|33.0955|-94.9615|Mount Pleasant|TX
KOSC|44.4516|-83.3941|Oscoda|MI
KOSH|43.9844|-88.557|Oshkosh|WI
KOSU|40.0798|-83.073|Columbus|OH
KOSX|33.0903|-89.542|Kosciusko|MS
KOTG|43.6548|-95.5774|Worthington|MN
KOTH|43.4171|-124.246|North Bend|OR
KOTM|41.1064|-92.4498|Ottumwa|IA
KOUN|35.2456|-97.4721|Norman|OK
KOVE|39.4878|-121.622|Oroville|CA
KOVL|44.7786|-95.0328|Olivia|MN
KOVO|39.0456|-85.6053|North Vernon|IN
KOVS|43.1602|-90.6755|Boscobel|WI
KOWA|44.1234|-93.2606|Owatonna|MN
KOWB|37.7401|-87.1668|Owensboro|KY
KOWD|42.1905|-71.1729|Norwood|MA
KOWI|38.5387|-95.253|Ottawa|KS
KOWK|44.7155|-69.8665|Norridgewock|ME
KOWP|36.1753|-96.1518|Sand Springs|OK
KOWX|41.0356|-83.982|Ottawa|OH
KOXB|38.3104|-75.124|Ocean City|MD
KOXC|41.4786|-73.1352|Oxford|CT
KOXD|39.5023|-84.7844|Oxford|OH
KOXI|41.3301|-86.6623|Knox|IN
KOXR|34.2008|-119.207|Oxnard|CA
KOXV|41.2989|-93.1138|Knoxville|IA
KOYM|41.4125|-78.5026|St Marys|PA
KOZA|30.7353|-101.203|Ozona|TX
KOZR|31.2757|-85.7134|Fort Rucker/Ozark|AL
KOZW|42.6292|-83.9821|Howell|MI
KP01|32.4529|-112.8615|Ajo|AZ
KP03|31.3711|-109.69|Douglas|AZ
KP04|31.364|-109.883|Bisbee|AZ
KP08|32.9359|-111.427|Coolidge|AZ
KP10|35.7917|-110.423|Polacca|AZ
KP13|33.3531|-110.667|Globe|AZ
KP14|34.9407|-110.138|Holbrook|AZ
KP19|33.2988|-111.9158|Chandler|AZ
KP20|34.1489|-114.2684|Parker|AZ
KP23|35.335|-112.887|Seligman|AZ
KP29|31.6711|-110.022|Tombstone|AZ
KP33|32.2454|-109.895|Willcox|AZ
KP52|34.73|-112.035|Cottonwood|AZ
KPAE|47.9063|-122.282|Everett|WA
KPAH|37.0608|-88.7738|Paducah|KY
KPAM|30.0696|-85.5754|Panama City|FL
KPAN|34.2568|-111.339|Payson|AZ
KPAO|37.4611|-122.115|Palo Alto|CA
KPBF|34.1741|-91.9356|Pine Bluff|AR
KPBG|44.6509|-73.4681|Plattsburgh|NY
KPBH|45.709|-90.4025|Phillips|WI
KPBX|37.5608|-82.5701|Pikeville|KY
KPCM|28.0002|-82.1642|Plant City|FL
KPCW|41.5156|-82.8683|Port Clinton|OH
KPCZ|44.3333|-89.0198|Waupaca|WI
KPDC|43.0193|-91.1237|Prairie Du Chien|WI
KPDK|33.8763|-84.3021|Atlanta|GA
KPDT|45.6951|-118.841|Pendleton|OR
KPDX|45.5887|-122.598|Portland|OR
KPEA|41.4001|-92.9459|Pella|IA
KPEO|42.6371|-77.0529|Penn Yan|NY
KPEQ|31.3824|-103.511|Pecos|TX
KPEX|45.3721|-94.7447|Paynesville|MN
KPEZ|28.9542|-98.52|Pleasanton|TX
KPFC|45.1998|-123.962|Pacific City|OR
KPGA|36.9242|-111.4477|Page|AZ
KPGD|26.9202|-81.9905|Punta Gorda|FL
KPGR|36.0629|-90.5078|Paragould|AR
KPGV|35.6355|-77.3843|Greenville|NC
KPHD|40.4709|-81.4197|New Philadelphia|OH
KPHF|37.1319|-76.493|Newport News|VA
KPHG|39.7358|-99.3171|Phillipsburg|KS
KPHH|33.4517|-79.5262|Andrews|SC
KPHK|26.785|-80.6934|Pahokee|FL
KPHL|39.8719|-75.2411|Philadelphia|PA
KPHN|42.911|-82.5289|Port Huron|MI
KPHP|44.0485|-101.6009|Philip|SD
KPHT|36.3382|-88.3829|Paris|TN
KPHX|33.4353|-112.0059|Phoenix|AZ
KPIA|40.6638|-89.6926|Peoria|IL
KPIB|31.4671|-89.3371|Moselle|MS
KPIE|27.9102|-82.6874|Pinellas Park|FL
KPIH|42.9098|-112.596|Pocatello|ID
KPIL|26.1662|-97.3459|Port Isabel|TX
KPIM|32.8407|-84.8824|Pine Mountain|GA
KPIR|44.3827|-100.286|Pierre|SD
KPIT|40.4915|-80.2329|Pittsburgh|PA
KPJC|40.8016|-80.1607|Zelienople|PA
KPJY|37.9779|-89.3605|Pinckneyville|IL
KPKB|39.3451|-81.4392|Parkersburg (Williamstown)|WV
KPKD|46.9006|-95.0731|Park Rapids|MN
KPKF|45.955|-90.4244|Park Falls|WI
KPKV|28.654|-96.6813|Port Lavaca|TX
KPLD|40.4508|-84.9901|Portland|IN
KPLK|36.6259|-93.2289|Branson / Hollister|MO
KPLN|45.5709|-84.7967|Pellston|MI
KPLR|33.5588|-86.2491|Pell City|AL
KPLU|47.1039|-122.287|Puyallup|WA
KPMB|48.9425|-97.2408|Pembina|ND
KPMD|34.6294|-118.085|Palmdale|CA
KPMH|38.8405|-82.8473|Portsmouth|OH
KPMP|26.2471|-80.1111|Pompano Beach|FL
KPMU|34.3635|-89.8926|Batesville|MS
KPMV|40.9479|-95.9173|Plattsmouth|NE
KPMZ|35.8084|-76.7593|Plymouth|NC
KPNA|42.7955|-109.807|Pinedale|WY
KPNC|36.732|-97.0998|Ponca City|OK
KPNE|40.0824|-75.0106|Philadelphia|PA
KPNM|45.5599|-93.6082|Princeton|MN
KPNN|45.2007|-67.5644|Princeton|ME
KPNS|30.4727|-87.1866|Pensacola|FL
KPNT|40.9243|-88.6245|Pontiac|IL
KPOB|35.1709|-79.0145|Fort Bragg|NC
KPOC|34.0916|-117.782|La Verne|CA
KPOE|31.0448|-93.1917|Fort Polk|LA
KPOF|36.7739|-90.3249|Poplar Bluff|MO
KPOH|42.7428|-94.6473|Pocahontas|IA
KPOU|41.6266|-73.8842|Poughkeepsie|NY
KPOV|41.2102|-81.2516|Ravenna|OH
KPOY|44.8672|-108.793|Powell|WY
KPPA|35.613|-100.996|Pampa|TX
KPPF|37.3299|-95.5062|Parsons|KS
KPPO|41.5725|-86.7345|La Porte|IN
KPPQ|39.6389|-90.7784|Pittsfield|IL
KPQI|46.689|-68.0448|Presque Isle|ME
KPQL|30.4628|-88.5292|Pascagoula|MS
KPQN|43.9833|-96.3003|Pipestone|MN
KPRB|35.6729|-120.627|Paso Robles|CA
KPRC|34.6535|-112.4199|Prescott|AZ
KPRG|39.7002|-87.6696|Paris|IL
KPRN|31.8457|-86.6107|Greenville|AL
KPRO|41.828|-94.1599|Perry|IA
KPRS|29.6342|-104.3615|Presidio|TX
KPRX|33.6366|-95.4508|Paris|TX
KPRZ|34.1455|-103.41|Portales|NM
KPSB|40.8844|-78.0873|Philipsburg|PA
KPSC|46.2647|-119.119|Pasco|WA
KPSF|42.4268|-73.2929|Pittsfield|MA
KPSK|37.1373|-80.6785|Dublin|VA
KPSM|43.0779|-70.8233|Portsmouth|NH
KPSN|31.7797|-95.7063|Palestine|TX
KPSO|37.2863|-107.056|Pagosa Springs|CO
KPSP|33.8297|-116.507|Palm Springs|CA
KPSX|28.7275|-96.251|Palacios|TX
KPTB|37.1838|-77.5074|Petersburg|VA
KPTD|44.6766|-74.9486|Potsdam|NY
KPTK|42.6655|-83.4201|Pontiac|MI
KPTN|29.7095|-91.339|Patterson|LA
KPTS|37.4495|-94.7311|Pittsburg|KS
KPTT|37.7016|-98.7469|Pratt|KS
KPTV|36.0296|-119.063|Porterville|CA
KPTW|40.2396|-75.5567|Pottstown|PA
KPUB|38.2891|-104.497|Pueblo|CO
KPUC|39.6139|-110.751|Price|UT
KPUJ|33.9123|-84.9411|Dallas|GA
KPUW|46.7416|-117.1116|Pullman|WA
KPVB|42.6894|-90.4444|Platteville|WI
KPVC|42.0719|-70.2214|Provincetown|MA
KPVD|41.725|-71.4257|Providence/Warwick|RI
KPVE|35.6564|-88.1954|Darden|TN
KPVF|38.7242|-120.753|Placerville|CA
KPVG|36.7804|-76.4488|Chesapeake|VA
KPVJ|34.7111|-97.2232|Pauls Valley|OK
KPVU|40.2189|-111.7224|Provo|UT
KPVW|34.1681|-101.717|Plainview|TX
KPWA|35.5342|-97.6471|Oklahoma City|OK
KPWC|46.7248|-94.3817|Pine River|MN
KPWD|48.7903|-104.534|Plentywood|MT
KPWG|31.4849|-97.3165|Waco|TX
KPWK|42.1142|-87.9015|Chicago/Prospect Heights/Wheeling|IL
KPWM|43.6462|-70.3093|Portland|ME
KPWT|47.4902|-122.765|Bremerton|WA
KPXE|32.5106|-83.7673|Perry|GA
KPYG|34.7421|-80.3452|Pageland|SC
KPYM|41.909|-70.7288|Plymouth|MA
KPYN|37.1267|-90.7129|Piedmont|MO
KPYP|34.09|-85.6101|Centre|AL
KPYX|36.4129|-100.752|Perryton|TX
KPZQ|45.4071|-83.8129|Rogers City|MI
KR47|41.1025|-84.0544|Leipsic|OH
KR49|48.7182|-118.6565|Republic|WA
KRAC|42.7606|-87.8152|Racine|WI
KRAL|33.9519|-117.445|Riverside|CA
KRAP|44.0453|-103.057|Rapid City|SD
KRAS|27.8118|-97.0888|Port Aransas|TX
KRAW|38.3469|-93.3454|Warsaw|MO
KRBD|32.6809|-96.8682|Dallas|TX
KRBE|42.5697|-99.5684|Bassett|NE
KRBG|43.2388|-123.356|Roseburg|OR
KRBL|40.1507|-122.252|Red Bluff|CA
KRBM|34.8477|-92.2995|North Little Rock|AR
KRBO|27.7781|-97.6901|Robstown|TX
KRBW|32.921|-80.6406|Walterboro|SC
KRCA|44.145|-103.104|Rapid City|SD
KRCK|30.6316|-96.9897|Rockdale|TX
KRCM|38.7842|-93.8029|Warrensburg|MO
KRCP|39.3466|-99.3046|Stockton|KS
KRCR|41.0656|-86.1817|Rochester|IN
KRCT|43.9018|-85.516|Reed City|MI
KRCX|45.4968|-91.0005|Ladysmith|WI
KRCZ|34.8913|-79.7596|Rockingham|NC
KRDD|40.509|-122.293|Redding|CA
KRDG|40.3785|-75.9652|Reading|PA
KRDK|41.0105|-95.2599|Red Oak|IA
KRDM|44.2541|-121.15|Redmond|OR
KRDR|47.9611|-97.4012|Grand Forks|ND
KRDU|35.8787|-78.7873|Raleigh/Durham|NC
KRED|45.1846|-109.2577|Red Lodge|MT
KREI|34.0853|-117.146|Redlands|CA
KREO|42.5777|-117.885|Rome|OR
KRFD|42.1954|-89.0972|Chicago/Rockford|IL
KRFG|28.2936|-97.323|Refugio|TX
KRFI|32.1417|-94.8517|Henderson|TX
KRGK|44.5894|-92.485|Red Wing|MN
KRHI|45.6312|-89.4675|Rhinelander|WI
KRHP|35.1952|-83.863|Andrews|NC
KRHV|37.3329|-121.819|San Jose|CA
KRIC|37.5052|-77.3197|Richmond|VA
KRID|39.7572|-84.8428|Richmond|IN
KRIF|38.7341|-112.1016|Richfield|UT
KRIL|39.5263|-107.727|Rifle|CO
KRIR|33.9897|-117.411|Riverside|CA
KRIU|38.4868|-121.1028|Rancho Murieta|CA
KRIV|33.8807|-117.259|Riverside|CA
KRIW|43.0642|-108.46|Riverton|WY
KRJD|38.9701|-75.8663|Ridgely|MD
KRK1|69.6768|-146.9002|Kavik|AK
KRKD|44.0601|-69.0992|Rockland|ME
KRKP|28.0868|-97.0446|Rockport|TX
KRKR|35.0216|-94.6213|Poteau|OK
KRKS|41.5942|-109.065|Rock Springs|WY
KRKW|35.9223|-84.6897|Rockwood|TN
KRLD|46.3056|-119.304|Richland|WA
KRME|43.2338|-75.407|Rome|NY
KRMG|34.3506|-85.158|Rome|GA
KRMN|38.3981|-77.4555|Fredericksburg|VA
KRMY|42.2512|-84.9555|Marshall|MI
KRNC|35.6987|-85.8438|Mc Minnville|TN
KRND|29.5297|-98.2789|Universal City|TX
KRNH|45.1483|-92.5381|New Richmond|WI
KRNM|33.0392|-116.915|Ramona|CA
KRNO|39.4991|-119.768|Reno|NV
KRNP|42.993|-84.1389|Owosso|MI
KRNT|47.4931|-122.216|Renton|WA
KRNV|33.7611|-90.7579|Cleveland|MS
KROA|37.3255|-79.9754|Roanoke|VA
KROC|43.1189|-77.6724|Rochester|NY
KROG|36.3723|-94.1069|Rogers|AR
KROS|45.698|-92.953|Rush City|MN
KROW|33.3016|-104.531|Roswell|NM
KROX|48.856|-95.697|Roseau|MN
KRPB|39.8179|-97.6596|Belleville|KS
KRPD|45.423|-91.7731|Rice Lake|WI
KRPH|33.1102|-98.5553|Graham|TX
KRPJ|41.893|-89.0783|Rochelle|IL
KRPX|46.4751|-108.5415|Roundup|MT
KRQB|43.7226|-85.5041|Big Rapids|MI
KRQE|35.6521|-109.067|Window Rock|AZ
KRQO|35.4727|-98.0058|El Reno|OK
KRRL|45.1989|-89.7129|Merrill|WI
KRRQ|43.4522|-96.1798|Rock Rapids|IA
KRRT|48.9414|-95.3484|Warroad|MN
KRSL|38.8721|-98.8118|Russell|KS
KRSN|32.5142|-92.5885|Ruston|LA
KRST|43.9083|-92.5|Rochester|MN
KRSV|39.016|-87.6498|Robinson|IL
KRSW|26.5347|-81.7528|Fort Myers|FL
KRTN|36.7415|-104.502|Raton|NM
KRUE|35.2591|-93.0933|Russellville|AR
KRUG|48.3904|-100.024|Rugby|ND
KRUQ|35.6459|-80.5203|Salisbury|NC
KRUT|43.5294|-72.9496|Rutland|VT
KRVJ|32.0595|-82.1538|Reidsville|GA
KRVL|40.6774|-77.6268|Reedsville|PA
KRVN|36.4576|-82.885|Surgoinsville|TN
KRVS|36.0396|-95.9846|Tulsa|OK
KRWF|44.5472|-95.0823|Redwood Falls|MN
KRWI|35.8563|-77.8919|Rocky Mount|NC
KRWL|41.8056|-107.2|Rawlins|WY
KRWN|41.0922|-86.6134|Winamac|IN
KRWV|30.5155|-96.7041|Caldwell|TX
KRXE|43.8339|-111.805|Rexburg|ID
KRYM|46.0912|-94.3605|Fort Ripley|MN
KRYN|32.1422|-111.175|Tucson|AZ
KRYV|43.1696|-88.7232|Watertown|WI
KRYW|30.4986|-97.9695|Lago Vista|TX
KRYY|34.0132|-84.5972|Atlanta|GA
KRZL|40.9479|-87.1826|Rensselaer|IN
KRZN|45.8227|-92.3725|Siren|WI
KRZR|35.2115|-84.7998|Cleveland|TN
KRZT|39.4404|-83.0231|Chillicothe|OH
KS01|48.1686|-111.976|Conrad|MT
KS03|42.1903|-122.6606|Ashland|OR
KS05|43.0865|-124.408|Bandon|OR
KS10|47.866|-119.943|Chelan|WA
KS12|44.6378|-123.059|Albany|OR
KS17|33.6457|-81.8671|Trenton|SC
KS19|33.9081|-82.2669|McCormick|SC
KS20|45.8321|-120.845|Goldendale|WA
KS21|43.8763|-121.453|Sunriver|OR
KS23|48.7081|-117.413|Ione|WA
KS24|41.2957|-83.0372|Fremont|OH
KS25|47.7957|-103.254|Watford City|ND
KS27|48.1786|-114.304|Kalispell|MT
KS32|47.4228|-98.1059|Cooperstown|ND
KS33|44.6702|-121.155|Madras|OR
KS34|47.4735|-114.9071|Plains|MT
KS36|47.3371|-122.104|Kent|WA
KS39|44.287|-120.904|Prineville|OR
KS40|46.2134|-119.791|Prosser|WA
KS42|36.3311|-104.6183|Springer|NM
KS45|44.8769|-124.029|Gleneden Beach|OR
KS48|45.3544|-122.268|Sandy|OR
KS50|47.3277|-122.227|Auburn|WA
KS52|48.4249|-120.146|Winthrop|WA
KS59|48.2838|-115.49|Libby|MT
KS64|47.1472|-110.23|Stanford|MT
KS67|43.5813|-116.523|Nampa|ID
KS69|46.9544|-112.65|Lincoln|MT
KS70|46.7949|-119.079|Othello|WA
KS71|48.5921|-109.251|Chinook|MT
KS72|47.3257|-116.5765|St Maries|ID
KS73|46.2193|-116.013|Kamiah|ID
KS75|44.0916|-116.901|Payette|ID
KS78|43.8499|-116.543|Emmett|ID
KS83|47.5473|-116.1887|Smelterville|ID
KS84|46.0388|-116.332|Cottonwood|ID
KS85|48.1533|-104.504|Culbertson|MT
KS87|44.2068|-116.962|Weiser|ID
KS94|46.8587|-117.414|Colfax|WA
KS95|46.0469|-118.417|College Place|WA
KS97|48.1049|-119.721|Brewster|WA
KSAA|41.4449|-106.824|Saratoga|WY
KSAC|38.5125|-121.493|Sacramento|CA
KSAD|32.8548|-109.635|Safford|AZ
KSAF|35.6171|-106.089|Santa Fe|NM
KSAN|32.7336|-117.19|San Diego|CA
KSAR|38.1489|-89.6987|Sparta|IL
KSAS|33.2414|-115.952|Salton City|CA
KSAT|29.5337|-98.4698|San Antonio|TX
KSAV|32.1266|-81.2|Savannah|GA
KSAW|46.3515|-87.3959|Gwinn|MI
KSAZ|46.3809|-94.8066|Staples|MN
KSBA|34.4262|-119.84|Santa Barbara|CA
KSBD|34.0967|-117.2366|San Bernardino|CA
KSBM|43.7696|-87.8514|Sheboygan|WI
KSBN|41.7083|-86.3169|South Bend|IN
KSBO|32.6091|-82.3699|Swainsboro|GA
KSBP|35.2368|-120.642|San Luis Obispo|CA
KSBS|40.5163|-106.866|Steamboat Springs|CO
KSBU|43.5953|-94.0928|Blue Earth|MN
KSBX|48.5407|-111.871|Shelby|MT
KSBY|38.3405|-75.5103|Salisbury|MD
KSCB|41.6103|-96.6299|Scribner|NE
KSCD|33.1718|-86.3055|Sylacauga|AL
KSCH|42.8525|-73.9289|Schenectady|NY
KSCK|37.8933|-121.2381|Stockton|CA
KSCX|36.4555|-84.586|Oneida|TN
KSDA|40.7516|-95.4137|Shenandoah|IA
KSDC|43.2346|-77.1194|Williamson/Sodus|NY
KSDF|38.1706|-85.7351|Louisville|KY
KSDM|32.5726|-116.98|San Diego|CA
KSDY|47.7051|-104.1944|Sidney|MT
KSEA|47.4479|-122.3103|Seattle|WA
KSEE|32.8262|-116.972|San Diego/El Cajon|CA
KSEF|27.4564|-81.3424|Sebring|FL
KSEG|40.8206|-76.8639|Selinsgrove|PA
KSEM|32.3439|-86.9878|Selma|AL
KSEP|32.2153|-98.1777|Stephenville|TX
KSEQ|29.5645|-97.9025|Seguin|TX
KSER|38.9236|-85.9074|Seymour|IN
KSET|38.9297|-90.43|St Charles|MO
KSEZ|34.8486|-111.788|Sedona|AZ
KSFB|28.7743|-81.2346|Orlando|FL
KSFF|47.6829|-117.3219|Spokane|WA
KSFM|43.3939|-70.708|Sanford|ME
KSFO|37.6198|-122.3748|San Francisco|CA
KSFQ|36.6824|-76.6019|Suffolk|VA
KSFY|42.0458|-90.1079|Savanna|IL
KSFZ|41.9208|-71.4914|Pawtucket|RI
KSGF|37.245|-93.3886|Springfield|MO
KSGH|39.8403|-83.8402|Springfield|OH
KSGJ|29.9592|-81.3398|St Augustine|FL
KSGR|29.6223|-95.6565|Houston|TX
KSGS|44.8571|-93.0329|South St Paul|MN
KSGT|34.5995|-91.575|Stuttgart|AR
KSGU|37.0364|-113.5103|St George|UT
KSHD|38.2638|-78.8964|Weyers Cave|VA
KSHL|43.2084|-95.8334|Sheldon|IA
KSHN|47.2336|-123.148|Shelton|WA
KSHR|44.7692|-106.98|Sheridan|WY
KSHV|32.4447|-93.8267|Shreveport|LA
KSIF|36.4372|-79.851|Stoneville|NC
KSIK|36.8989|-89.5618|Sikeston|MO
KSIV|39.1147|-87.4483|Sullivan|IN
KSIY|41.7814|-122.468|Montague|CA
KSJN|34.5186|-109.379|St Johns|AZ
KSJT|31.3577|-100.496|San Angelo|TX
KSJX|45.6923|-85.5666|Beaver Island|MI
KSKA|47.6151|-117.656|Spokane|WA
KSKF|29.3842|-98.5811|San Antonio|TX
KSKI|42.3791|-94.9797|Sac City|IA
KSKX|36.4525|-105.6775|Taos|NM
KSLB|42.5973|-95.2407|Storm Lake|IA
KSLC|40.7889|-111.9799|Salt Lake City|UT
KSLE|44.9095|-123.003|Salem|OR
KSLG|36.1919|-94.49|Siloam Springs|AR
KSLH|45.6537|-84.5193|Cheboygan|MI
KSLI|33.7909|-118.0503|Los Alamitos|CA
KSLK|44.3869|-74.2046|Saranac Lake|NY
KSLN|38.791|-97.6522|Salina|KS
KSLO|38.6429|-88.9642|Salem|IL
KSLR|33.1598|-95.6211|Sulphur Springs|TX
KSMD|41.1434|-85.1528|Fort Wayne|IN
KSME|37.0534|-84.6159|Somerset|KY
KSMF|38.6954|-121.591|Sacramento|CA
KSMN|45.1222|-113.882|Salmon|ID
KSMO|34.0158|-118.451|Santa Monica|CA
KSMQ|40.626|-74.6702|Somerville|NJ
KSMS|33.995|-80.3613|Sumter|SC
KSMX|34.8989|-120.457|Santa Maria|CA
KSNA|33.6751|-117.8693|Santa Ana|CA
KSNC|41.3842|-72.506|Chester|CT
KSNH|35.1704|-88.2159|Savannah|TN
KSNK|32.6902|-100.9489|Snyder|TX
KSNL|35.3579|-96.9428|Shawnee|OK
KSNS|36.6628|-121.606|Salinas|CA
KSNY|41.1013|-102.985|Sidney|NE
KSOA|30.5857|-100.649|Sonora|TX
KSOP|35.2372|-79.3894|Carthage|NC
KSOW|34.2641|-110.0071|Show Low|AZ
KSPA|34.9157|-81.9565|Spartanburg|SC
KSPB|45.771|-122.862|Scappoose|OR
KSPF|44.4803|-103.783|Spearfish|SD
KSPG|27.7651|-82.627|St Petersburg|FL
KSPH|32.9834|-93.4092|Springhill|LA
KSPI|39.8441|-89.6779|Springfield|IL
KSPK|40.145|-111.6677|Spanish Fork|UT
KSPS|33.9888|-98.4919|Wichita Falls|TX
KSPW|43.1655|-95.2028|Spencer|IA
KSPZ|39.403|-119.251|Silver Springs|NV
KSQI|41.7422|-89.6736|Rock Falls|IL
KSQL|37.5131|-122.2508|San Carlos|CA
KSRB|36.0559|-85.5307|Sparta|TN
KSRC|35.2106|-91.7375|Searcy|AR
KSRE|35.2747|-96.6752|Seminole|OK
KSRQ|27.3946|-82.5544|Sarasota/Bradenton|FL
KSRR|33.4628|-105.535|Alto|NM
KSSC|33.9727|-80.4706|Sumter|SC
KSSF|29.337|-98.4711|San Antonio|TX
KSSI|31.1518|-81.3913|St Simons Island|GA
KSSQ|45.7314|-91.9207|Shell Lake|WI
KSTC|45.5466|-94.0599|Saint Cloud|MN
KSTE|44.5452|-89.5303|Stevens Point|WI
KSTF|33.4331|-88.8486|Starkville|MS
KSTJ|39.7719|-94.9097|St Joseph|MO
KSTK|40.6135|-103.2638|Sterling|CO
KSTL|38.7487|-90.37|St Louis|MO
KSTP|44.9348|-93.06|Saint Paul|MN
KSTS|38.509|-122.813|Santa Rosa|CA
KSUA|27.1817|-80.2211|Stuart|FL
KSUD|35.7896|-96.6557|Stroud|OK
KSUE|44.8437|-87.4215|Sturgeon Bay|WI
KSUN|43.5044|-114.296|Hailey|ID
KSUO|43.2585|-100.8595|Rosebud|SD
KSUS|38.6621|-90.652|St Louis|MO
KSUT|33.9293|-78.075|Southport|NC
KSUU|38.2627|-121.927|Fairfield|CA
KSUW|46.6886|-92.095|Superior|WI
KSUX|42.3976|-96.3822|Sioux City|IA
KSUZ|34.5906|-92.4794|Bryant|AR
KSVC|32.6367|-108.1547|Silver City|NM
KSVE|40.3757|-120.573|Susanville|CA
KSVH|35.7653|-80.9539|Statesville|NC
KSVN|32.01|-81.1457|Savannah|GA
KSWF|41.5042|-74.1089|Newburgh|NY
KSWI|33.6242|-96.5861|Sherman|TX
KSWO|36.1621|-97.0856|Stillwater|OK
KSWT|40.8647|-97.1092|Seward|NE
KSWW|32.4674|-100.467|Sweetwater|TX
KSXK|42.9858|-96.1614|Maurice|IA
KSXL|38.2316|-80.8708|Mount Lookout|WV
KSYF|39.7611|-101.796|St Francis|KS
KSYI|35.5601|-86.4425|Shelbyville|TN
KSYN|44.4756|-93.0168|Dennison|MN
KSYR|43.1112|-76.1063|Syracuse|NY
KSYV|31.5585|-83.8957|Sylvester|GA
KSZL|38.7303|-93.5479|Knob Noster|MO
KSZN|34.0601|-119.9152|Santa Cruz Island|CA
KSZP|34.3472|-119.061|Santa Paula|CA
KSZT|48.2995|-116.56|Sandpoint|ID
KSZY|35.2029|-88.4984|Selmer|TN
KT00|29.7701|-94.6624|Anahuac|TX
KT03|36.0911|-111.3829|Tuba City|AZ
KT05|26.56|-97.4392|Port Mansfield|TX
KT12|30.6458|-93.9136|Kirbyville|TX
KT15|31.3407|-96.852|Marlin|TX
KT16|33.6942|-108.849|Reserve|NM
KT17|29.273|-95.8869|Boling-Iago|TX
KT19|27.8836|-98.6003|Freer|TX
KT20|29.5292|-97.4643|Gonzales|TX
KT23|32.719|-99.2676|Albany|TX
KT24|31.2335|-93.9819|Pineland|TX
KT30|28.8223|-99.109|Pearsall|TX
KT31|33.2085|-96.7422|McKinney|TX
KT35|30.8794|-96.9711|Cameron|TX
KT36|31.146|-90.1681|Tylertown|MS
KT39|33.5823|-98.6187|Archer City|TX
KT41|29.6693|-95.0642|La Porte|TX
KT42|40.2113|-123.298|Mad River|CA
KT45|35.3617|-101.365|Panhandle|TX
KT47|33.5351|-96.6496|Howe|TX
KT50|30.9319|-99.8088|Menard|TX
KT51|30.0429|-95.6672|Houston|TX
KT54|29.5226|-95.7771|Rosenberg|TX
KT55|34.5667|-102.323|Dimmitt|TX
KT59|35.4511|-100.2|Wheeler|TX
KT60|33.1698|-100.1973|Aspermont|TX
KT65|26.1776|-97.9731|Weslaco|TX
KT67|32.9305|-97.4123|Fort Worth|TX
KT69|28.0392|-97.5424|Sinton|TX
KT74|30.5726|-97.4432|Taylor|TX
KT78|30.0778|-94.6986|Liberty|TX
KT82|30.2432|-98.9092|Fredericksburg|TX
KT85|29.3132|-97.1384|Yoakum|TX
KT88|32.4684|-100.921|Colorado City|TX
KT90|29.8195|-94.4311|Winnie|TX
KT92|30.7322|-99.1843|Mason|TX
KT93|36.4408|-100.124|Follett|TX
KTAD|37.2594|-104.341|Trinidad|CO
KTAN|41.8744|-71.0166|Taunton|MA
KTAZ|39.5342|-89.3278|Taylorville|IL
KTBN|37.7416|-92.1407|Fort Leonard Wood|MO
KTBR|32.4827|-81.7369|Statesboro|GA
KTCC|35.1828|-103.603|Tucumcari|NM
KTCL|33.2206|-87.6114|Tuscaloosa|AL
KTCM|47.1377|-122.476|Tacoma|WA
KTCS|33.2369|-107.272|Truth or Consequences|NM
KTCY|37.689|-121.442|Tracy|CA
KTDF|36.2849|-78.9842|Timberlake|NC
KTDO|46.4772|-122.806|Toledo|WA
KTDW|35.1699|-101.826|Amarillo|TX
KTDZ|41.5649|-83.4823|Toledo|OH
KTEL|38.0177|-86.6909|Tell City|IN
KTEW|42.5658|-84.4232|Mason|MI
KTEX|37.9538|-107.908|Telluride|CO
KTFP|27.913|-97.2115|Aransas Pass|TX
KTGC|35.9325|-88.8489|Milan|TN
KTGI|37.8251|-75.9978|Tangier|VA
KTHA|35.3801|-86.2464|Tullahoma|TN
KTHM|47.5735|-115.281|Thompson Falls|MT
KTHV|39.917|-76.873|Thomasville|PA
KTIF|41.9627|-100.5704|Thedford|NE
KTIK|35.4147|-97.3866|Oklahoma City|OK
KTIP|40.2936|-88.1424|Rantoul|IL
KTIW|47.2674|-122.5773|Tacoma|WA
KTIX|28.5148|-80.7992|Titusville|FL
KTKC|44.2491|-95.6073|Tracy|MN
KTKI|33.1779|-96.5905|Dallas|TX
KTKO|39.805|-98.2206|Mankato|KS
KTKV|45.4691|-89.8057|Tomahawk|WI
KTKX|36.2259|-90.0366|Kennett|MO
KTLH|30.4012|-84.3543|Tallahassee|FL
KTLR|36.1563|-119.326|Tulare|CA
KTMA|31.429|-83.4885|Tifton|GA
KTMB|25.6479|-80.4328|Miami|FL
KTME|29.8072|-95.8979|Houston|TX
KTMK|45.4182|-123.814|Tillamook|OR
KTNP|34.1339|-115.9473|Twentynine Palms|CA
KTNT|25.8618|-80.897|Miami|FL
KTNU|41.6744|-93.0217|Newton|IA
KTNX|37.7988|-116.781|Tonopah|NV
KTOA|33.8034|-118.34|Torrance|CA
KTOB|44.018|-92.8315|Dodge Center|MN
KTOC|34.5938|-83.2958|Toccoa|GA
KTOI|31.8604|-86.0121|Troy|AL
KTOL|41.5868|-83.8078|Toledo|OH
KTOP|39.0699|-95.6226|Topeka|KS
KTOR|42.0645|-104.153|Torrington|WY
KTPA|27.9755|-82.5332|Tampa|FL
KTPF|27.9156|-82.4493|Tampa|FL
KTPH|38.0602|-117.087|Tonopah|NV
KTPL|31.1525|-97.4078|Temple|TX
KTQE|41.7635|-96.1779|Tekamah|NE
KTQH|35.9289|-95.0045|Tahlequah|OK
KTQK|38.4743|-100.885|Scott City|KS
KTRI|36.4752|-82.4074|Blountville|TN
KTRK|39.3186|-120.1406|Truckee|CA
KTRL|32.7092|-96.2674|Terrell|TX
KTRM|33.6267|-116.16|Palm Springs|CA
KTRX|40.0835|-93.5906|Trenton|MO
KTSO|40.5619|-81.0775|Carrollton|OH
KTSP|35.135|-118.439|Tehachapi|CA
KTTA|35.5837|-79.1008|Sanford|NC
KTTD|45.5494|-122.401|Portland|OR
KTTF|41.9399|-83.4347|Monroe|MI
KTTN|40.2767|-74.8135|Ewing Township|NJ
KTTS|28.615|-80.6945|Titusville|FL
KTUL|36.1971|-95.8862|Tulsa|OK
KTUP|34.2681|-88.7699|Tupelo|MS
KTUS|32.115|-110.9381|Tucson|AZ
KTVB|37.1324|-92.084|Cabool|MO
KTVC|44.7414|-85.5822|Traverse City|MI
KTVF|48.0657|-96.185|Thief River Falls|MN
KTVI|30.9016|-83.8813|Thomasville|GA
KTVK|40.6839|-92.901|Centerville|IA
KTVL|38.8939|-119.995|South Lake Tahoe|CA
KTVR|32.3516|-91.0277|Tallulah|LA
KTVY|40.6123|-112.351|Tooele|UT
KTWF|42.4818|-114.488|Twin Falls|ID
KTWM|47.0492|-91.7451|Two Harbors|MN
KTWT|37.5408|-87.9518|Sturgis|KY
KTXK|33.4537|-93.991|Texarkana|AR
KTYL|34.4528|-110.115|Taylor|AZ
KTYQ|40.0307|-86.2514|Indianapolis|IN
KTYR|32.3541|-95.4024|Tyler|TX
KTYS|35.811|-83.994|Knoxville/Maryville|TN
KTZR|39.9012|-83.1369|Columbus|OH
KTZT|41.8788|-92.2846|Belle Plaine|IA
KTZV|36.729|-85.6524|Tompkinsville|KY
KU00|44.6738|-113.353|Leadore|ID
KU01|42.7973|-112.825|American Falls|ID
KU02|43.2092|-112.3496|Blackfoot|ID
KU03|42.5916|-114.797|Buhl|ID
KU05|46.3194|-113.305|Philipsburg|MT
KU06|42.0458|-110.966|Cokeville|WY
KU07|37.5478|-110.7129|Bullfrog|UT
KU08|36.568|-114.443|Overton|NV
KU10|42.1069|-111.913|Preston|ID
KU12|43.9505|-111.6834|St Anthony|ID
KU13|38.2517|-112.2233|Junction|UT
KU14|39.7368|-111.8701|Nephi|UT
KU25|43.5483|-109.69|Dubois|WY
KU30|36.0205|-114.335|Temple Bar|AZ
KU34|38.9614|-110.227|Green River|UT
KU36|42.921|-112.881|Aberdeen|ID
KU42|40.6195|-111.993|Salt Lake City|UT
KU52|38.2307|-112.675|Beaver|UT
KU55|37.8452|-112.392|Panguitch|UT
KU56|43.6426|-111.9292|Rigby|ID
KU58|42.4239|-112.1096|Downey|ID
KU62|43.9041|-113.601|Mackay|ID
KU63|44.4155|-115.317|Stanley|ID
KU68|44.912|-108.4435|Cowley|WY
KU69|40.1919|-110.381|Duchesne|UT
KU70|44.4938|-116.016|Cascade|ID
KU76|43.1313|-115.73|Mountain Home|ID
KU78|42.6416|-111.58|Soda Springs|ID
KU79|45.3733|-115.1982|Warren|ID
KU81|45.2948|-114.9456|Warren|ID
KU82|44.7503|-116.4451|Council|ID
KU89|42.9438|-115.3302|Glenns Ferry|ID
KU96|37.4352|-110.5644|Halls Crossing|UT
KUAO|45.2471|-122.77|Aurora|OR
KUBE|45.506|-91.9801|Cumberland|WI
KUBS|33.4654|-88.3803|Columbus|MS
KUBX|38.0688|-91.4289|Cuba|MO
KUCP|41.0253|-80.4134|New Castle|PA
KUCY|36.3818|-88.9854|Union City|TN
KUDD|33.7485|-116.275|Bermuda Dunes|CA
KUDG|34.4494|-79.8901|Darlington|SC
KUES|43.041|-88.2371|Waukesha|WI
KUGN|42.4222|-87.8679|Chicago/Waukegan|IL
KUIL|47.9366|-124.563|Quillayute|WA
KUIN|39.9427|-91.1946|Quincy|IL
KUKF|36.2228|-81.0983|North Wilkesboro|NC
KUKI|39.126|-123.201|Ukiah|CA
KUKL|38.3025|-95.725|Burlington|KS
KUKT|40.4352|-75.3819|Quakertown|PA
KULM|44.3196|-94.5023|New Ulm|MN
KULS|37.604|-101.374|Ulysses|KS
KUMP|39.9352|-86.045|Indianapolis|IN
KUNI|39.2118|-82.2295|Albany|OH
KUNO|36.8784|-91.9027|Pomona|MO
KUNU|43.4266|-88.7032|Juneau|WI
KUNV|40.8494|-77.8485|State College|PA
KUOS|35.2048|-85.8991|Sewanee|TN
KUOX|34.3843|-89.5368|Oxford|MS
KUSE|41.6101|-84.1272|Wauseon|OH
KUTA|34.685|-90.3478|Tunica|MS
KUTS|30.7469|-95.5872|Huntsville|TX
KUUU|41.5322|-71.281|Newport|RI
KUUV|38.2335|-91.1643|Sullivan|MO
KUVA|29.2113|-99.7436|Uvalde|TX
KUWL|39.8759|-85.3265|New Castle|IN
KUXL|30.1314|-93.3761|Sulphur|LA
KUYF|39.9327|-83.462|London|OH
KUZA|34.9878|-81.0572|Rock Hill|SC
KVAD|30.9678|-83.193|Valdosta|GA
KVAY|39.9429|-74.8457|Lumberton|NJ
KVBG|34.7373|-120.584|Lompoc|CA
KVBT|36.3457|-94.2193|Bentonville|AR
KVCB|38.3768|-121.962|Vacaville|CA
KVCT|28.8526|-96.9185|Victoria|TX
KVCV|34.5975|-117.383|Victorville|CA
KVDF|28.014|-82.3453|Tampa|FL
KVDI|32.1927|-82.3712|Vidalia|GA
KVEL|40.4362|-109.5117|Vernal|UT
KVER|38.9467|-92.6827|Boonville|MO
KVES|40.2044|-84.5319|Versailles|OH
KVGC|42.8438|-75.5614|Hamilton|NY
KVGT|36.2091|-115.194|Las Vegas|NV
KVHN|31.0578|-104.784|Van Horn|TX
KVIH|38.1274|-91.7695|Vichy|MO
KVIQ|44.5581|-90.5122|Neillsville|WI
KVIS|36.3187|-119.393|Visalia|CA
KVJI|36.6871|-82.0333|Abingdon|VA
KVKS|32.2393|-90.9284|Vicksburg|MS
KVLA|38.9915|-89.1662|Vandalia|IL
KVLD|30.7825|-83.2767|Valdosta|GA
KVLL|42.5429|-83.1779|Troy|MI
KVMR|42.7653|-96.9343|Vermillion|SD
KVNC|27.0705|-82.4408|Venice|FL
KVNW|40.8647|-84.6094|Van Wert|OH
KVOK|43.939|-90.2534|Camp Douglas|WI
KVPC|34.1231|-84.8487|Cartersville|GA
KVPS|30.4813|-86.5158|Valparaiso|FL
KVPZ|41.454|-87.0071|Valparaiso|IN
KVQQ|30.2187|-81.8767|Jacksonville|FL
KVRB|27.6556|-80.4179|Vero Beach|FL
KVSF|43.3436|-72.5173|North Springfield|VT
KVTA|40.0247|-82.4618|Newark|OH
KVTI|42.2182|-92.0259|Vinton|IA
KVTN|42.8562|-100.5492|Valentine|NE
KVUJ|35.4167|-80.1508|Albemarle|NC
KVUO|45.6205|-122.656|Vancouver|WA
KVVS|39.9609|-79.6577|Connellsville|PA
KVVV|45.3057|-96.4244|Ortonville|MN
KVWU|48.1541|-94.5169|Waskish|MN
KVYS|41.3519|-89.1531|Peru|IL
KW01|48.7249|-119.4656|Tonasket|WA
KW03|35.7704|-77.9698|Wilson|NC
KW05|39.8412|-77.2747|Gettysburg|PA
KW22|39.0005|-80.2737|Buckhannon|WV
KW28|48.0981|-123.187|Sequim|WA
KW31|36.9602|-78.185|Kenbridge|VA
KW35|39.6926|-78.1661|Berkeley Springs|WV
KW38|36.795|-84.1995|Williamsburg|KY
KW40|35.2214|-78.0387|Mount Olive|NC
KW41|38.0183|-75.8258|Crisfield|MD
KW43|44.6629|-104.568|Hulett|WY
KW45|38.6671|-78.5006|Luray|VA
KW63|36.5958|-78.5601|Clarksville|VA
KW78|36.71|-78.848|South Boston|VA
KW81|37.181|-78.0983|Crewe|VA
KW90|37.2718|-79.3359|Forest|VA
KW91|37.1077|-79.5925|Moneta|VA
KW94|37.3128|-76.6382|Williamsburg|VA
KW95|35.1012|-75.966|Ocracoke|NC
KW96|37.5032|-77.1255|Quinton|VA
KW99|38.9949|-79.1459|Petersburg|WV
KWAL|37.9408|-75.4623|Wallops Island|VA
KWAY|39.9011|-80.1307|Waynesburg|PA
KWBW|41.2972|-75.8512|Wilkes-Barre|PA
KWDG|36.3792|-97.7911|Enid|OK
KWDR|33.9829|-83.6674|Winder|GA
KWEA|32.7463|-97.6824|Weatherford|TX
KWHP|34.2593|-118.413|Pacoima|CA
KWJF|34.7411|-118.219|Lancaster|CA
KWLD|37.1686|-97.0376|Winfield / Arkansas City|KS
KWLW|39.5164|-122.218|Willows|CA
KWMC|40.8966|-117.806|Winnemucca|NV
KWRB|32.6401|-83.5919|Warner Robins|GA
KWRI|40.0156|-74.5917|Wrightstown|NJ
KWRL|43.9657|-107.951|Worland|WY
KWST|41.3496|-71.8034|Westerly|RI
KWVI|36.9357|-121.79|Watsonville|CA
KWVL|44.5332|-69.6755|Waterville|ME
KWWD|39.0085|-74.9083|Wildwood|NJ
KWWR|36.438|-99.5227|Woodward|OK
KWYS|44.6884|-111.118|West Yellowstone|MT
KX04|28.7072|-81.5817|Apopka|FL
KX05|28.3303|-82.4965|Brooksville|FL
KX06|27.1955|-81.8373|Arcadia|FL
KX07|27.8938|-81.6204|Lake Wales|FL
KX10|26.7009|-80.6623|Belle Glade|FL
KX13|29.8422|-84.701|Carrabelle|FL
KX14|26.7442|-81.4326|La Belle|FL
KX21|28.6226|-80.8353|Titusville|FL
KX26|27.8126|-80.4959|Sebastian|FL
KX35|29.0618|-82.3766|Dunnellon|FL
KX39|28.2213|-82.3745|Tampa|FL
KX43|36.0292|-101.829|Sunray|TX
KX49|27.9334|-82.044|Lakeland|FL
KX50|28.9789|-80.9251|New Smyrna Beach|FL
KX51|25.4992|-80.5543|Homestead|FL
KX58|27.0364|-80.4401|Indiantown|FL
KX59|27.962|-80.5598|Malabar|FL
KX60|29.3542|-82.4729|Williston|FL
KXBP|33.1753|-97.8284|Bridgeport|TX
KXFL|29.4674|-81.2063|Palm Coast|FL
KXLL|40.5701|-75.4868|Allentown|PA
KXMR|28.4676|-80.5666|Cocoa Beach|FL
KXNA|36.2819|-94.3068|Fayetteville/Springdale/Rogers|AR
KXNI|35.0607|-108.9376|Zuni|NM
KXNO|33.6094|-81.0833|North|SC
KXSA|37.8596|-76.8941|Tappahannock|VA
KXTA|37.235|-115.811|Groom Lake|NV
KXVG|46.9902|-94.204|Longville|MN
KXWA|48.2609|-103.7512|Williston|ND
KY03|42.8814|-97.901|Springfield|SD
KY14|43.4552|-96.8014|Tea|SD
KY19|46.7682|-100.894|Mandan|ND
KY23|45.3062|-91.6362|Chetek|WI
KY27|46.0664|-100.635|Fort Yates|ND
KY31|44.2448|-84.1798|West Branch|MI
KY37|48.394|-97.7808|Park River|ND
KY43|41.44|-94.77|Anita|IA
KY47|42.5031|-83.6237|New Hudson|MI
KY50|44.0416|-89.3045|Wautoma|WI
KY51|43.5801|-90.9002|Viroqua|WI
KY55|45.5221|-88.9251|Crandon|WI
KY65|45.4062|-84.5991|Indian River|MI
KY70|42.938|-85.0605|Ionia|MI
KY72|43.975|-90.4835|Tomah|WI
KY74|47.9364|-102.142|Parshall|ND
KY83|43.457|-82.841|Sandusky|MI
KY89|44.7252|-85.2031|Kalkaska|MI
KY93|44.9969|-84.1267|Atlanta|MI
KY94|45.131|-85.1078|East Jordan|MI
KY95|45.0832|-83.9388|Hillman|MI
KYIP|42.2379|-83.5304|Detroit|MI
KYKM|46.5682|-120.544|Yakima|WA
KYKN|42.9167|-97.3859|Yankton|SD
KYNG|41.2607|-80.6791|Youngstown/Warren|OH
KZ01|30.6337|-86.7427|Valparaiso|FL
KZ10|47.0217|-122.446|Spanaway|WA
KZ98|42.8172|-85.9281|Zeeland|MI
KZEF|36.28|-80.7861|Elkin|NC
KZER|40.7065|-76.3731|Pottsville|PA
KZPH|28.2282|-82.1559|Zephyrhills|FL
KZZV|39.9444|-81.8921|Zanesville|OH
`;

// ICAO -> { lat, lon, city, state }. Built once.
// ⚠⚠ globalThis.Map, NOT Map. THE BUILT-IN IS SHADOWED IN THIS FILE by the
// react-map-gl default import at the top. A bare Map constructor here builds
// the MAP COMPONENT — a forwardRef object, not a function — and throws
// "X is not a constructor" at MODULE SCOPE, which white-screens the ENTIRE
// application, not just this tab. vite reports a clean build; the identifier
// resolves fine and is only wrong at runtime.
// ⚠ The header comment at the top of this file already warned about exactly
// this and named the sweep to re-run. It was written unqualified regardless,
// and it took the site down.
const AIRPORT_DB = (() => {
  const m = new globalThis.Map();
  for (const line of _AIRPORT_TABLE.split("\n")) {
    if (!line) continue;
    const [icao, lat, lon, city, state] = line.split("|");
    const la = Number(lat), lo = Number(lon);
    if (!icao || !Number.isFinite(la) || !Number.isFinite(lo)) continue;
    m.set(icao, { lat: la, lon: lo, city: city || "", state: state || "" });
  }
  return m;
})();

/** [lat, lon] for any ICAO the map can place, or undefined.
 *  ⚠ The 12 monitored fields WIN — their coordinates are hand-verified against
 *  the backend geo-fence (KADS is 32.9686, not the 33.0564 a dataset once gave
 *  us, which is ~10km into the wrong suburb). */
function airportPoint(icao) {
  const c = String(icao || "").toUpperCase();
  if (!c) return undefined;
  if (AIRPORT_COORDS[c]) return AIRPORT_COORDS[c];
  const hit = AIRPORT_DB.get(c);
  return hit ? [hit.lat, hit.lon] : undefined;
}

/** [lon, lat] for a Mapbox camera, from the [lat, lon] the tables hold.
 *
 * ⚠⚠ AIRPORT_COORDS AND airportPoint() RETURN [lat, lon]. flyTo TAKES [lon, lat].
 * THESE ARE NOT THE SAME AND MAPBOX WILL NOT TELL YOU WHEN YOU GET IT WRONG.
 * KTEB is [40.8501, -74.0608]; handed to flyTo unswapped that is latitude
 * -74.06, which is off the bottom of the world. There is no error, no warning
 * and no thrown exception — the camera silently clamps and the only symptom is
 * a map that flew somewhere absurd, which reads as a broken click rather than a
 * reversed pair. Every flyTo in this file goes through this function so the
 * swap exists in exactly one place.
 *
 * ⚠⚠ AND THE RANGE TEST BELOW DOES NOT CATCH A DROPPED SWAP — measured, not
 * assumed. It validates the pair coming OUT of the table, so corrupt data
 * cannot move the camera; it cannot see what the caller does with the result.
 * Worse, a reversed pair is often still a VALID coordinate: KTEB reversed is
 * lon 40.85 / lat -74.06, which is a real place in the Southern Ocean, and
 * Mapbox will fly there without a murmur. Only 7 of the 12 fields have a
 * longitude past ±90 that would make a reversal detectable at all, so the bug
 * would be silent at TEB, HPN, BED, OPF and PBI and loud everywhere else —
 * intermittent by airport, which is the worst way for it to fail.
 * ⚠ The real protection is the harness, which asserts center[0] === lon and
 * center[1] === lat for all twelve. Keep it that way.
 */
function airportCenter(icao) {
  const p = airportPoint(icao);
  if (!p) return undefined;
  const lat = Number(p[0]);
  const lon = Number(p[1]);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return undefined;
  if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return undefined;
  return [lon, lat];
}

/** "TEB" -> "KTEB", or an ICAO passed straight through. "" when unresolvable.
 *
 * ⚠⚠ "K" + IATA HOLDS WITHOUT EXCEPTION FOR THE 12 MONITORED FIELDS ONLY. IT IS
 * NOT A GENERAL RULE. Plenty of US airports break it outright — Kona is PHKO,
 * Anchorage is PANC — and outside the US it means nothing at all. What makes it
 * safe here is the source of the data, not the pattern: a flight event's
 * `airport` field is produced by the backend from ICAO_TO_IATA over exactly the
 * 12 monitored fields, so it can never carry a code the rule does not cover.
 * ⚠ The AIRPORT_COORDS membership test is what enforces that. A code outside
 * the 12 returns "" rather than a confidently-wrong "K"-prefixed guess.
 */
function monitoredIcao(code) {
  const c = String(code || "").trim().toUpperCase();
  if (!c) return "";
  if (AIRPORT_COORDS[c]) return c;                 // already an ICAO we hold
  return AIRPORT_COORDS["K" + c] ? "K" + c : "";   // IATA for one of the 12
}

/** "KASE — Aspen, CO" when known, else "KASE". Never invents a name. */
function airportDescribe(icao) {
  const c = String(icao || "").toUpperCase();
  if (!c) return "—";
  const name = AIRPORT_NAMES[c];
  if (name) return `${c} — ${name}`;
  const hit = AIRPORT_DB.get(c);
  if (hit && hit.city) {
    return hit.state ? `${c} — ${hit.city}, ${hit.state}` : `${c} — ${hit.city}`;
  }
  return c;
}

const AIRPORT_COORDS = {
  KTEB: [40.8501, -74.0608],   // Teterboro NJ
  KHPN: [41.0670, -73.7076],   // Westchester NY
  KBED: [42.4700, -71.2896],   // Boston Hanscom MA
  KOPF: [25.9069, -80.2784],   // Miami Opa-locka FL
  KADS: [32.9686, -96.8364],   // Dallas Addison TX
  KSDL: [33.6229, -111.9117],  // Scottsdale AZ
  KVNY: [34.2098, -118.4899],  // Van Nuys CA
  KPBI: [26.6832, -80.0956],   // Palm Beach FL
  KSJC: [37.3626, -121.9290],  // San Jose CA
  KBUR: [34.2007, -118.3585],  // Burbank CA
  KHOU: [29.6454, -95.2789],   // Houston Hobby TX
  KDAL: [32.8471, -96.8517],   // Dallas Love Field TX
};

// ⚠ Display names for the 12 monitored fields, hardcoded and NOT fetched. An
// airport name is static data; a lookup would be a network round trip to learn
// something that has not changed since the runway was built.
// ⚠ Anything outside these 12 shows its ICAO code and nothing else — inventing
// or fetching a name for an unmonitored field is not worth a request, and a
// wrong name is worse than a code the reader can look up.
const AIRPORT_NAMES = {
  KTEB: "Teterboro",
  KHPN: "Westchester",
  KBED: "Hanscom Field",
  KOPF: "Opa-locka",
  KADS: "Addison",
  KSDL: "Scottsdale",
  KVNY: "Van Nuys",
  KPBI: "Palm Beach",
  KSJC: "San Jose",
  KBUR: "Burbank",
  KHOU: "Houston Hobby",
  KDAL: "Dallas Love Field",
};

/** "KTEB (Teterboro)" for a monitored field, bare "CYYZ" for anything else. */
function airportLabel(icao) {
  const code = String(icao || "").trim().toUpperCase();
  if (!code) return "—";
  const name = AIRPORT_NAMES[code];
  return name ? `${code} (${name})` : code;
}

// ⚠ N-number shape, matching the backend's _N_NUMBER exactly. Used to decide
// whether a lazy registry lookup is worth firing: an operator callsign like
// EJA504 will never be in the FAA registry under that string, so firing for one
// would be a guaranteed miss and a wasted request.
const N_NUMBER_RE = /^N[1-9][0-9A-Z]{0,4}$/;

// Phase colours. Shared by the arc segments and the marker box so a flight reads
// the same in both places.
const PHASE_TAKEOFF = "#FFD700";  // yellow
const PHASE_CRUISE = "#1E90FF";   // dodger blue
const PHASE_LANDING = "#00FF87";  // platform green
const PHASE_COLOR = { takeoff: PHASE_TAKEOFF, cruise: PHASE_CRUISE, landing: PHASE_LANDING };
const PHASE_LABEL = { takeoff: "CLIMBING", cruise: "CRUISE", landing: "DESCENDING" };

const _toRad = (d) => (d * Math.PI) / 180;
const _toDeg = (r) => (r * 180) / Math.PI;

/** Point at fraction f along the great circle from (lat1,lon1) to (lat2,lon2).
 *  Returns [lon, lat] — GeoJSON order, not the lat/lon order of the inputs.
 *
 *  ⚠ Spherical interpolation, not a lerp of the coordinates. A straight line in
 *  lat/lon space is not the path an aircraft flies and diverges badly on the
 *  long transcontinental legs this map is mostly showing (TEB↔VNY is ~3,900km).
 */
function gcPoint(lat1, lon1, lat2, lon2, f) {
  const p1 = _toRad(lat1), l1 = _toRad(lon1);
  const p2 = _toRad(lat2), l2 = _toRad(lon2);
  const dp = p2 - p1, dl = l2 - l1;
  const a = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  const d = 2 * Math.asin(Math.min(1, Math.sqrt(a)));   // angular distance
  // ⚠ Coincident points: sin(d) is 0 and the interpolation divides by it.
  if (d === 0 || !Number.isFinite(d)) return [lon1, lat1];
  const A = Math.sin((1 - f) * d) / Math.sin(d);
  const B = Math.sin(f * d) / Math.sin(d);
  const x = A * Math.cos(p1) * Math.cos(l1) + B * Math.cos(p2) * Math.cos(l2);
  const y = A * Math.cos(p1) * Math.sin(l1) + B * Math.cos(p2) * Math.sin(l2);
  const z = A * Math.sin(p1) + B * Math.sin(p2);
  return [_toDeg(Math.atan2(y, x)), _toDeg(Math.atan2(z, Math.hypot(x, y)))];
}

/** Initial great-circle bearing in degrees (0 = north, clockwise). */
function gcBearing(lat1, lon1, lat2, lon2) {
  const p1 = _toRad(lat1), p2 = _toRad(lat2), dl = _toRad(lon2 - lon1);
  const y = Math.sin(dl) * Math.cos(p2);
  const x = Math.cos(p1) * Math.sin(p2) - Math.sin(p1) * Math.cos(p2) * Math.cos(dl);
  return (_toDeg(Math.atan2(y, x)) + 360) % 360;
}

/** progress → phase key. Thresholds match the arc segment split. */
function phaseOf(progress) {
  if (progress < 0.10) return "takeoff";
  if (progress > 0.90) return "landing";
  return "cruise";
}

/** Great-circle distance in NAUTICAL MILES between two [lat, lon] points.
 *
 *  Pure, module-level, no side effects. Nautical miles because the only consumer
 *  divides by a speed in KNOTS, and knots are nautical miles per hour — doing it
 *  in km would mean a unit conversion at the call site, which is where that kind
 *  of bug lives.
 *  ⚠ 3440.065 is the Earth's mean radius in nautical miles. gcPoint computes
 *  the same angular distance internally but consumes it for interpolation and
 *  never returns it, so this is a second derivation rather than a refactor of a
 *  function the whole map depends on.
 */
function haversineNm(lat1, lon1, lat2, lon2) {
  const p1 = _toRad(lat1), p2 = _toRad(lat2);
  const dp = _toRad(lat2 - lat1), dl = _toRad(lon2 - lon1);
  const a = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  return 3440.065 * 2 * Math.asin(Math.min(1, Math.sqrt(a)));
}

// Typical cruise speeds in knots by airframe tier, for arrival estimation only.
//
// ⚠⚠ HONEST ESTIMATES, NOT MEASUREMENTS. No wind, no climb or descent profile,
// no step climb. On a short leg the descent phase alone makes these optimistic
// by several minutes. That inaccuracy is the whole reason an estimated position
// renders dimmed — the number is a reasonable guess and the UI says so.
// ⚠ The key is the tier NUMBER. aircraft_tier arrives off a Redis hash, so the
// payload carries the STRING "1", and an === against 1 would miss every time and
// fall to the default silently. _cruiseKts coerces before the lookup.
const TIER_CRUISE_KTS = { 1: 550, 2: 490, 3: 430 };
const DEFAULT_CRUISE_KTS = 460;

function _cruiseKts(tier) {
  const t = Number(tier);
  return TIER_CRUISE_KTS[t] || DEFAULT_CRUISE_KTS;
}

/** Build the cached geometry for one flight, or null if it cannot be drawn.
 *
 *  ⚠⚠ CALLED ONCE PER FETCH, NEVER PER RENDER. The 21-point geodesic and its
 *  three segments are pure functions of the two airports, so recomputing them
 *  on every render — or worse, on every 60s position tick — would be ~1,300
 *  trig calls a minute for geometry that has not changed.
 */
function buildFlightArc(f) {
  // ⚠⚠ airportPoint(), NOT AIRPORT_COORDS. Against 100 live flights only TWO
  // had both endpoints among the 12 monitored fields — the map drew 2 arcs and
  // 98 dots, and animated exactly one aircraft. The broader lookup resolves the
  // other end for 88% of them, which is the whole reason the route is drawable.
  const dep = airportPoint(f?.departure_icao);
  const arr = airportPoint(f?.arrival_icao);

  // ⚠⚠ departure_time IS ABSENT ON ~30% OF SWIM MESSAGES. last_updated (SWIM
  // message receipt time) is used as a proxy — close enough for arc animation,
  // may show slightly early departure on the arc. Without it flightPosition
  // returns null and the flight renders as a route with NO AIRCRAFT ON IT.
  const depRaw = f?.departure_time || f?.last_updated || "";
  const depMs = Date.parse(depRaw);
  const filedArrMs = Date.parse(f?.arrival_time_est || "");
  const depEstimated = !f?.departure_time && !!f?.last_updated;

  // ⚠⚠ arrival_time_est IS ABSENT ON ~55% OF SWIM MESSAGES, and unlike
  // departure_time it had no fallback at all — flightPosition bails on its first
  // line when arrMs is not finite, so those flights drew a route with NO
  // AIRCRAFT ON IT. Measured on 685 live flights: 253 animated on a real arc;
  // deriving the arrival takes that to 585.
  // ⚠ NEVER OVERRIDES A FILED TIME. The estimate runs only when SWIM gave us
  // nothing, so a real arrival_time_est always wins and this branch cannot move
  // an aircraft the feed already placed in time.
  // ⚠ Needs BOTH endpoints — there is no distance without two coordinates — so
  // a one-endpoint flight keeps NaN and stays a dot. That is deliberate: a dot
  // says "departed here", and inventing a duration to nowhere would not.
  let arrMs = filedArrMs;
  let arrEstimated = false;
  if (!Number.isFinite(arrMs) && Number.isFinite(depMs) && dep && arr) {
    const nm = haversineNm(dep[0], dep[1], arr[0], arr[1]);
    const kts = _cruiseKts(f?.aircraft_tier);
    if (nm > 0 && kts > 0) {
      arrMs = depMs + (nm / kts) * 3600 * 1000;
      arrEstimated = true;
    }
  }

  // ⚠ Neither endpoint placeable. Now genuinely rare — the backend only captures
  // a flight that touches the geo-fence, so one end is monitored by
  // construction, and this fires only when the OTHER end is unknown AND the
  // monitored one somehow failed to resolve.
  if (!dep && !arr) return null;
  // ⚠ Still a dot when one end cannot be placed at all — international, almost
  // always (CYUL, MBPV, TXKF, SKCL). Drawing a line toward a bearing guessed
  // from the ICAO prefix would put the leg over open ocean and assert a
  // direction the data does not carry. A dot says "departed here"; a wrong arc
  // says "went there".
  if (!dep || !arr) {
    const at = dep || arr;
    return {
      kind: "dot",
      key: String(f.callsign || ""),
      flight: f,
      lon: at[1],
      lat: at[0],
      depMs, arrMs, depEstimated, arrEstimated,
      unusual: looksUnusual(f),
    };
  }

  const pts = [];
  for (let i = 0; i <= ARC_STEPS; i++) {
    pts.push(gcPoint(dep[0], dep[1], arr[0], arr[1], i / ARC_STEPS));
  }
  // ⚠ Segments SHARE their boundary points (2 and 18), so the three coloured
  // pieces butt together with no gap at the joins.
  const cut1 = Math.round(ARC_STEPS * 0.10);
  const cut2 = Math.round(ARC_STEPS * 0.90);
  return {
    kind: "arc",
    key: String(f.callsign || ""),
    flight: f,
    pts,
    depMs, arrMs, depEstimated, arrEstimated,
    unusual: looksUnusual(f),
    segments: [
      { phase: "takeoff", coords: pts.slice(0, cut1 + 1) },
      { phase: "cruise", coords: pts.slice(cut1, cut2 + 1) },
      { phase: "landing", coords: pts.slice(cut2) },
    ],
  };
}

/** Position, bearing and phase for a flight at time `now`. null = no icon.
 *
 *  ⚠ Computed FRESH on every tick and never stored — the bearing especially,
 *  which is the tangent at the current point and changes continuously along a
 *  great circle. A stored heading would be right only at the moment it was
 *  taken.
 */
function flightPosition(arc, now) {
  if (!arc || !Number.isFinite(arc.depMs) || !Number.isFinite(arc.arrMs)) return null;
  const total = arc.arrMs - arc.depMs;
  if (!(total > 0)) return null;
  const raw = (now - arc.depMs) / total;
  // ⚠ NOT YET DEPARTED. The arc is drawn (the route is known and real) but no
  // icon: placing one at the origin would show an aircraft in flight that has
  // not left the ground.
  if (raw < 0) return { notDeparted: true, progress: raw };
  // ⚠⚠ CLAMPED, NOT DISCARDED. Past its estimated arrival the aircraft holds at
  // the destination rather than disappearing, and stays until Redis expires the
  // key at 8 hours. Someone opening the map after a flight landed still sees
  // WHERE IT WENT, which is the intelligence — the arrival is the signal, not
  // the aeroplane in transit.
  const progress = Math.min(1, raw);
  const landed = raw > 1;

  if (arc.kind === "dot") {
    return { lon: arc.lon, lat: arc.lat, bearing: null, progress, landed, phase: phaseOf(progress) };
  }
  const [lon, lat] = interpolateOnArc(arc, progress);
  // ⚠ Bearing is taken toward a point slightly FURTHER ALONG the arc, so the
  // nose points at where the aircraft is going. At progress 1 the look-ahead
  // clamps onto the same point, which is why the equality check exists — a
  // bearing between two identical points is meaningless, not zero.
  const ahead = interpolateOnArc(arc, Math.min(1, progress + 0.01));
  const bearing = (lon === ahead[0] && lat === ahead[1])
    ? gcBearing(arc.pts[arc.pts.length - 2][1], arc.pts[arc.pts.length - 2][0],
                arc.pts[arc.pts.length - 1][1], arc.pts[arc.pts.length - 1][0])
    : gcBearing(lat, lon, ahead[1], ahead[0]);
  return { lon, lat, bearing, progress, landed, phase: phaseOf(progress) };
}

/** [lon, lat] at fraction f of a built arc, interpolating between cached pts. */
function interpolateOnArc(arc, f) {
  const n = arc.pts.length - 1;
  const x = Math.max(0, Math.min(n, f * n));
  const i = Math.min(n - 1, Math.floor(x));
  const t = x - i;
  const a = arc.pts[i], b = arc.pts[i + 1];
  // Linear between two adjacent geodesic samples — they are ~1/20th of the leg
  // apart, so the error against the true curve is negligible at map scale.
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
}

// ⚠⚠ C.amber IS #00FF87 — EMERALD, NOT AMBER. Using it for departures beside
// C.green arrivals renders two near-identical greens and the direction cue
// disappears. C.orange (#f97316) is the token that actually reads amber. Same
// class as the C.violet / C.emerald traps: the token name is not the colour.
const EVENT_STYLE = {
  arrival:            { arrow: "\u2193",  color: C.green,  label: "ARRIVAL" },
  departure:          { arrow: "\u2191",  color: C.orange, label: "DEPARTURE" },
  // ⚠ The tilde IS the inferred marker — detected between polls, so the
  // timestamp is detection time and not wheels-up.
  departure_inferred: { arrow: "\u2191~", color: C.orange, label: "DEPARTURE · INFERRED" },
};

// ⚠ Built ONCE at module scope. Constructing an Intl.DateTimeFormat is the
// expensive part, and this list re-renders on every 5-minute poll.
const ET_HHMM_FMT = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/New_York",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

// ⚠⚠ Event timestamps are epoch SECONDS (int(time.time()) server-side) while JS
// Date takes MILLISECONDS. Without the x1000 every card reads 00:00 on 1 January
// 1970 — a plausible-looking clock that is wrong by 55 years.
function etHhmm(tsSeconds) {
  // ⚠⚠ typeof, NOT Number(). Number(null) and Number("") are BOTH 0, and 0 is
  // finite — so a coercion-only guard renders a missing timestamp as a confident
  // "19:00 ET" (epoch 0). A wrong clock reads as data; a blank reads as a gap.
  if (typeof tsSeconds !== "number" || !Number.isFinite(tsSeconds)) return "";
  try { return ET_HHMM_FMT.format(new Date(tsSeconds * 1000)); } catch { return ""; }
}

// "2h 30m" — ground dwell before a departure. Returns "" for missing or zero so
// the row omits the line entirely rather than showing a meaningless "0m".
function dwellLabel(seconds) {
  const n = Number(seconds);
  if (!Number.isFinite(n) || n <= 0) return "";
  const h = Math.floor(n / 3600);
  const m = Math.floor((n % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

// Terminal-dark popup styling (scoped class; injected in-component).
const POPUP_CSS = `
.qs-pi-popup .mapboxgl-popup-content {
  background: ${C.bg}; border: 1px solid ${C.border};
  border-radius: 3px; padding: 8px 10px; box-shadow: 0 2px 14px rgba(0,0,0,0.65);
}
.qs-pi-popup .mapboxgl-popup-tip {
  border-top-color: ${C.bg} !important; border-bottom-color: ${C.bg} !important;
}
.qs-pi-popup .mapboxgl-popup-close-button { color: ${C.textDim}; font-size: 15px; padding: 0 5px; }
`;

const LABEL_STYLE = {
  fontSize: 11, fontFamily: font, color: "rgba(255,255,255,0.65)",
  background: "rgba(0,0,0,0.5)", padding: "2px 6px", borderRadius: 3,
  whiteSpace: "nowrap", pointerEvents: "none", textShadow: "0 0 4px #000",
};

function loadLayers() {
  // ⚠ flights defaults TRUE. The spec asked for "on when SWIM data exists, off
  // when it does not", but layers load from localStorage before any fetch has
  // happened, so a data-aware default is not knowable here. With no flights the
  // layer renders nothing either way, so defaulting on gives the same picture
  // and means the first arcs appear without the user hunting for a pill.
  const def = { vessels: true, thermal: true, aircraft: false, ports: true, flights: true };
  try {
    const saved = JSON.parse(localStorage.getItem(LAYERS_KEY));
    if (saved && typeof saved === "object") return { ...def, ...saved };
  } catch { /* ignore */ }
  return def;
}

// ⚠⚠ HIDDEN, NOT REMOVED — MIL and CARGO removed from ADS-B collection Aug 2026.
// Bizjet-only architecture. All toggles hidden pending revenue/API upgrade.
// Restore when collection expands.
// Aircraft sub-type preference (default ON). Stored as "true"/"false" strings.
// ⚠ Deliberately NOT read for the bizjet filter any more. A stored
// qs_ac_bizjet:"false" from before the toggles were hidden would survive here and
// permanently empty both the markers and the list with no visible control to undo
// it. The keys are left in localStorage untouched so restoring is uncommenting.
// TO RESTORE: uncomment, then re-wire the state at the sub-type block below.
// function loadAcPref(key) {
//   try {
//     const v = localStorage.getItem(key);
//     return v === null ? true : v === "true";
//   } catch { return true; }
// }

async function fetchJSON(path) {
  const res = await backendFetch(path, path);
  if (!res.ok) throw new Error(String(res.status));
  return res.json();
}


const DEST_REFRESH_MS = 300_000;
const DEST_CARD_CAP = 10;

// ⚠⚠ THE NORTH DAKOTA SIGNAL. Everything else on this map answers "what is
// flying"; this answers "where did it go that it does not usually go". The
// endpoint already orders by visit count then recency, so a fourth trip to the
// same strip outranks a first sighting rather than being buried under it.
// ⚠ onFocus / selectedId are OPTIONAL and the card is inert without them —
// same opt-in-by-callback shape the rest of this codebase uses. This component
// is module-level and has no access to the map, so the parent owns both the
// camera and the selection state and hands down only what it wants used.
function DestinationSignals({ onFocus, selectedId }) {
  const [rows, setRows] = useState([]);

  useEffect(() => {
    let dead = false;
    const pull = async () => {
      try {
        const p = "/api/altdata/swim/destinations/unusual";
        const r = await backendFetch(p, p);
        if (!r.ok || dead) return;
        const d = await r.json();
        if (!dead) setRows(Array.isArray(d?.destinations) ? d.destinations : []);
      } catch { /* an empty feed is a fine outcome */ }
    };
    pull();
    const id = setInterval(pull, DEST_REFRESH_MS);
    return () => { dead = true; clearInterval(id); };
  }, []);

  const shown = rows.slice(0, DEST_CARD_CAP);
  return (
    <div style={{ marginTop: 18 }}>
      <div style={{ fontSize: 11, color: C.textMid, letterSpacing: "0.12em" }}>
        UNUSUAL DESTINATIONS
      </div>
      <div style={{ fontSize: 11, color: C.textDim, margin: "2px 0 8px" }}>
        Flights to non-commercial airports from monitored hubs
      </div>
      {shown.length === 0 ? (
        <div style={{ ...s.panel, padding: 14, color: C.textDim, lineHeight: 1.7 }}>
          No unusual destinations recorded in the last 7 days.
        </div>
      ) : shown.map((d) => {
        // ⚠ Number(null) is 0 and 0 is a REAL score, so the null test has to come
        // first and separately. Coercing would paint every legacy row as a
        // confident zero, which is the opposite of "not assessed".
        const scored = d.signal_score !== null && d.signal_score !== undefined;
        const score = scored ? Number(d.signal_score) : null;
        return (
        <div
          key={d.id}
          onClick={onFocus ? () => onFocus(d) : undefined}
          title={onFocus ? "Show the departure field on the map" : undefined}
          style={{
            ...s.panel, padding: 10, marginBottom: 6,
            cursor: onFocus ? "pointer" : "default",
            // ⚠⚠ LEGACY ROWS ARE DIMMED, NOT HIDDEN. Every unscored row here
            // predates the scoring deploy on 2026-09-04; they are still real
            // sightings and still worth reading, but they carry no assessment
            // and should not look as authoritative as one that does. They fall
            // out of this endpoint entirely once the 7-day window clears them.
            ...(scored ? {} : { opacity: 0.7 }),
            // ⚠ SPREAD CONDITIONALLY, never `background: sel ? x : undefined`.
            // An explicit undefined would overwrite s.panel's own background
            // with nothing and strip the card's surface on every unselected
            // row — the key has to be absent, not present and empty.
            ...(selectedId != null && selectedId === d.id
              ? { background: "rgba(255,255,255,0.08)" }
              : {}),
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between",
                        gap: 8, flexWrap: "wrap" }}>
            <div style={{ fontWeight: 700, flex: 1, minWidth: 160 }}>
              {d.faa_owner || d.registration || d.owner_key || "Unknown operator"}
            </div>
            <span style={{ display: "flex", alignItems: "center", gap: 6,
                           flexWrap: "wrap" }}>
              {/* ⚠ Absent, not zero-badged, when the row was never scored. A "0"
                  pill would state an assessment that was never made. */}
              {scored && (
                <span
                  title={`Destination signal score ${score} of 10`}
                  style={{
                    fontSize: 9, fontWeight: 700, color: scoreColor(score),
                    border: `1px solid ${scoreColor(score)}`, borderRadius: 10,
                    padding: "1px 7px", whiteSpace: "nowrap",
                  }}
                >
                  {score}
                </span>
              )}
              <span style={{
                fontSize: 9, fontWeight: 700, color: UNUSUAL_ARC_COLOR,
                border: `1px solid ${UNUSUAL_ARC_COLOR}`, borderRadius: 10,
                padding: "1px 7px", whiteSpace: "nowrap",
              }}>
                {UNUSUAL_LABEL[d.unusual_reason] || "UNUSUAL"}
              </span>
            </span>
          </div>
          <div style={{ fontSize: 12, color: C.textMid, marginTop: 3 }}>
            {/* The arrow sits in an EXPRESSION, not in JSX text, and it has to.
                JSX text does not process backslash escapes: a bare u2192 escape
                between tags renders as its six literal characters, so this line
                read "KTEB u2192 KFOK" on screen until it was wrapped. Inside a
                JS string the escape IS processed, which is why every other
                unicode in this file works -- they all live in string literals
                (EVENT_STYLE arrows, the LIVE/LOG labels, the middot in the
                airport name). Do not paste a raw arrow character here to fix
                it: the editor writes it back out as the escape and the bug
                returns looking like it was never touched. */}
            {airportLabel(d.departure_icao)} {"\u2192"}{" "}
            <span style={{ color: C.text }}>{d.arrival_icao}</span>{" "}
            {destWhere(d) && <span style={{ color: C.textDim }}>{destWhere(d)}</span>}
          </div>
          <div style={{ fontSize: 10, color: C.textDim, marginTop: 2 }}>
            {destAgo(d.created_at)}
            {d.arrival_airport_name ? ` \u00b7 ${d.arrival_airport_name}` : ""}
          </div>
          {/* ⚠⚠ CONTEXT NOW, NOT THE RANKING. visit_count used to decide what
              surfaced, which handed the whole endpoint to one operator flying
              one route. It is still worth reading — a second visit is the
              difference between a trip and a pattern — so it drops to a dim
              line at >= 2 rather than a gold headline at >= 3.
              ⚠ "in 7 days", NOT 90. The count is a window function over this
              query's own 7-day WHERE clause. The 90-day figure belongs to
              _check_destination_pattern in the listener, which is a different
              measurement; the old copy stated it as though they were the same. */}
          {d.visit_count >= 2 && (
            <div style={{ fontSize: 10, color: C.textDim, marginTop: 3 }}>
              {`${d.visit_count} visits in 7 days`}
            </div>
          )}
        </div>
        );
      })}
    </div>
  );
}

export function PhysicalIntelTab({ keys, onData, globalSignals }) {
  const [tankers,     setTankers]     = useState([]);
  // Region manifest from /api/altdata/tankers — see the note above the imports.
  const [regions,     setRegions]     = useState([]);
  // ⚠ Placeholder payloads never reach these lists (liveRows in load()), so the
  // old thermalRep / aircraftRep flags and the "(mock)" count suffix are gone.
  const [thermalList, setThermalList] = useState([]);
  const [aircraftList, setAircraftList] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [refreshing,  setRefreshing]  = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [layers,      setLayers]      = useState(loadLayers);
  const [popup,       setPopup]       = useState(null); // { kind, d }
  const [narrow,      setNarrow]      = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 640px)").matches
  );
  // ⚠⚠ HIDDEN, NOT REMOVED — MIL and CARGO removed from ADS-B collection Aug 2026.
  // Bizjet-only architecture. All toggles hidden pending revenue/API upgrade.
  // Restore when collection expands.
  // TO RESTORE: uncomment these three alongside loadAcPref, toggleAcType,
  // subToggleBtn and the pill row, then restore the sub-type clause in the
  // aircraft filter below.
  // const [showMilitary, setShowMilitary] = useState(() => loadAcPref("qs_ac_military"));
  // const [showCargo,    setShowCargo]    = useState(() => loadAcPref("qs_ac_cargo"));
  // const [showBizjet,   setShowBizjet]   = useState(() => loadAcPref("qs_ac_bizjet"));
  // ── SWIM flight paths ──
  const [swimFlights, setSwimFlights] = useState([]);
  // ⚠ A CLOCK, not a counter. Every 60s this becomes the new Date.now(), which
  // is the only input the position memo needs — the arcs themselves do not move.
  const [flightTick,  setFlightTick]  = useState(() => Date.now());
  // ⚠ Lazy owner lookups, keyed by N-number: { status, owner, make, model }.
  // Component-lifetime cache so re-opening the same popup never re-fetches —
  // the backend caches for 7 days, but a request per popup open would still be
  // a request per popup open.
  const [ownerCache, setOwnerCache] = useState({});
  // ⚠⚠ ON DEMAND ONLY. Keyed by callsign, written solely by the Look-up button.
  // Nothing here runs on map load or on popup open — of 380 live flights only
  // eight are truly unknown, so an automatic sweep would be 380 requests to
  // improve eight rows.
  const [enrichCache, setEnrichCache] = useState({});
  const [showList,     setShowList]     = useState(false);  // aircraft list panel (default collapsed)
  // ⚠ Separate from showList on purpose — see the pill comment. Collapsed by
  // default so the map is the first thing seen, matching the aircraft panel.
  const [showVessels,  setShowVessels]  = useState(false);
  // Region groups start OPEN: a collapsed-by-default list would open on an
  // empty-looking panel and read as "no vessels".
  const [closedRegions, setClosedRegions] = useState(() => new Set());
  const [selectedAc,   setSelectedAc]   = useState(null);   // highlighted list row (icao24/callsign)
  // ⚠ SEPARATE from selectedAc on purpose. That one is keyed on icao24/callsign
  // and is shared with the map markers; reusing it for these two lists would
  // make a click in one highlight an unrelated row in another.
  const [selectedEvt,  setSelectedEvt]  = useState(null);   // flight-log row key
  const [selectedDest, setSelectedDest] = useState(null);   // destination card id
  // ⚠ A THIRD, SEPARATE highlight key, for the same reason selectedEvt is
  // separate from selectedAc: a SWIM row and an ADS-B row can carry the same
  // callsign, and one shared key would light both.
  const [selectedSwim, setSelectedSwim] = useState(null);   // SWIM row callsign
  const swimTimer = useRef(null);  // clears the SWIM row highlight
  const mapRef   = useRef(null);   // react-map-gl instance → flyTo on row click
  // ⚠ The map is UNCONTROLLED (initialViewState, not viewState), so no zoom
  // value existed anywhere in React. This tracks it for marker sizing only —
  // the map still owns its own camera and nothing here drives it.
  const [zoom, setZoom] = useState(MAP_INITIAL_ZOOM);
  const selTimer = useRef(null);   // 30s auto-clear for the row highlight
  const evtTimer = useRef(null);   // …for the flight-log row
  const destTimer = useRef(null);  // …for the destination card
  const listRef  = useRef(null);   // aircraft list panel → scroll into view on open

  // Keep onData current without re-creating the fetch loop (stable interval).
  const onDataRef = useRef(onData);
  useEffect(() => { onDataRef.current = onData; }, [onData]);

  // Clear the row-highlight timer on unmount.
  useEffect(() => () => {
    if (selTimer.current) clearTimeout(selTimer.current);
    if (evtTimer.current) clearTimeout(evtTimer.current);
    if (destTimer.current) clearTimeout(destTimer.current);
  }, []);

  // ⚠ The list panel is a sibling BELOW a calc(100vh - 120px) map, so opening it
  // changes nothing inside the viewport — it reads as "the list never populated".
  // Scroll it into view on open. This must be an effect, not part of the button's
  // onClick: the panel is not mounted until after the showList state commit, so a
  // ref read in the handler is still null on the first open.
  useEffect(() => {
    if (!showList) return;
    listRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [showList]);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 640px)");
    const h = (e) => setNarrow(e.matches);
    mq.addEventListener?.("change", h);
    return () => mq.removeEventListener?.("change", h);
  }, []);

  // ── Fetch both endpoints in parallel; never throws to the caller ──
  const load = useCallback(async (force = false) => {
    const q = force ? "?force_refresh=true" : "";
    const [tRes, thRes, acRes] = await Promise.allSettled([
      // ⚠⚠ THIS DESTRUCTURING IS POSITIONAL. The stub that used to sit here was
      // Promise.resolve([]) rather than a deleted element for exactly that reason:
      // removing the slot silently hands thRes the AIRCRAFT result and acRes
      // nothing, so thermal would render aircraft data and the build cannot see it.
      // Restored 2026-08-26 — the endpoint is live again (649 vessels) after the
      // scan_iter COUNT fix; it is no longer a dead upstream retried on every poll.
      fetchJSON("/api/altdata/tankers" + q),
      fetchJSON("/api/altdata/thermal" + q),
      fetchJSON("/api/altdata/aircraft" + q),
    ]);

    let tList = [];
    let tRegions = [];
    if (tRes.status === "fulfilled") {
      const d = tRes.value;
      // ⚠⚠ Placeholder vessels are dropped here, before the map, the dark-vessel
      // summary and the bus. This path never checked, so stand-in tankers drew
      // on the map and fired "N VESSELS DARK".
      tList = liveRows(d);
      // ⚠ The manifest rides on every payload shape the endpoint can return —
      // live, stale, representative and timeout — so an empty array here means
      // the request itself failed, not that monitoring stopped.
      tRegions = Array.isArray(d?.regions) ? d.regions : [];
    }
    setTankers(tList);
    setRegions(tRegions);
    // Signal Board ais-dark rule consumes globalSignals.tankers (the full array).
    // ⚠ Restored 2026-08-26 with the feed. The rule fires off real vessels again.
    onDataRef.current?.("tankers", tList);

    let thList = [];
    if (thRes.status === "fulfilled") {
      thList = liveRows(thRes.value);
    }
    setThermalList(thList);
    // Signal Board thermal rule consumes the full array.
    onDataRef.current?.("fires", thList);

    let acList = [];
    if (acRes.status === "fulfilled") {
      // Coerce coords — the feed can carry string lat/lon.
      acList = liveRows(acRes.value).map((r) => ({ ...r, lat: _num(r?.lat), lon: _num(r?.lon) }));
    }
    setAircraftList(acList);

    setLastUpdated(Date.now());
    setLoading(false);
  }, []);

  // Auto-refresh: mount + every 5 minutes.
  useEffect(() => {
    let cancelled = false;
    const run = () => { load(false).catch(() => {}); };
    run();
    const id = setInterval(() => { if (!cancelled) run(); }, REFRESH_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [load]);

  // ── SWIM flights: fetch on mount + every 60s ──
  // ⚠ Deliberately NOT folded into load(). That runs on a 5-minute cadence and
  // fans out over four slow upstreams; a flight feed that only moved every five
  // minutes would show aircraft teleporting. Separate source, separate clock.
  // ⚠ Failure is silent and non-destructive: SWIM is a side layer on a map whose
  // primary job is vessels and fires. A 404 while the backend redeploys must
  // leave the previous arcs on screen, not blank them.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const d = await fetchJSON(SWIM_FLIGHTS_PATH);
        if (cancelled) return;
        const evs = Array.isArray(d?.events) ? d.events : [];
        setSwimFlights(evs);
      } catch { /* keep whatever we already have */ }
    };
    run();
    const id = setInterval(() => { if (!cancelled) run(); }, SWIM_REFRESH_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // ── Lazy owner lookup when a flight popup opens unenriched ──
  // ⚠⚠ NEVER BLOCKS THE POPUP. The popup renders immediately from whatever the
  // hash already holds; this fills the owner line in afterwards. A popup that
  // waits on a network call to show a route it already knows is worse than one
  // that shows the route and resolves the name a moment later.
  // ⚠ Fires ONLY for an N-number callsign with no owner. An operator callsign
  // (EJA504, VJT809) is not a registration and will never match, so firing for
  // one is a guaranteed miss — and the backend already resolved every N-number
  // it could at write time, so this only ever covers what it missed.
  useEffect(() => {
    if (!popup || popup.kind !== "flight") return undefined;
    const d = popup.d || {};
    if (String(d.faa_owner || "").trim()) return undefined;
    const cs = String(d.callsign || "").trim().toUpperCase();
    if (!cs || !N_NUMBER_RE.test(cs)) return undefined;
    if (ownerCache[cs]) return undefined;          // already fetched or in flight

    let cancelled = false;
    setOwnerCache((prev) => ({ ...prev, [cs]: { status: "loading" } }));
    (async () => {
      let next = { status: "none" };
      try {
        const rec = await fetchJSON(`/api/aircraft/registry/${encodeURIComponent(cs)}`);
        // ⚠ The endpoint never 404s — it returns { found: false } — so absence
        // is a value to read, not an error to catch.
        if (rec && rec.found) {
          next = {
            status: "found",
            owner: rec.registrant_name || "",
            make: rec.make || "",
            model: rec.model || "",
          };
        }
      } catch {
        // ⚠ A failed lookup is "unknown", not an error banner. The flight and
        // its route are still perfectly good intelligence without a name.
        next = { status: "error" };
      }
      if (!cancelled) setOwnerCache((prev) => ({ ...prev, [cs]: next }));
    })();
    return () => { cancelled = true; };
  }, [popup, ownerCache]);

  // ── Position tick: advance the clock every 60s, no re-fetch ──
  useEffect(() => {
    const id = setInterval(() => setFlightTick(Date.now()), SWIM_REFRESH_MS);
    return () => clearInterval(id);
  }, []);

  // ⚠ Fires from a tap, never from an effect. Records "loading" first so a
  // second tap cannot start a second request for the same callsign.
  const lookUpOwner = useCallback(async (callsign) => {
    const cs = String(callsign || "").trim().toUpperCase();
    if (!cs || enrichCache[cs]?.status === "loading") return;
    setEnrichCache((p) => ({ ...p, [cs]: { status: "loading" } }));
    try {
      const path = `/api/altdata/swim/enrich/${encodeURIComponent(cs)}`;
      const r = await backendFetch(path, path);
      if (!r.ok) {
        setEnrichCache((p) => ({ ...p, [cs]: { status: "failed" } }));
        return;
      }
      const d = await r.json();
      setEnrichCache((p) => ({ ...p, [cs]: { status: "done", data: d } }));
    } catch {
      setEnrichCache((p) => ({ ...p, [cs]: { status: "failed" } }));
    }
  }, [enrichCache]);

  // ── Arc geometry, cached against the fetched data ──
  // ⚠⚠ Keyed on swimFlights ALONE. flightTick is deliberately absent from the
  // dependency list: including it would rebuild every geodesic once a minute to
  // produce byte-identical geometry.
  const flightArcs = useMemo(() => {
    const seen = new Set();
    const out = [];
    const cap = maxFlights();
    for (const f of swimFlights) {
      const cs = String(f?.callsign || "");
      // One arc per airframe. The feed carries an arrival AND a departure event
      // for a leg between two monitored fields, which is the same flight twice.
      if (!cs || seen.has(cs)) continue;
      const arc = buildFlightArc(f);
      if (!arc) continue;
      seen.add(cs);
      out.push(arc);
      if (out.length >= cap) break;   // newest-first from the endpoint
    }
    return out;
  }, [swimFlights]);

  // ── Live positions: recomputed on each tick, cheap by construction ──
  const flightPositions = useMemo(
    () => flightArcs.map((arc) => ({ arc, pos: flightPosition(arc, flightTick) })),
    [flightArcs, flightTick],
  );

  // Arc GeoJSON. One source, one layer, colour and opacity driven by feature
  // properties — 20 flights × 3 segments as separate layers would be 60 layers.
  const arcGeoJSON = useMemo(() => ({
    type: "FeatureCollection",
    features: flightPositions.flatMap(({ arc, pos }) => {
      if (arc.kind !== "arc") return [];
      // ⚠ A likely-landed flight fades rather than disappearing: the leg still
      // happened and is still the most recent thing that aircraft did.
      const op = pos?.landed ? 0.3 : 0.7;
      // ⚠ One colour for the whole leg when the destination is unusual — the
      // phase gradient says "where in the flight", gold says "where it is
      // going", and the second question is the one this feed exists to answer.
      const gold = arc.unusual;
      return arc.segments.map((s, i) => ({
        type: "Feature",
        properties: {
          color: gold ? UNUSUAL_ARC_COLOR : PHASE_COLOR[s.phase],
          op, callsign: arc.key,
        },
        geometry: { type: "LineString", coordinates: s.coords },
        id: `${arc.key}-${i}`,
      }));
    }),
  }), [flightPositions]);

  // Signal Board integration — publish a dark-vessel summary when any exist.
  useEffect(() => {
    if (!onData) return;
    const dark = (tankers || []).filter((v) => v?.aisDark);
    if (dark.length > 0) {
      // ⚠ Restored 2026-08-26. SignalBoard.ruleTankerDarkCluster renders
      // "N VESSELS AIS DARK" from this — live measurement had 23 of 649 dark.
      onData("aisDark", {
        count: dark.length,
        vessels: dark.map((v) => v.name),
        locations: dark
          .filter((v) => typeof v?.lat === "number" && typeof v?.lon === "number")
          .map((v) => ({ lat: v.lat, lon: v.lon })),
      });
    }
  }, [tankers, onData]);

  // Diagnostics — placeable vs dropped vessel counts.
  // ⚠⚠ AISStream defunct as of Aug 2026. Restore when a new source is wired.
  // Silenced because the fetch above now resolves to [], so this logged
  // "0/0 vessels placeable" to the console on every 5-minute poll — noise that
  // reads like a failure rather than a disabled feed.
  // TO RESTORE: uncomment alongside the fetch.
  // useEffect(() => {
  //   const list = tankers || [];
  //   const dropped = list.filter((v) => !_validCoord(v));
  //   console.log(
  //     `[PhysicalIntel] ${list.length - dropped.length}/${list.length} vessels placeable · ${dropped.length} dropped (invalid/zero/out-of-bounds coords)`,
  //     dropped.length ? dropped.map((v) => ({ name: v?.name, lat: v?.lat, lon: v?.lon })) : ""
  //   );
  // }, [tankers]);

  const toggle = (key) => {
    setLayers((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem(LAYERS_KEY, JSON.stringify(next)); } catch { /* quota */ }
      return next;
    });
  };

  // ⚠⚠ HIDDEN, NOT REMOVED — MIL and CARGO removed from ADS-B collection Aug 2026.
  // Bizjet-only architecture. All toggles hidden pending revenue/API upgrade.
  // Restore when collection expands.
  // Aircraft sub-type toggle: flip + persist the boolean under its own key.
  // const toggleAcType = (storageKey, setter) => setter((prev) => {
  //   const next = !prev;
  //   try { localStorage.setItem(storageKey, String(next)); } catch { /* quota */ }
  //   return next;
  // });

  const vessels = (tankers || []).filter(_validCoord);
  // ⚠ A cache fold-back is a LAST KNOWN position, not a sighting. 559 of 649 rows
  // carried is_cached at the last live measurement, so conflating the two would
  // present a 40-minute-old position with the same confidence as a live one.
  const cachedCount = vessels.filter((v) => v?.is_cached).length;

  // Vessels grouped by region, in CHOKEPOINTS order so the map labels and the
  // list agree. Live sightings first, then cached; by speed descending within
  // each. ⚠ Regions absent from CHOKEPOINTS fall into "Unnamed area" rather than
  // being dropped — a vessel we cannot label is still a vessel we saw.
  const vesselsByRegion = (() => {
    // ⚠⚠ globalThis.Map, NOT Map. THE BUILT-IN IS SHADOWED IN THIS FILE by the
    // react-map-gl default import at the top (`import Map, { Marker, Popup }`),
    // so a bare `new Map()` here constructs the MAP COMPONENT. react-map-gl's
    // export is a forwardRef object rather than a plain function, so `new` on it
    // throws "X is not a constructor" — and after minification X is a generated
    // name like `CO`, which says nothing about where the fault is.
    // ⚠ The failure is invisible to the build: the identifier resolves fine, it
    // is only wrong at runtime, and only on the code path that renders vessels.
    const groups = new globalThis.Map();
    regions.forEach((cp) => groups.set(cp.name, []));
    vessels.forEach((v) => {
      const key = v?.region && groups.has(v.region) ? v.region : "Unnamed area";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(v);
    });
    groups.forEach((list) => list.sort((a, b) => {
      const ac = a?.is_cached ? 1 : 0, bc = b?.is_cached ? 1 : 0;
      if (ac !== bc) return ac - bc;                       // live before cached
      return (Number(b?.speed) || 0) - (Number(a?.speed) || 0);
    }));
    return groups;
  })();
  // Recomputed on the quantised zoom only — see the onMove note.
  const mScale = markerScale(zoom);
  const showLabels = zoom > LABEL_MIN_ZOOM;
  const thermal = (thermalList || []).filter(_validCoord);
  // Aircraft: airborne + valid coords. Military sorted last so it renders on top.
  // Foreign bizjets with no FAA owner carry no intelligence-chain value, so drop
  // them as noise. A bizjet is dropped ONLY when it has no faa_owner AND its ICAO
  // hex is not US (A-block) — US-registered bizjets show even without an owner
  // match (their popup/list read "Operator flight").
  //
  // ⚠⚠ MIL and CARGO removed from ADS-B collection Aug 2026. Bizjet-only
  // architecture. All toggles hidden pending revenue/API upgrade. Restore when
  // collection expands.
  // The bizjet condition is forced on: qs_ac_bizjet is NOT read here any more. A
  // stored "false" from before the pills were hidden would otherwise empty the
  // markers AND the list forever, with no control left on screen to undo it.
  // ⚠ The type gate itself is KEPT, not dropped. The backend is bizjet-only now,
  // but the aircraft cache can still hold pre-rebuild military/cargo records until
  // they age out — without this gate those would draw on the map under the very
  // types that were just retired.
  // TO RESTORE: put the sub-type clause back alongside the state declarations:
  //   && (
  //     (a.type === "military" && showMilitary) ||
  //     (a.type === "cargo" && showCargo) ||
  //     (a.type === "bizjet" && showBizjet)
  //   )
  const aircraft = (aircraftList || []).filter(
    (a) =>
      a &&
      a.onGround !== true &&
      _validCoord(a) &&
      !(a.type === "bizjet" && !a.faa_owner && !_isUsHex(a.icao24)) &&
      a.type === "bizjet"
  );

  // Row click → fly the map to the aircraft and open its popup. flyTo only; no
  // other map state touched. Skips the pan when coordinates are missing.
  const focusAircraft = (a) => {
    if (a && _validCoord(a) && mapRef.current) {
      mapRef.current.flyTo({ center: [a.lon, a.lat], zoom: 6, duration: 1000 });
    }
    if (a) setPopup({ kind: "aircraft", d: a });
    setSelectedAc(a ? (a.icao24 || a.callsign || null) : null);
    if (selTimer.current) clearTimeout(selTimer.current);
    selTimer.current = setTimeout(() => setSelectedAc(null), 30000);
  };

  /** Stable identity for a flight-log row.
   *
   * ⚠ NOT the array index. The log re-fetches every five minutes and a new
   * event pushes everything down, so an index-keyed highlight would jump to a
   * different flight on the next poll. icao24 is empty on ~85% of SWIM events,
   * hence the callsign fallback.
   */
  const eventKey = (e, i) =>
    `${e?.icao24 || e?.callsign || "?"}|${e?.timestamp ?? i}`;

  // Flight-log row click → put the flight on the map, by whichever of three
  // routes is still available. Ordered strongest evidence first.
  //
  // ⚠⚠ UNREACHABLE AFTER THE UNIFIED LIST — log events removed in this
  // commit. Retained for the one-file guard: nothing calls this, and eventKey /
  // selectedEvt / evtTimer exist only to keep it coherent. Deleting it is a
  // follow-up, together with EVENT_STYLE and dwellLabel, which lost their only
  // consumer with the LOG render.
  const focusFlightEvent = (e, i) => {
    setSelectedEvt(eventKey(e, i));
    if (evtTimer.current) clearTimeout(evtTimer.current);
    evtTimer.current = setTimeout(() => setSelectedEvt(null), 30000);

    const cs = String(e?.callsign || "").trim().toUpperCase();
    const hex = String(e?.icao24 || "").trim().toLowerCase();

    // ── 1. Still drawn as a SWIM arc. Opens the SAME popup the marker opens,
    // built from the same fields, so clicking the row and clicking the aircraft
    // are one action rather than two things that look alike.
    if (cs) {
      const hit = flightPositions.find(({ arc, pos }) =>
        pos && !pos.notDeparted &&
        String(arc?.flight?.callsign || "").trim().toUpperCase() === cs);
      if (hit && mapRef.current) {
        mapRef.current.flyTo({ center: [hit.pos.lon, hit.pos.lat], zoom: 6, duration: 1000 });
        setPopup({ kind: "flight", d: {
          ...hit.arc.flight, lon: hit.pos.lon, lat: hit.pos.lat, _pos: hit.pos,
          _depMs: hit.arc.depMs, _depEstimated: hit.arc.depEstimated,
          _arrMs: hit.arc.arrMs, _arrEstimated: hit.arc.arrEstimated,
          _unusual: hit.arc.unusual,
        } });
        return;
      }
    }

    // ── 2. Still a live ADS-B contact. Hand to the existing handler rather
    // than restating it — one definition of what focusing an aircraft means.
    const ac = (aircraft || []).find((a) =>
      (hex && String(a?.icao24 || "").toLowerCase() === hex) ||
      (cs && String(a?.callsign || "").trim().toUpperCase() === cs));
    if (ac) { focusAircraft(ac); return; }

    // ── 3. Aged out of both live feeds, which is the COMMON case: the log runs
    // 24 hours deep and the live arrays hold only what is airborne right now.
    // ⚠ The monitored field is where the event actually happened, so panning
    // there is honest. There is no marker to open and none is invented.
    const icao = monitoredIcao(e?.airport)
      || monitoredIcao(e?.event === "departure" ? e?.departure_icao : e?.arrival_icao);
    const center = airportCenter(icao);
    if (center && mapRef.current) {
      mapRef.current.flyTo({ center, zoom: 6, duration: 1000 });
    }
  };

  // Destination card click → the DEPARTURE field, deliberately.
  //
  // ⚠⚠ NOT the destination. The whole definition of an unusual destination is
  // that it is not one of the 12 monitored fields, so it has no marker on this
  // map and frequently no coordinates we hold at all. The departure IS
  // monitored, is always drawable, and is the end of the route this map knows.
  const focusDestination = (d) => {
    setSelectedDest(d?.id ?? null);
    if (destTimer.current) clearTimeout(destTimer.current);
    destTimer.current = setTimeout(() => setSelectedDest(null), 30000);
    const center = airportCenter(monitoredIcao(d?.departure_icao));
    if (center && mapRef.current) {
      mapRef.current.flyTo({ center, zoom: 6, duration: 1000 });
    }
  };
  const aircraftSorted = [...aircraft].sort(
    (a, b) => (a.type === "military" ? 1 : 0) - (b.type === "military" ? 1 : 0)
  );

  // Unified-list SWIM row click → the aircraft where it is now, else the field
  // the leg ended at.
  //
  // ⚠⚠ buildFlightArc IS CALLED FRESH, NOT READ FROM flightPositions. That memo
  // is capped at maxFlights() — 20 on a narrow viewport — while this list shows
  // up to 50, so rows 21-50 would have no cached arc on mobile and would fall
  // through to the airport pan for a reason the reader could never see.
  const focusSwimFlight = (f) => {
    setSelectedSwim(String(f?.callsign || "") || null);
    if (swimTimer.current) clearTimeout(swimTimer.current);
    swimTimer.current = setTimeout(() => setSelectedSwim(null), 30000);

    const arc = buildFlightArc(f);
    const pos = arc ? flightPosition(arc, Date.now()) : null;
    if (arc && pos && !pos.notDeparted && !pos.landed) {
      if (mapRef.current) {
        mapRef.current.flyTo({ center: [pos.lon, pos.lat], zoom: 6, duration: 1000 });
      }
      // Same payload the marker builds, so clicking the row and clicking the
      // aircraft are one action rather than two things that look alike.
      setPopup({ kind: "flight", d: {
        ...f, lon: pos.lon, lat: pos.lat, _pos: pos,
        _depMs: arc.depMs, _depEstimated: arc.depEstimated,
        _arrMs: arc.arrMs, _arrEstimated: arc.arrEstimated,
        _unusual: arc.unusual,
      } });
      return;
    }
    // ⚠ NO POPUP INVENTED. A landed or unplaceable flight has no marker, and
    // opening one over an airport would assert an aircraft is sitting there.
    const center = airportCenter(f?.arrival_icao) || airportCenter(f?.departure_icao);
    if (center && mapRef.current) {
      mapRef.current.flyTo({ center, zoom: 6, duration: 1000 });
    }
  };

  // SWIM rows: unusual first, then newest departure, then newest message.
  // ⚠ A row with no departure_time is NOT pushed to the bottom — it falls
  // through to last_updated and interleaves. departure_time is on ~64% of
  // records, so ranking on its presence would be ranking on enrichment luck.
  const swimRows = useMemo(() => {
    const seen = new Set();
    const rows = [];
    for (const f of swimFlights) {
      const cs = String(f?.callsign || "");
      if (!cs || seen.has(cs)) continue;
      seen.add(cs);
      rows.push(f);
    }
    rows.sort((a, b) => {
      const u = Number(looksUnusual(b)) - Number(looksUnusual(a));
      if (u !== 0) return u;
      const ad = Date.parse(a?.departure_time || "");
      const bd = Date.parse(b?.departure_time || "");
      if (Number.isFinite(ad) && Number.isFinite(bd) && ad !== bd) return bd - ad;
      return (Date.parse(b?.last_updated || "") || 0) - (Date.parse(a?.last_updated || "") || 0);
    });
    return rows.slice(0, SWIM_LIST_CAP);
  }, [swimFlights]);

  // ⚠ `aircraft` is already filtered to airborne contacts, so the onGround term
  // is a no-op today. It stays because the filter is the thing that guarantees
  // it, not this sort, and the two are edited independently.
  const adsbRows = [...aircraft]
    .sort((a, b) =>
      (Number(a?.onGround === true) - Number(b?.onGround === true)) ||
      String(a?.callsign || "").localeCompare(String(b?.callsign || "")))
    .slice(0, ADSB_LIST_CAP);

  const timeLabel = lastUpdated ? new Date(lastUpdated).toLocaleTimeString() : "—";
  const mapHeight = narrow ? "calc(100vh - 180px)" : "calc(100vh - 120px)";

  const toggleBtn = (key, label) => {
    const active = !!layers[key];
    return (
      <button
        key={key}
        onClick={() => toggle(key)}
        style={{
          minHeight: 44, padding: "4px 10px", borderRadius: 3,
          fontSize: 9, fontWeight: 700, letterSpacing: "0.1em",
          background: "transparent", cursor: "pointer",
          color: active ? C.amber : C.textDim,
          border: `1px solid ${active ? C.amber : C.border}`,
          display: "inline-flex", alignItems: "center", gap: 5,
        }}
      >
        {label}
      </button>
    );
  };

  // ⚠⚠ HIDDEN, NOT REMOVED — MIL and CARGO removed from ADS-B collection Aug 2026.
  // Bizjet-only architecture. All toggles hidden pending revenue/API upgrade.
  // Restore when collection expands.
  // Aircraft sub-type pill — same look as the layer toggles, slightly smaller.
  // const subToggleBtn = (label, active, onClick) => (
  //   <button
  //     key={label}
  //     onClick={onClick}
  //     style={{
  //       minHeight: 30, padding: "3px 9px", borderRadius: 3,
  //       fontSize: 9, fontWeight: 700, letterSpacing: "0.1em",
  //       background: "transparent", cursor: "pointer",
  //       color: active ? C.amber : C.textDim,
  //       border: `1px solid ${active ? C.amber : C.border}`,
  //     }}
  //   >
  //     {label}
  //   </button>
  // );

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
        <span style={{ color: "#00FF87", fontSize: 11, fontWeight: 700, letterSpacing: "0.15em" }}>
          PHYSICAL INTEL
        </span>
        <span style={{ fontSize: 9, color: C.textDim }}>
          <span style={{ color: C.green }}>●</span>
          {" LIVE"}
          {/* ⚠ Live sightings are counted separately from cache fold-backs. 559 of
              649 rows were `is_cached` at the last measurement, so a bare vessel
              count would overstate what the feed just saw by roughly 6x. */}
          {vessels.length > 0
            ? ` · ${vessels.length} vessels${cachedCount > 0 ? ` (${vessels.length - cachedCount} live)` : ""}`
            : ""}
          {/* ⚠ layers.aircraft gate removed (Aug 2026) to match the markers, which
              no longer honour it either. Leaving it here made the toggle half-dead:
              clicking it blanked the count while the planes stayed on the map. */}
          {aircraft.length > 0 ? ` · ${aircraft.length} aircraft` : ""}
          {` · ${thermal.length} anomalies · Updated ${loading ? "…" : timeLabel}`}
        </span>
      </div>

      {/* Layer toggles + refresh */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
        {toggleBtn("vessels", "VESSELS")}
        {toggleBtn("thermal", "THERMAL")}
        {/* ⚠ HIDDEN, NOT REMOVED — Aug 2026. Nothing reads layers.aircraft any
            more: the marker map dropped the gate in a7da618 and the header count
            in 556e762, so this button flipped its highlight and wrote to
            qs_physicalintel_layers while changing nothing on screen. Same shape
            of dead control as VESSELS above.
            The AIRCRAFT LIST button below is a different thing (showList) and is
            deliberately left in place.
            layers.aircraft still defaults false in loadLayers and any stored
            qs_physicalintel_layers value is left untouched, so restoring this
            line plus the two gates restores the previous behaviour exactly.
        {toggleBtn("aircraft", "AIRCRAFT")}
        */}
        {toggleBtn("ports", "PORTS")}
        {/* ⚠ Always rendered, even with no flights — a control that vanishes
            when its feed is quiet reads as a broken panel rather than an empty
            one. SWIM is still filling out, so an empty layer is expected. */}
        {toggleBtn("flights", "FLIGHTS")}
        <button
          onClick={() => setShowList((v) => !v)}
          title="Toggle the flight list — flight plan data, plus live aircraft contacts near a tracked field"
          style={{
            minHeight: 44, padding: "4px 10px", borderRadius: 3,
            fontSize: 9, fontWeight: 700, letterSpacing: "0.1em",
            background: "transparent", cursor: "pointer",
            color: showList ? C.amber : C.textDim,
            border: `1px solid ${showList ? C.amber : C.border}`,
          }}
        >
          {`FLIGHTS (${swimRows.length + adsbRows.length})`}
        </button>
        {/* ⚠⚠ INDEPENDENT OF THE AIRCRAFT PILL. Separate state (showVessels vs
            showList), separate panel, separate toggle — opening one must not
            close or re-mode the other. The aircraft pill has two modes and both
            are aircraft; this one is vessels and has none. */}
        <button
          onClick={() => setShowVessels((v) => !v)}
          title="Toggle the vessel list — vessel contacts grouped by monitored region"
          style={{
            minHeight: 44, padding: "4px 10px", borderRadius: 3,
            fontSize: 9, fontWeight: 700, letterSpacing: "0.1em",
            background: "transparent", cursor: "pointer",
            color: showVessels ? VESSEL_BLUE : C.textDim,
            border: `1px solid ${showVessels ? VESSEL_BLUE : C.border}`,
          }}
        >
          {`VESSELS (${vessels.length})`}
        </button>
        <button
          onClick={() => { setRefreshing(true); load(true).catch(() => {}).finally(() => setRefreshing(false)); }}
          disabled={refreshing}
          title="Refresh — fetch fresh data now"
          style={{
            minHeight: 44, padding: "4px 10px", borderRadius: 3, marginLeft: "auto",
            fontSize: 9, fontWeight: 700, letterSpacing: "0.1em",
            background: "transparent", cursor: refreshing ? "default" : "pointer",
            color: C.teal, border: `1px solid ${C.border}`,
          }}
        >
          {refreshing ? "REFRESHING…" : "REFRESH"}
        </button>
      </div>

      {/* ⚠⚠ HIDDEN, NOT REMOVED — MIL and CARGO removed from ADS-B collection Aug 2026.
          Bizjet-only architecture. All toggles hidden pending revenue/API upgrade.
          Restore when collection expands.

          MIL and CARGO would be controls over record types the backend no longer
          collects — they could only ever filter an empty set. BIZJET is hidden with
          them for visual consistency: it is the sole remaining type, so a lone pill
          that can only subtract is not a filter, it is a way to blank the panel.
          The aircraft filter above now renders every record the feed returns.

          TO RESTORE: uncomment this row alongside subToggleBtn, toggleAcType,
          loadAcPref, the three state declarations, and the sub-type clause in the
          aircraft filter. The qs_ac_* localStorage keys were never cleared.
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 8, marginLeft: 4 }}>
        {subToggleBtn("MIL", showMilitary, () => toggleAcType("qs_ac_military", setShowMilitary))}
        {subToggleBtn("CARGO", showCargo, () => toggleAcType("qs_ac_cargo", setShowCargo))}
        {subToggleBtn("BIZJET", showBizjet, () => toggleAcType("qs_ac_bizjet", setShowBizjet))}
      </div>
      */}

      {/* Map */}
      {!MAP_TOKEN ? (
        <div style={{
          width: "100%", height: mapHeight, borderRadius: 3, border: `1px solid ${C.border}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: C.textDim, fontSize: 12, textAlign: "center", padding: 16,
        }}>
          Map token required — add VITE_MAP_TOKEN to Cloudflare Pages environment
        </div>
      ) : (
        <div style={{ position: "relative", width: "100%", height: mapHeight, borderRadius: 3, overflow: "hidden", border: `1px solid ${C.border}` }}>
          <style>{POPUP_CSS}</style>
          <Map
            ref={mapRef}
            mapboxAccessToken={MAP_TOKEN}
            initialViewState={{ longitude: 20, latitude: 25, zoom: MAP_INITIAL_ZOOM }}
            onMove={(e) => {
              // ⚠⚠ onMove FIRES ON EVERY FRAME of a pan or zoom gesture. With 649
              // vessel markers a setState per frame re-renders the whole layer
              // continuously and the map stutters under the drag. Quantising to
              // 0.25 collapses a gesture into a handful of updates, and 0.25 of a
              // zoom level is far below any visible size step.
              // ⚠ A pure PAN reports an unchanged zoom, so this also makes panning
              // free rather than a render storm.
              const z = e.viewState?.zoom;
              if (typeof z !== "number" || !Number.isFinite(z)) return;
              setZoom((prev) => (Math.abs(prev - z) >= 0.25 ? z : prev));
            }}
            mapStyle="mapbox://styles/mapbox/dark-v11"
            style={{ width: "100%", height: "100%" }}
          >
            {/* ── SWIM FLIGHT ARCS ──
                ⚠ First child on purpose. A Mapbox <Layer> always paints beneath
                every <Marker> (markers are DOM overlays, layers are canvas), so
                the arcs sit under the aircraft, vessel and thermal markers
                without any z-index work. Ordering here is for readers.
                ⚠ Colour and opacity come from FEATURE PROPERTIES via `get`, so
                20 flights × 3 phase segments is one source and one layer rather
                than 60 layers. */}
            {layers.flights && arcGeoJSON.features.length > 0 && (
              <Source id="swim-arcs" type="geojson" data={arcGeoJSON}>
                <Layer
                  id="swim-arcs-line"
                  type="line"
                  layout={{ "line-cap": "round", "line-join": "round" }}
                  paint={{
                    "line-color": ["get", "color"],
                    "line-opacity": ["get", "op"],
                    "line-width": 2,
                  }}
                />
              </Source>
            )}

            {/* ── SWIM FLIGHT POSITIONS: the same ✈︎ as the geo-fence markers ──
                ⚠ Same glyph, same +45 rotation offset, phase colour on the BOX
                rather than the glyph — the geo-fence marker is white-on-coloured
                and these must read as the same family of object. */}
            {layers.flights && flightPositions.map(({ arc, pos }) => {
              // Not yet departed → the route is real and drawn, but there is no
              // aircraft in the air to place.
              if (!pos || pos.notDeparted) return null;
              const color = PHASE_COLOR[pos.phase];
              return (
                <Marker
                  key={`swim-${arc.key}`}
                  longitude={pos.lon}
                  latitude={pos.lat}
                  anchor="center"
                  onClick={(e) => {
                    e.originalEvent?.stopPropagation();
                    setPopup({ kind: "flight", d: {
                      ...arc.flight, lon: pos.lon, lat: pos.lat, _pos: pos,
                      // ⚠ depMs/depEstimated live on the ARC, not on arc.flight —
                      // the fallback to last_updated happens in buildFlightArc, so
                      // re-parsing departure_time in the popup would show "no time"
                      // for exactly the ~30% the fallback exists to cover.
                      _depMs: arc.depMs, _depEstimated: arc.depEstimated,
                      // ⚠ Same reason as _depMs above: the DERIVED arrival lives
                      // on the arc, so a popup re-parsing arrival_time_est would
                      // show no ETA for exactly the ~55% the estimate exists for.
                      _arrMs: arc.arrMs, _arrEstimated: arc.arrEstimated,
                      _unusual: arc.unusual,
                    } });
                  }}
                >
                  <div style={{
                    // ⚠ 22 against the geo-fence bizjet's 20 — a touch larger so
                    // a SWIM flight is distinguishable from an ADS-B contact at a
                    // glance, without becoming a different kind of object.
                    width: scaledPx(22, mScale), height: scaledPx(22, mScale),
                    background: `${color}33`,
                    // ⚠⚠ DASHED MEANS THE DEPARTURE TIME IS ALSO INFERRED. Opacity
                    // already carries "this position is estimated"; dimming twice
                    // for the double case would land at 0.36 and read as a render
                    // bug rather than as lower confidence. Two independent facts
                    // get two independent channels and neither compounds.
                    border: `1.5px ${arc.depEstimated && arc.arrEstimated ? "dashed" : "solid"} ${color}`,
                    borderRadius: 4,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    cursor: "pointer",
                    boxShadow: pos.landed ? "none" : `0 0 6px ${color}66`,
                    // ⚠ MINIMUM, NEVER A PRODUCT. Both rules can fire on the same
                    // marker — an estimated flight past its estimated arrival is
                    // both — and 0.6 x 0.55 would be a third value neither rule
                    // asked for. The most-dimmed rule wins and the scale stays
                    // readable: 1 known, 0.6 estimated, 0.55 landed.
                    opacity: Math.min(pos.landed ? 0.55 : 1, arc.arrEstimated ? 0.6 : 1),
                  }}>
                    <span style={{
                      fontSize: scaledPx(16, mScale), color: "#fff", lineHeight: 1,
                      display: "inline-block",
                      // ⚠ +45 for the same reason as the geo-fence marker: ✈︎
                      // (U+2708) points NW at rest, so bearing 0 needs +45 to
                      // point north. A null bearing means we could not compute
                      // one — leave it unrotated rather than assert north.
                      transform: pos.bearing === null || pos.bearing === undefined
                        ? undefined
                        : `rotate(${pos.bearing + 45}deg)`,
                    }}>{"✈︎"}</span>
                  </div>
                </Marker>
              );
            })}

            {/* ── VESSELS layer: region + port labels + vessel markers ── */}
            {/* ⚠⚠ A REGION WITH NO RECEIVER COVERAGE IS LABELLED AS SUCH, NOT LEFT
                BLANK. Hormuz and Bab el-Mandeb return zero vessels because nobody
                runs an AIS receiver there — showing nothing would read as "we are
                watching the world's most important crude chokepoint and nothing is
                moving", which is the opposite of the truth. Red, with the reason
                spelled out underneath — the colour alone would not say WHY. */}
            {layers.vessels && regions.map((cp) => {
              // ⚠ has_coverage from the manifest, NOT inferred from the count.
              const dead = cp.has_coverage === false;
              const n = (vesselsByRegion.get(cp.name) || []).length;
              return (
                <Marker key={`cp-${cp.name}`} longitude={cp.anchor_lon} latitude={cp.anchor_lat} anchor="center">
                  <div style={{ ...LABEL_STYLE, textAlign: "center", pointerEvents: "none" }}>
                    <div style={dead ? { color: C.red, opacity: 0.85 } : undefined}>
                      {cp.name}{!dead && n > 0 ? ` (${n})` : ""}
                    </div>
                    {dead && (
                      <div style={{ fontSize: 8, color: C.red, opacity: 0.8, letterSpacing: 0 }}>
                        {/* ⚠ The endpoint supplies the wording. One phrasing, one place. */}
                        {cp.coverage_note || "no receiver coverage"}
                      </div>
                    )}
                  </div>
                </Marker>
              );
            })}
            {/* ⚠ RE-GATED from layers.vessels to layers.ports (Aug 2026). These are
                static US port NAME labels, not vessels - they were only grouped with the
                AIS layer for rendering. Hiding the vessel layer would have taken working
                geography with it. */}
            {layers.ports && PORT_LABELS.map((p) => (
              <Marker key={`plbl-${p.name}`} longitude={p.lon} latitude={p.lat} anchor="center">
                <div style={LABEL_STYLE}>{p.name}</div>
              </Marker>
            ))}
            {/* ⚠⚠ VESSEL_BLUE (#00BFFF), not C.teal and not C.blue. C.teal is
                "#00FF87" — byte-identical to C.amber — so the original teal dot was the
                same colour as an aircraft marker and every other accent on the map.
                C.blue (#3b82f6) fixed the collision but sat too close to dark-v11's own
                blue-grey landmass shading to read cleanly. See the constant.
                ⚠ A CACHED vessel is dimmed and hollow: it is a last-known position,
                possibly an hour old, and must not look like a live sighting. */}
            {layers.vessels && vessels.map((v, i) => {
              const firstWord = String(v?.name || "").trim().split(/\s+/)[0] || "—";
              return (
                <Marker
                  // ⚠ Keyed on MMSI, not the array index. The vessel array reorders
                  // between polls as live/cached membership shifts, and an index key
                  // makes React reuse one vessel's DOM node for a different vessel —
                  // the dot keeps its old colour and label until something else
                  // forces an update. Aircraft already key on icao24.
                  key={`v-${v.mmsi || i}`}
                  longitude={v.lon}
                  latitude={v.lat}
                  anchor="center"
                  onClick={(e) => { e.originalEvent?.stopPropagation(); setPopup({ kind: "vessel", d: v }); }}
                >
                  <div style={{ position: "relative", cursor: "pointer", lineHeight: 0 }}>
                    {v.aisDark && (
                      <span style={{
                        position: "absolute", left: "50%", bottom: "100%", transform: "translateX(-50%)",
                        marginBottom: 3, fontSize: 9, fontWeight: 700, color: C.red,
                        whiteSpace: "nowrap", fontFamily: font, textShadow: "0 0 3px #000", pointerEvents: "none",
                      }}>
                        ⚠ DARK
                      </span>
                    )}
                    <div style={v.aisDark ? {
                      width: scaledPx(12, mScale), height: scaledPx(12, mScale),
                      borderRadius: "50%", background: C.red,
                      border: "1px solid rgba(255,255,255,0.25)", boxShadow: `0 0 7px ${C.red}`,
                    } : v.is_cached ? {
                      // last known position — hollow, dim, static (no pulse)
                      width: scaledPx(8, mScale), height: scaledPx(8, mScale),
                      borderRadius: "50%", background: "transparent",
                      border: `1.5px solid ${VESSEL_BLUE}`, opacity: 0.5,
                    } : {
                      width: scaledPx(8, mScale), height: scaledPx(8, mScale),
                      borderRadius: "50%", background: VESSEL_BLUE,
                      boxShadow: `0 0 5px ${VESSEL_BLUE}`, animation: "qsPulse 2.5s ease-in-out infinite",
                    }} />
                    {/* ⚠⚠ GATED ON ZOOM. 649 vessels each drew a black name plate at
                        every zoom level, so the default world view was a wall of
                        overlapping text with the dots buried under it. The dot carries
                        the position; the name is only legible zoomed in anyway.
                        ⚠ The "⚠ DARK" badge above is NOT gated — 23 of 649 is an alert,
                        not clutter, and hiding it at the zoom where you scan the whole
                        map would hide the one thing worth scanning for. */}
                    {showLabels && (
                      <span style={{
                        position: "absolute", left: "50%", top: "100%", transform: "translateX(-50%)",
                        marginTop: 4, fontSize: 11, fontWeight: 600, color: "#fff",
                        background: "rgba(0,0,0,0.75)", padding: "3px 6px", borderRadius: 3,
                        border: "0.5px solid rgba(255,255,255,0.3)",
                        maxWidth: 90, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        fontFamily: font, pointerEvents: "none", display: "block",
                      }}>
                        {firstWord}
                      </span>
                    )}
                  </div>
                </Marker>
              );
            })}

            {/* ── THERMAL layer (real detections only) ── */}
            {layers.thermal && thermal.map((t, i) => {
              const st = thermalStyle(t);
              return (
                <Marker
                  key={`t-${i}`}
                  longitude={t.lon}
                  latitude={t.lat}
                  anchor="center"
                  onClick={(e) => { e.originalEvent?.stopPropagation(); setPopup({ kind: "thermal", d: t }); }}
                >
                  {/* ⚠ st.size encodes FRP (6/8/12/16). Scaling by a factor keeps
                      that ladder intact; clamping absolute pixels would flatten it. */}
                  <div style={{
                    width: scaledPx(st.size, mScale), height: scaledPx(st.size, mScale),
                    borderRadius: "50%", background: st.color,
                    boxShadow: `0 0 6px ${st.color}`, cursor: "pointer",
                  }} />
                </Marker>
              );
            })}

            {/* ── PORTS layer: diamond markers ── */}
            {layers.ports && PORTS.map((p) => (
              <Marker
                key={`port-${p.name}`}
                longitude={p.lon}
                latitude={p.lat}
                anchor="center"
                onClick={(e) => { e.originalEvent?.stopPropagation(); setPopup({ kind: "port", d: p }); }}
              >
                <div style={{
                  width: scaledPx(12, mScale), height: scaledPx(12, mScale),
                  background: "#FFC107", transform: "rotate(45deg)",
                  border: "1px solid rgba(0,0,0,0.5)", boxShadow: "0 0 5px rgba(255,193,7,0.6)", cursor: "pointer",
                }} />
              </Marker>
            ))}

            {/* ── AIRCRAFT layer: ✈ markers by type; military rendered last (on top) ──
                ⚠ The layers.aircraft gate was removed here (Aug 2026). It defaulted to
                FALSE, so on a fresh load bizjet markers never drew at all — the map
                looked empty while the list had rows. Markers now render
                unconditionally. layers.aircraft itself is left intact in
                qs_physicalintel_layers and still gates the header count. */}
            {aircraftSorted.map((a, i) => {
              const st = aircraftStyle(a.type);
              // Military markers take their nationality color when the backend
              // resolved one (US/UK/RU/CN/NATO/…); border falls back to the
              // bizjet-enriched amber, then the type color. Background uses the
              // nationality color at 20% opacity only for military-with-color.
              // NATO and US get distinct 2px treatments: NATO = dark-blue fill +
              // gold (NATO-flag) border; US = red fill + navy border. Every other
              // military nationality keeps its nationality_color logic.
              const isNato = a.type === "military" && a.nationality === "NATO";
              const isUS = a.type === "military" && a.nationality === "US";
              const isMilNat = a.type === "military" && a.nationality_color;
              const borderColor = isNato
                ? "#FFD700"
                : isUS
                ? "#003087"
                : isMilNat
                ? a.nationality_color
                : (a.type === "bizjet" && a.faa_owner ? C.amber : st.color);
              const borderWidth = isNato || isUS ? 2 : 1.5;
              const boxBg = isNato
                ? "#1B3A6B33"
                : isUS
                ? "#CC000033"
                : (isMilNat ? `${a.nationality_color}33` : st.bg);
              return (
                <Marker
                  key={`ac-${a.icao24 || i}`}
                  longitude={a.lon}
                  latitude={a.lat}
                  anchor="center"
                  onClick={(e) => { e.originalEvent?.stopPropagation(); setPopup({ kind: "aircraft", d: a }); }}
                >
                  <div style={{ position: "relative", cursor: "pointer", lineHeight: 0 }}>
                    <div style={{
                      position: "relative",
                      // ⚠ st.box encodes the type hierarchy (bizjet 20 / cargo 24 /
                      // military 28). Factor-scaled so military stays the largest.
                      width: scaledPx(st.box, mScale), height: scaledPx(st.box, mScale),
                      // Military → nationality color; FAA-enriched bizjet → amber;
                      // otherwise the type color / fill (see borderColor/boxBg above).
                      background: boxBg,
                      border: `${borderWidth}px solid ${borderColor}`,
                      borderRadius: 4,
                      display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
                    }}>
                      <span style={{
                        fontSize: scaledPx(st.icon, mScale), color: "#fff", lineHeight: 1, display: "inline-block",
                        // ✈︎ (U+2708) points NW by default (45° CCW of "up"), so +45°
                        // makes heading=0 point north. heading 0/null is treated as
                        // "unknown" → no rotation (track is sparse on the feed).
                        transform: a.heading ? `rotate(${a.heading + 45}deg)` : undefined,
                      }}>{"✈︎"}</span>
                    </div>
                    {/* ⚠ Gated on the same threshold as the vessel labels — two
                        label layers on one map must appear and disappear together, or
                        the map reads as though one feed dropped out. */}
                    {showLabels && (
                      <span style={{
                        position: "absolute", left: "50%", top: "100%", transform: "translateX(-50%)",
                        marginTop: 4, fontSize: 11, fontWeight: 600, color: "#fff",
                        background: "rgba(0,0,0,0.75)", padding: "3px 6px", borderRadius: 3,
                        border: "0.5px solid rgba(255,255,255,0.3)",
                        maxWidth: 90, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        fontFamily: font, pointerEvents: "none", display: "block",
                      }}>
                        {a.callsign}
                      </span>
                    )}
                  </div>
                </Marker>
              );
            })}

            {/* ── Popups ── */}
            {popup && (
              <Popup
                longitude={popup.d.lon}
                latitude={popup.d.lat}
                anchor="bottom"
                offset={14}
                closeOnClick={false}
                onClose={() => setPopup(null)}
                className="qs-pi-popup"
                maxWidth="240px"
              >
                {popup.kind === "flight" && (() => {
                  const d = popup.d, p = d._pos || {};
                  const color = PHASE_COLOR[p.phase] || C.textDim;
                  const pct = Math.round(Math.max(0, Math.min(1, p.progress || 0)) * 100);
                  const depMs = Number.isFinite(d._depMs)
                    ? d._depMs : Date.parse(d.departure_time || "");
                  // ⚠ _arrMs FIRST, the filed time only as a fallback. The
                  // derived arrival is computed in buildFlightArc and lives on
                  // the arc, so re-parsing the record here would blank the ETA
                  // for every flight whose arrival this release estimates.
                  const arrMs = Number.isFinite(d._arrMs)
                    ? d._arrMs : Date.parse(d.arrival_time_est || "");

                  // ⚠ The lazily-fetched record only ever FILLS A GAP — it is
                  // consulted after the hash, never instead of it, so a
                  // backend-resolved owner is never replaced by a client fetch.
                  const cs = String(d.callsign || "").trim().toUpperCase();
                  const lazy = ownerCache[cs];
                  const owner = String(d.faa_owner || "").trim()
                    || (lazy?.status === "found" ? lazy.owner : "")
                    // ⚠ An on-demand result outranks nothing — it only fills a
                    // gap. It is last so a registry hit always wins.
                    || String(enrichCache[cs]?.data?.faa_owner || "").trim();
                  const make = d.faa_make || (lazy?.status === "found" ? lazy.make : "");
                  const model = d.faa_model || (lazy?.status === "found" ? lazy.model : "");
                  const makeModel = [make, model].filter(Boolean).join(" ");
                  const isOperator = d.operator_enriched === "1" && !lazy?.owner;

                  // ⚠⚠ Read from the payload `d`, never from a name in an outer
                  // scope — the popup IIFE declares its own bindings and an
                  // out-of-scope reference here is a runtime ReferenceError the
                  // build cannot see. (This file has produced that bug before.)
                  const enrich = enrichCache[cs];
                  const enriched = enrich?.status === "done" ? enrich.data : null;
                  const ownerResolved = owner || (enriched?.faa_owner || "");
                  const foreignLabel = String(ownerResolved).includes("registration \u2014");
                  const SOURCE_LABEL = {
                    faa_registry: "Via aircraft registry",
                    foreign_prefix: "Country derived from registration prefix",
                    flightaware_url: null,
                    none: null,
                  };
                  const enrichSource = enriched
                    ? SOURCE_LABEL[enriched.enrichment_source] || null
                    : null;
                  const faUrl = enriched?.enrichment_url || d.enrichment_url || null;

                  // ⚠ SCHEDULED is a real state, not an edge case: a filed plan
                  // whose departure has not arrived yet is on the map with an
                  // arc and no aircraft, and the badge has to say why.
                  const badge = p.notDeparted ? "SCHEDULED"
                    : p.landed ? "LANDED"
                    : (PHASE_LABEL[p.phase] || "—");
                  const badgeColor = p.notDeparted ? C.textDim
                    : p.landed ? PHASE_LANDING : color;

                  return (
                    <div style={{ fontFamily: font, color: C.text, fontSize: 11, lineHeight: 1.6 }}>
                      {/* HEADER — callsign, aircraft, and the status badge */}
                      <div style={{ display: "flex", alignItems: "baseline", gap: 6, flexWrap: "wrap" }}>
                        <span style={{ fontWeight: 700, fontSize: 13, color }}>
                          {d.callsign || "—"}
                        </span>
                        <span style={{
                          fontSize: 9, fontWeight: 700, letterSpacing: "0.08em",
                          color: badgeColor, border: `1px solid ${badgeColor}`,
                          borderRadius: 3, padding: "1px 4px", whiteSpace: "nowrap",
                        }}>
                          {badge}
                        </span>
                      </div>
                      {makeModel && (
                        <div style={{ color: C.textMid }}>{makeModel}</div>
                      )}
                      {/* ⚠ Falls back to the ICAO type only when the registry
                          gave nothing — "GLF5" is worse than "GULFSTREAM G550"
                          but far better than an empty line. */}
                      {!makeModel && d.aircraft_type && (
                        <div style={{ color: C.textMid }}>{d.aircraft_type}</div>
                      )}

                      {/* OWNER — the wording is a claim, and the three cases are
                          three DIFFERENT claims. "Operated by" is not ownership:
                          NetJets flies other people's aircraft. */}
                      <div style={{ marginTop: 4 }}>
                        {owner ? (
                          <span>
                            {isOperator ? "Operated by: " : "Owner: "}
                            {/* ⚠ A COUNTRY IS NOT AN OWNER. The foreign-prefix
                                label says which registry the tail belongs to,
                                not who flies it, so it is set apart in italic
                                dim text rather than sitting where a company
                                name would. */}
                            <span style={foreignLabel
                              ? { color: C.textDim, fontStyle: "italic" }
                              : { color: C.text }}>
                              {owner}
                            </span>
                          </span>
                        ) : lazy?.status === "loading" ? (
                          <span style={{ color: C.textDim }}>Looking up owner…</span>
                        ) : enrich?.status === "loading" ? (
                          <span style={{ color: C.textDim }}>Looking up…</span>
                        ) : d.registration_status === "unregistered" ? (
                          // ⚠ Says what we KNOW, not what we suspect. Telling a
                          // reserved mark from one merely absent from our
                          // snapshot needs the FAA reserved file, which the
                          // platform does not import.
                          <span style={{ color: C.orange }}>
                            No registry match — may be reserved or recently changed
                          </span>
                        ) : enrich?.status === "failed" ? (
                          <span style={{ color: C.textDim }}>Owner: Lookup failed</span>
                        ) : enrich?.status === "done" ? (
                          <span style={{ color: C.textDim }}>Owner: Could not identify</span>
                        ) : (
                          <span style={{ color: C.textDim }}>
                            Owner: Unknown{" "}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                lookUpOwner(d.callsign);
                              }}
                              style={{
                                background: "none", border: `1px solid ${C.border}`,
                                borderRadius: 2, color: C.textMid, cursor: "pointer",
                                fontFamily: "inherit", fontSize: 10, padding: "0 5px",
                                marginLeft: 4,
                              }}
                            >
                              Look up →
                            </button>
                          </span>
                        )}
                        {/* ⚠ How we know, when we know. "Via FAA registry" and a
                            country label are different claims and the reader
                            should be able to tell them apart. */}
                        {enrichSource && (
                          <div style={{ color: C.textDim, fontSize: 10 }}>
                            {enrichSource}
                          </div>
                        )}
                      </div>

                      {/* ROUTE */}
                      {/* ⚠⚠ THE DESTINATION IS THE SIGNAL. Which field a
                          corporate aircraft filed TO is the whole reason this
                          feed exists — a Gulfstream to Williston ND says
                          something a departure from Teterboro does not.
                          airportDescribe resolves 3,595 US fields to a city and
                          state; anything it cannot place shows the bare ICAO
                          rather than an invented name. */}
                      <div style={{ marginTop: 4 }}>
                        Departed: <span style={{ color: C.text }}>{airportDescribe(d.departure_icao)}</span>
                        {Number.isFinite(depMs) ? ` at ${etHhmm(depMs / 1000)} ET` : ""}
                        {/* ⚠ Says so when the time is inferred rather than
                            filed — see the departure_time proxy note. */}
                        {d._depEstimated && Number.isFinite(depMs) && (
                          <span style={{ color: C.textDim }}> (est.)</span>
                        )}
                      </div>
                      <div style={{ fontWeight: 700 }}>
                        Destination: <span style={{ color: C.text }}>{airportDescribe(d.arrival_icao)}</span>
                        {Number.isFinite(arrMs)
                          ? ` ETA ${d._arrEstimated ? "~" : ""}${etHhmm(arrMs / 1000)} ET`
                          : ""}
                        {/* ⚠ Says so when the ETA was derived from distance and a
                            tier cruise speed rather than filed by SWIM. */}
                        {d._arrEstimated && Number.isFinite(arrMs) && (
                          <span style={{ color: C.textDim }}> (est.)</span>
                        )}
                      </div>
                      {d._unusual && (
                        <div style={{ marginTop: 4 }}>
                          <span style={{
                            fontSize: 10, fontWeight: 700, color: UNUSUAL_ARC_COLOR,
                            border: `1px solid ${UNUSUAL_ARC_COLOR}`, borderRadius: 10,
                            padding: "1px 7px", letterSpacing: "0.08em",
                          }}>
                            ⚠ UNUSUAL DESTINATION
                          </span>
                          <div style={{ color: C.textDim, fontSize: 11, marginTop: 3 }}>
                            Not a monitored field and not in the airport index —
                            likely a private or non-commercial strip.
                          </div>
                        </div>
                      )}

                      {/* ⚠ Constructed by the backend from the callsign, never
                          fetched or validated. Shown whenever it exists, even
                          for a known owner — live tracking detail is useful
                          either way. */}
                      {faUrl && (
                        <div style={{ marginTop: 3 }}>
                          <a href={faUrl} target="_blank" rel="noopener noreferrer"
                             style={{ color: C.blue, fontSize: 10, textDecoration: "none" }}>
                            View live tracking →
                          </a>
                        </div>
                      )}

                      {/* PROGRESS — suppressed before departure, where a
                          percentage would be a number about nothing. */}
                      {!p.notDeparted && (
                        <div style={{ color: C.textDim, marginTop: 4 }}>{pct}% complete</div>
                      )}
                      {d.registration && (
                        <div style={{ color: C.textDim }}>{d.registration}</div>
                      )}
                      {/* ⚠ Says where this came from. The map carries ADS-B
                          contacts too, and a SWIM flight is a filed flight plan
                          being flown — not a live radar return. */}
                      <div style={{ color: C.textDim, marginTop: 4, fontSize: 10 }}>
                        Position interpolated
                      </div>
                    </div>
                  );
                })()}
                {popup.kind === "vessel" && (
                  <div style={{ fontFamily: font, color: C.text, fontSize: 11, lineHeight: 1.6 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: popup.d.aisDark ? C.red : VESSEL_BLUE }}>
                      {popup.d.name}
                    </div>
                    <div style={{ color: C.textDim }}>MMSI {popup.d.mmsi || "—"}</div>
                    {/* ⚠ "Type pending" is not "Vessel". A hull with no ShipStaticData
                        yet is one we have not been TOLD the type of — asserting
                        "Vessel" is how the old panel claimed a fleet of crude
                        carriers that were mostly tugs. Static lands for ~3.7% of
                        contacts per collection window and accumulates from there. */}
                    <div style={{ color: C.textDim }}>
                      {popup.d.has_static ? popup.d.type : "Type pending"} · FLAG {popup.d.flag}
                    </div>
                    {popup.d.region && <div style={{ color: C.textDim }}>{popup.d.region}</div>}
                    <div>Speed: <span style={{ color: C.text }}>{popup.d.speed}kn</span></div>
                    {popup.d.dest && popup.d.dest !== "UNKNOWN" && (
                      <div style={{ color: VESSEL_BLUE }}>→ {popup.d.dest}</div>
                    )}
                    {popup.d.imo && <div style={{ color: C.textDim }}>IMO {popup.d.imo}</div>}
                    {/* ⚠ Stated on every popup, both ways round — a viewer must never
                        have to infer which of the two they are looking at. */}
                    <div style={{ color: popup.d.is_cached ? C.orange : C.textDim, marginTop: 4 }}>
                      {popup.d.is_cached ? "Last known position" : "Live position"}
                      {popup.d.last_seen ? ` · ${etHhmm(Date.parse(popup.d.last_seen) / 1000)} ET` : ""}
                    </div>
                    {popup.d.signal && <div style={{ color: VESSEL_BLUE, marginTop: 4 }}>📡 {popup.d.signal}</div>}
                    {popup.d.aisDark && <div style={{ color: C.red, fontWeight: 700, marginTop: 4 }}>⚠ DARK VESSEL EVENT</div>}
                  </div>
                )}
                {popup.kind === "thermal" && (
                  <div style={{ fontFamily: font, color: C.text, fontSize: 11, lineHeight: 1.6 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: "#FF5252" }}>{popup.d.location_type || "THERMAL"}</div>
                    <div style={{ color: C.textDim }}>{popup.d.region || "—"}</div>
                    <div>FRP: <span style={{ color: C.text }}>{popup.d.frp} MW</span></div>
                    <div style={{ color: C.textDim }}>Confidence: {String(popup.d.confidence ?? "—")}</div>
                    <div style={{ color: C.textDim }}>Date: {popup.d.date || "—"}</div>
                    {popup.d.label && <div style={{ color: C.amber, marginTop: 4 }}>{popup.d.label}</div>}
                  </div>
                )}
                {popup.kind === "port" && (
                  <div style={{ fontFamily: font, color: C.text, fontSize: 11, lineHeight: 1.6 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: "#FFC107" }}>{popup.d.name}</div>
                    <div style={{ color: C.textDim }}>Major US import/export hub</div>
                  </div>
                )}
                {popup.kind === "aircraft" && (
                  <div style={{ fontFamily: font, color: C.text, fontSize: 11, lineHeight: 1.6 }}>
                    {popup.d.type === "military" && (
                      <div style={{ color: "#FF5252", fontWeight: 700, fontSize: 12, marginBottom: 3 }}>
                        ⚠ MILITARY
                        {popup.d.nationality && (
                          <>
                            <span style={{ color: C.textDim }}> · </span>
                            <span style={{ color: popup.d.nationality_color || "#FF5252" }}>{popup.d.nationality}</span>
                          </>
                        )}
                      </div>
                    )}
                    <div style={{ fontWeight: 700, fontSize: 13, color: aircraftStyle(popup.d.type).color }}>{popup.d.callsign}</div>
                    <div style={{ color: C.textDim }}>Origin: {popup.d.origin}</div>
                    <div style={{ color: C.textDim }}>Type: {String(popup.d.type || "—").toUpperCase()}</div>
                    <div>Altitude: <span style={{ color: C.text }}>{popup.d.alt}ft</span></div>
                    <div>Speed: <span style={{ color: C.text }}>{popup.d.speed}kts</span></div>
                    <div style={{ color: C.textDim }}>Heading: {popup.d.heading != null ? `${popup.d.heading}°` : "—"}</div>
                    <div style={{ color: C.textDim }}>ICAO24: {popup.d.icao24 || "—"}</div>
                    {/* FAA registry enrichment. Only N-numbered callsigns resolve
                        (the backend skips airline/military ones), so faa_owner is
                        the gate — absent means "no registry match", not an error. */}
                    {popup.d.faa_owner && (
                      <div style={{ marginTop: 6, paddingTop: 5, borderTop: "1px solid rgba(255,255,255,0.12)" }}>
                        <div style={{ fontSize: 9, color: C.textDim, letterSpacing: "0.12em", marginBottom: 2 }}>
                          REGISTERED OWNER
                        </div>
                        <div>Owner: <span style={{ color: C.text }}>{popup.d.faa_owner}</span></div>
                        {[popup.d.faa_city, popup.d.faa_state].filter(Boolean).length > 0 && (
                          <div style={{ color: C.textDim }}>
                            Location: {[popup.d.faa_city, popup.d.faa_state].filter(Boolean).join(", ")}
                          </div>
                        )}
                        {[popup.d.faa_make, popup.d.faa_model].filter(Boolean).length > 0 && (
                          <div style={{ color: C.textDim }}>
                            Aircraft: {[popup.d.faa_make, popup.d.faa_model].filter(Boolean).join(" ")}
                          </div>
                        )}
                        {(popup.d.faa_is_govt === true || popup.d.faa_is_llc === true) && (
                          <div style={{ display: "flex", gap: 5, marginTop: 4, flexWrap: "wrap" }}>
                            {popup.d.faa_is_govt === true && (
                              <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.08em",
                                             color: C.red, border: `1px solid ${C.red}`, padding: "1px 4px" }}>
                                GOVERNMENT
                              </span>
                            )}
                            {popup.d.faa_is_llc === true && (
                              <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.08em",
                                             color: C.amber, border: `1px solid ${C.amber}`, padding: "1px 4px" }}>
                                LLC
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                    {/* US-registered bizjet with no registry match — a live operator
                        flight, not noise. Shown where the owner block would be. */}
                    {!popup.d.faa_owner && popup.d.type === "bizjet" && _isUsHex(popup.d.icao24) && (
                      <div style={{ marginTop: 6, paddingTop: 5, borderTop: "1px solid rgba(255,255,255,0.12)", color: C.textDim }}>
                        Operator flight
                      </div>
                    )}
                  </div>
                )}
              </Popup>
            )}
          </Map>
        </div>
      )}

      {/* ── DESTINATION SIGNALS ────────────────────────────────────────────
          ⚠ A SIBLING of the map, not part of it. The map answers "what is
          flying"; this answers "where did something go that it does not usually
          go", and the second question survives the flight hash expiring. */}
      <DestinationSignals onFocus={focusDestination} selectedId={selectedDest} />

      {/* ── LOG MODE — persistent 24h flight log ──────────────────────────
          ⚠ A SEPARATE SIBLING BLOCK, not a branch inside the live panel. The live
          list renders from the same array the map markers use, so keeping the two
          apart means a regression here cannot reach the markers. LOG mode is
          additive.                                                              */}
      {/* ── VESSEL LIST ─────────────────────────────────────────────────────
          ⚠ A SIBLING of the aircraft panels, gated on its own state. Neither pill
          can close or re-mode the other. */}
      {showVessels && (
        <div style={{ marginTop: 8, border: `1px solid ${C.border}`, borderRadius: 3, overflow: "hidden" }}>
          <div style={{ padding: "6px 10px", fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", color: C.textDim, borderBottom: `1px solid ${C.border}` }}>
            {vessels.length} VESSEL{vessels.length === 1 ? "" : "S"}
            {` · ${vessels.length - cachedCount} LIVE · ${cachedCount} LAST KNOWN`}
          </div>
          <div style={{ maxHeight: 320, overflowY: "auto" }}>
            {regions.map((cp) => {
              const list = vesselsByRegion.get(cp.name) || [];
              const dead = cp.has_coverage === false;
              // ⚠ An empty region is HIDDEN unless it is a known coverage gap, which
              // is shown with its reason. Hiding those too would silently shrink the
              // monitored set to whatever happened to report.
              if (!list.length && !dead) return null;
              const closed = closedRegions.has(cp.name);
              return (
                <div key={`vg-${cp.name}`}>
                  <div
                    onClick={() => setClosedRegions((prev) => {
                      const next = new Set(prev);
                      if (next.has(cp.name)) next.delete(cp.name); else next.add(cp.name);
                      return next;
                    })}
                    style={{
                      padding: "5px 10px", fontSize: 9, fontWeight: 700, letterSpacing: "0.08em",
                      color: dead ? C.red : VESSEL_BLUE, background: C.panelAlt,
                      borderBottom: `1px solid ${C.border}`,
                      cursor: dead ? "default" : "pointer",
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                    }}
                  >
                    <span>{cp.name.toUpperCase()}</span>
                    <span style={{ color: dead ? C.red : C.textDim, fontWeight: 400 }}>
                      {dead ? (cp.coverage_note || "no receiver coverage")
                           : `${list.length}${closed ? " ▸" : " ▾"}`}
                    </span>
                  </div>
                  {!closed && list.map((v, i) => (
                    <div
                      key={`vr-${v.mmsi || i}`}
                      style={{
                        padding: "6px 10px",
                        borderLeft: `3px solid ${v.aisDark ? C.red : v.is_cached ? C.border : VESSEL_BLUE}`,
                        borderBottom: `1px solid ${C.border}`,
                        opacity: v.is_cached ? 0.7 : 1,
                      }}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>
                          {v.name || `MMSI ${v.mmsi}`}
                        </span>
                        <span style={{ fontSize: 11, color: C.textDim, whiteSpace: "nowrap" }}>
                          {Number(v.speed) || 0}kn
                        </span>
                      </div>
                      <div style={{ fontSize: 10, color: C.textDim, marginTop: 2 }}>
                        {v.has_static ? v.type : "Type pending"}
                        {v.dest && v.dest !== "UNKNOWN" ? ` · → ${v.dest}` : ""}
                        {v.is_cached ? " · Last known" : ""}
                        {v.aisDark ? " · ⚠ DARK VESSEL" : ""}
                      </div>
                    </div>
                  ))}
                </div>
              );
            })}
            {vessels.length === 0 && (
              <div style={{ padding: 16, fontSize: 11, color: C.textDim, textAlign: "center", lineHeight: 1.6 }}>
                No vessels in the monitored regions.
                <div style={{ fontSize: 10, marginTop: 4 }}>
                  Collection runs every 10 minutes.
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── UNIFIED FLIGHT LIST ──────────────────────────────────────────
          ⚠⚠ ONE LIST, NO MODE. The LIVE/LOG toggle asked the reader to know
          which of two feeds a flight would be in before they could go looking
          for it, and to guess which of the two was worth opening. SWIM is the
          primary surface and sits first; ADS-B contacts are context and sit
          under it.
          ⚠ The ADS-B section simply does not render when that feed is empty,
          which is most of the time — the geo-fence only scans weekdays
          07:00-10:00 ET. An empty-state panel for it would report a scheduled
          silence as a fault every evening and all weekend. */}
      {showList && (
        <div ref={listRef} style={{ marginTop: 8, border: `1px solid ${C.border}`, borderRadius: 3, overflow: "hidden" }}>
          <div style={{ padding: "6px 10px", fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", color: C.textDim, borderBottom: `1px solid ${C.border}` }}>
            {`FLIGHTS · ${swimRows.length}`}
            {adsbRows.length > 0 ? ` · ${adsbRows.length} CONTACTS` : ""}
          </div>
          <div style={{ maxHeight: 300, overflowY: "auto" }}>
            {swimRows.length === 0 ? (
              <div style={{ padding: 16, fontSize: 11, color: C.textDim, textAlign: "center" }}>
                No active flights.
              </div>
            ) : swimRows.map((f) => {
              const cs = String(f?.callsign || "");
              const tier = Number(f?.aircraft_tier);
              const tierCol = TIER_BADGE_COLOR[tier];
              const unusual = looksUnusual(f);
              const who = String(f?.faa_owner || "").trim() || String(f?.operator || "").trim();
              const depMs = Date.parse(f?.departure_time || "");
              const updMs = Date.parse(f?.last_updated || "");
              // ⚠ "Dep" ONLY when SWIM actually filed one. last_updated is message
              // receipt time, so labelling it "Dep" would state a departure we do
              // not have — the same distinction the arc's depEstimated flag draws.
              const when = Number.isFinite(depMs)
                ? `Dep ${etHhmm(depMs / 1000)} ET`
                : (Number.isFinite(updMs) ? `Updated ${etHhmm(updMs / 1000)} ET` : "");
              const sel = selectedSwim && selectedSwim === cs;
              return (
                <div
                  key={`swim-${cs}`}
                  onClick={() => focusSwimFlight(f)}
                  title="Show this flight on the map"
                  style={{
                    cursor: "pointer", padding: "6px 10px",
                    borderLeft: `3px solid ${unusual ? UNUSUAL_ARC_COLOR : (tierCol || C.border)}`,
                    borderBottom: `1px solid ${C.border}`,
                    background: sel ? "rgba(255,255,255,0.08)" : "transparent",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    {tierCol && (
                      <span style={{
                        fontSize: 8, fontWeight: 700, letterSpacing: "0.06em",
                        color: tierCol, border: `1px solid ${tierCol}`,
                        borderRadius: 2, padding: "0 4px",
                      }}>
                        {`T${tier}`}
                      </span>
                    )}
                    <span style={{ fontWeight: 700, fontSize: 12, color: C.text }}>{cs || "—"}</span>
                  </div>
                  <div style={{ fontSize: 10, color: who ? C.textMid : C.textDim, marginTop: 1 }}>
                    {who || "Unknown operator"}
                  </div>
                  {/* ⚠⚠ EVERY GLYPH SITS IN A STRING LITERAL, NEVER IN BARE JSX
                      TEXT. JSX does not process backslash escapes and the editor
                      writes a pasted character back out as one — that is exactly
                      how the destination route arrow shipped as six literal
                      characters. Inside a string the escape is processed either
                      way, so this form is correct however the character lands. */}
                  <div style={{ fontSize: 10, color: C.textDim, marginTop: 1 }}>
                    {unusual && (
                      <span style={{ color: UNUSUAL_ARC_COLOR }}>{"⚠"}{" "}</span>
                    )}
                    {String(f?.departure_icao || "—")}{" "}{"→"}{" "}{String(f?.arrival_icao || "—")}
                  </div>
                  {when && (
                    <div style={{ fontSize: 9, color: C.textDim, marginTop: 1 }}>{when}</div>
                  )}
                </div>
              );
            })}

            {adsbRows.length > 0 && (
              <div style={{
                padding: "6px 10px", fontSize: 9, fontWeight: 700, letterSpacing: "0.1em",
                color: C.textDim, background: C.panelAlt,
                borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`,
              }}>
                {`AIRCRAFT CONTACTS · ${adsbRows.length}`}
              </div>
            )}
            {adsbRows.map((a, i) => {
              const key = a.icao24 || a.callsign || `ac-${i}`;
              const col = AC_LIST_COLOR[a.type] || C.textDim;
              const sel = selectedAc && (a.icao24 === selectedAc || a.callsign === selectedAc);
              const subtitle = a.faa_owner
                ? a.faa_owner
                : (a.type === "bizjet" && _isUsHex(a.icao24) ? "Operator flight"
                  : (a.type === "military" ? (a.nationality || "Military") : (a.origin || "")));
              const alt = Number.isFinite(Number(a.alt)) ? `${Number(a.alt).toLocaleString()} ft` : null;
              const spd = Number.isFinite(Number(a.speed)) ? `${Number(a.speed)} kts` : null;
              const line3 = [alt, spd].filter(Boolean).join(" · ");
              return (
                <div
                  key={key}
                  onClick={() => focusAircraft(a)}
                  style={{
                    cursor: "pointer", padding: "6px 10px",
                    borderLeft: `3px solid ${col}`,
                    borderBottom: `1px solid ${C.border}`,
                    background: sel ? "rgba(255,255,255,0.08)" : "transparent",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ fontWeight: 700, fontSize: 12, color: C.text }}>{a.callsign || "—"}</span>
                    <span style={{ fontSize: 8, fontWeight: 700, letterSpacing: "0.06em", color: col, border: `1px solid ${col}`, borderRadius: 2, padding: "0 4px" }}>
                      {AC_LIST_BADGE[a.type] || String(a.type || "").toUpperCase()}
                    </span>
                  </div>
                  {subtitle && (
                    <div style={{ fontSize: 10, color: C.textDim, marginTop: 1 }}>{subtitle}</div>
                  )}
                  {line3 && (
                    <div style={{ fontSize: 9, color: C.textDim, marginTop: 1 }}>{line3}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
